import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiBookOpen, FiChevronDown, FiChevronRight, FiCpu, FiDownload, FiFile, FiFolder, FiLogIn, FiMoon, FiPause, FiPlay, FiPlus, FiRadio, FiRefreshCw, FiSave, FiSend, FiSquare, FiSun, FiTrash2 } from 'react-icons/fi';
import { listDeployments } from './signalDataFeeds';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import StrategyTesterChart, { EquityMiniChart } from './StrategyTesterChart';
import {
  createIndicator,
  createRuleCondition,
  DAY_FILTER_OPTIONS,
  DEFAULT_INDICATORS,
  DEFAULT_RULES,
  DEFAULT_SETUP,
  ensureSymbol,
  explainSignalBar,
  findConditionSignalIndices,
  formatCurrency,
  formatPrice,
  getOperandDisplayLabel,
  getOperandOptions,
  inferConditionsFromSelection,
  INDICATOR_LIBRARY,
  metricTone,
  normalizeRules,
  RULE_OPERATOR_OPTIONS,
  SESSION_OPTIONS,
  STRATEGY_TIMEZONE_OPTIONS,
  simulateBacktest,
  summarizeBacktest,
  SYMBOL_MAP,
  TIMEFRAME_OPTIONS,
  CONDITION_TIMEFRAME_OPTIONS,
} from './strategyTesterEngine';
import InfoTip from './InfoTip';
import { compileVixScript } from './vixScriptEngine';
import {
  cancelServerBackfillJob,
  cancelServerBacktestJob,
  createSavedTesterStrategy,
  deleteSavedTesterStrategy,
  describeMarketDataSource,
  exportStrategyEa,
  fetchServerCandles,
  fetchSavedTesterStrategy,
  fetchServerBackfillJob,
  fetchServerBacktestJob,
  fetchServerSymbolCatalog,
  fetchTesterUserProfile,
  formatMarketTimestamp,
  getBrokerProfileLabel,
  getDefaultBrokerProfileId,
  listSavedTesterStrategies,
  runServerBacktest,
  startServerBackfillJob,
  startServerBacktestJob,
  startOptimization,
  fetchOptimization,
  cancelOptimization,
  startWalkForward,
  fetchWalkForward,
  cancelWalkForward,
  sampleSymbolSpreads,
  subscribeDerivTicks,
  updateSavedTesterStrategy,
} from './strategyTesterDataFeeds';
import { requestVixAiCodeDraft, runVixAiWebSearch, sendVixAiMessage, sendVixAiVerify } from './strategyArchitectDataFeeds';
import { DerivAffiliateBanner } from './DerivAffiliate';
import './StrategyTester.css';

const metricCards = [
  { key: 'netProfit', label: 'Net Profit', type: 'currency' },
  { key: 'winRate', label: 'Win Rate', type: 'percent' },
  { key: 'maxDrawdown', label: 'Max Drawdown', type: 'percent' },
  { key: 'profitFactor', label: 'Profit Factor', type: 'decimal' },
  { key: 'totalTrades', label: 'Total Trades', type: 'number' },
  { key: 'sharpe', label: 'Sharpe Ratio', type: 'decimal' },
  { key: 'averageWin', label: 'Avg Win', type: 'currency' },
  { key: 'averageLoss', label: 'Avg Loss', type: 'currency' },
];

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_SESSION_IDS = SESSION_OPTIONS.map((session) => session.id);
const COLLAPSED_SIZE = 24;
const MIN_LEFT = 280;
const MAX_LEFT = 460;
const MIN_RIGHT = 300;
const MAX_RIGHT = 500;
const PLAYBACK_SPEED_OPTIONS = [
  { id: '0.05x', label: '0.05x', intervalMs: 1500, step: 1 },
  { id: '0.1x', label: '0.1x', intervalMs: 750, step: 1 },
  { id: '0.25x', label: '0.25x', intervalMs: 300, step: 1 },
  { id: '0.5x', label: '0.5x', intervalMs: 140, step: 1 },
  { id: '1x', label: '1x', intervalMs: 78, step: 1 },
  { id: '2x', label: '2x', intervalMs: 60, step: 2 },
  { id: '4x', label: '4x', intervalMs: 44, step: 4 },
  { id: '8x', label: '8x', intervalMs: 34, step: 8 },
];
const RULE_TARGET_OPTIONS = [
  { id: 'operand', label: 'Market / Indicator' },
  { id: 'value', label: 'Fixed Value' },
];
const TIMEFRAME_SECONDS = { M1: 60, M5: 300, M15: 900, M30: 1800, H1: 3600, H4: 14400, D1: 86400, W1: 604800, MN1: 2592000 };
const INITIAL_ENGINE = simulateBacktest(DEFAULT_SETUP, DEFAULT_INDICATORS, DEFAULT_RULES, DEFAULT_SESSION_IDS);
const DEFAULT_STRATEGY_NAME = 'VixAlgo Strategy';
const WALKTHROUGH_STORAGE_KEY = 'vixAlgoBuilderWalkthroughDismissed';
const MODEL_LABELS = {
  open_prices: 'Open Prices Only',
  minute_ohlc: '1-Minute OHLC',
  every_tick: 'Every Tick',
};
const CERTIFIED_EXPORT_DATA_SOURCES = new Set(['deriv']);
const CERTIFIED_EXPORT_BROKER_PROFILES = new Set(['deriv-svg-server-03']);
const CERTIFIED_EXPORT_MODELING_MODES = new Set(['open_prices', 'minute_ohlc', 'every_tick']);
const CERTIFIED_EXPORT_OPERATORS = new Set(['crossesAbove', 'crossesBelow', 'greaterThan', 'greaterOrEqual', 'lessThan', 'lessOrEqual']);
const BUILDER_AI_INITIAL_MESSAGE = 'Describe the EA you want.';
const WALKTHROUGH_STEPS = [
  {
    title: 'Choose a market',
    body: 'Pick the symbol, timeframe, modeling mode, and date range you want to test.',
    panel: 'setup',
    tab: 'market',
  },
  {
    title: 'Build only certified rules',
    body: 'Add indicators or Range Breakout, then connect them in Rules.',
    panel: 'setup',
    tab: 'rules',
  },
  {
    title: 'Run the server backtest',
    body: 'Press Run and let the server finish the test before reviewing the summary, report, and replay.',
    panel: 'chart',
  },
  {
    title: 'Read the history badge',
    body: 'The history badge tells you whether the selected range loaded cleanly from the source provider.',
    panel: 'results',
    tab: 'summary',
  },
  {
    title: 'Export only when ready',
    body: 'Open Strategies and export MQ5 after the Builder confirms the selected rules can be generated safely.',
    panel: 'setup',
    tab: 'strategies',
  },
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}


function getTimeframeMinutesValue(timeframe) {
  const normalized = String(timeframe || 'H1').toUpperCase();
  return Math.max(1, Math.round((TIMEFRAME_SECONDS[normalized] || TIMEFRAME_SECONDS.H1) / 60));
}

function getExecutionTimeframeForSetup(setup) {
  const signalTimeframe = String(setup?.timeframe || 'H1').toUpperCase();
  const modelingMode = String(setup?.modelingMode || 'open_prices');
  if (modelingMode === 'open_prices') {
    return signalTimeframe;
  }
  return getTimeframeMinutesValue(signalTimeframe) <= 1 ? signalTimeframe : 'M1';
}

function getExecutionHistoryRequirement(setup) {
  const signalTimeframe = String(setup?.timeframe || 'H1').toUpperCase();
  const executionTimeframe = getExecutionTimeframeForSetup(setup);
  return {
    executionTimeframe,
    signalTimeframe,
    requiresSeparateExecutionHistory: executionTimeframe !== signalTimeframe,
  };
}

function getBackfillJobSignature(setup) {
  return [
    'deriv',
    String(setup?.symbol || '').toUpperCase(),
    getExecutionTimeframeForSetup(setup),
    setup?.from || '',
    setup?.to || '',
  ].join('|');
}

function backfillMatchesSetup(job, setup) {
  if (!job) {
    return false;
  }
  const jobSignature = [
    String(job.provider || 'deriv').toLowerCase(),
    String(job.symbol || '').toUpperCase(),
    String(job.timeframe || '').toUpperCase(),
    job.from || '',
    job.to || '',
  ].join('|');
  return jobSignature === getBackfillJobSignature(setup);
}

function backfillReadyForSetup(job, setup) {
  return job?.status === 'completed' && backfillMatchesSetup(job, setup);
}

function isDerivRetentionError(message) {
  return /does not currently expose enough|retention|too long|range is too long|not enough .*history|0 candles/i.test(String(message || ''));
}

function getDerivRecentRange(toDate, timeframe) {
  const today = new Date();
  const requestedEnd = new Date(`${toDate || formatDateForInput(Date.now())}T00:00:00Z`);
  const staleThreshold = today.getTime() - 7 * 86400000;
  const end = !Number.isNaN(requestedEnd.getTime()) && requestedEnd.getTime() >= staleThreshold
    ? requestedEnd
    : new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  if (Number.isNaN(end.getTime())) {
    return null;
  }
  const days = String(timeframe || '').toUpperCase() === 'M1' ? 30 : 90;
  const start = new Date(end.getTime() - days * 86400000);
  return {
    from: formatDateForInput(start.getTime()),
    to: formatDateForInput(end.getTime()),
  };
}

function getModelLabel(mode) {
  return MODEL_LABELS[mode] || mode || MODEL_LABELS.open_prices;
}

function getHistoryQualityBadge(historyQuality) {
  const grade = String(historyQuality?.grade || '').toLowerCase();
  const qaReferenceOnly = Boolean(historyQuality?.qaReferenceOnly);
  if (grade === 'certified') {
    return {
      label: qaReferenceOnly ? 'MT5 QA reference' : 'Certified MT5-grade',
      className: qaReferenceOnly ? 'qa' : 'certified',
      title: historyQuality?.label || '',
    };
  }
  if (grade === 'reference_mismatch') {
    return {
      label: 'Reference mismatch',
      className: 'mismatch',
      title: historyQuality?.label || '',
    };
  }
  if (grade === 'external_unverified') {
    return {
      label: 'Source-only history',
      className: 'external',
      title: historyQuality?.label || '',
    };
  }
  if (grade === 'failed') {
    return {
      label: 'History failed',
      className: 'failed',
      title: historyQuality?.label || '',
    };
  }
  return null;
}

// Data-confidence badge: the plain-English trustworthiness grade for the loaded history window
// (completeness, sample size, spec confidence). Distinct from the MT5 "certification" grade above.
function getDataConfidenceBadge(historyQuality) {
  const confidence = historyQuality?.dataConfidence;
  const grade = String(confidence?.grade || '').toLowerCase();
  if (!grade) return null;
  const byGrade = {
    high: { label: 'Data: High', className: 'confidence-high' },
    medium: { label: 'Data: Medium', className: 'confidence-medium' },
    low: { label: 'Data: Low', className: 'confidence-low' },
    poor: { label: 'Data: Poor', className: 'confidence-poor' },
    unusable: { label: 'Data: Unusable', className: 'confidence-unusable' },
  };
  const base = byGrade[grade];
  if (!base) return null;
  const score = Number(confidence?.score);
  const reasonText = Array.isArray(confidence?.reasons) && confidence.reasons.length
    ? confidence.reasons.join(' ')
    : (confidence?.label || '');
  return {
    label: Number.isFinite(score) ? `${base.label} (${score})` : base.label,
    className: base.className,
    title: [confidence?.label, reasonText].filter(Boolean).join(' — '),
  };
}

function getCertifiedExportSupport(setup, indicators, rules) {
  const reasons = [];
  const dataSource = String(setup?.dataSource || '').toLowerCase();
  const brokerProfileId = normalizeBrokerProfileIdForSource(setup?.dataSource, setup?.brokerProfileId);
  const modelingMode = String(setup?.modelingMode || 'open_prices');
  const conditions = [
    ...(Array.isArray(rules?.longConditions) ? rules.longConditions : []),
    ...(Array.isArray(rules?.shortConditions) ? rules.shortConditions : []),
  ];

  if (!CERTIFIED_EXPORT_DATA_SOURCES.has(dataSource)) {
    reasons.push('Use the configured source-provider history for Builder export.');
  }
  if (!CERTIFIED_EXPORT_BROKER_PROFILES.has(brokerProfileId)) {
    reasons.push('Use the configured source-provider broker assumptions for Builder export.');
  }
  if (!CERTIFIED_EXPORT_MODELING_MODES.has(modelingMode)) {
    reasons.push('The selected modeling mode is not certified for export.');
  }

  const indicatorIds = new Set((Array.isArray(indicators) ? indicators : []).map((indicator) => indicator.id));
  (Array.isArray(indicators) ? indicators : []).forEach((indicator) => {
    if (!INDICATOR_LIBRARY[indicator.type]) {
      reasons.push(`${indicator.type || 'Unknown indicator'} is not implemented in the builder indicator library.`);
    }
  });
  conditions.forEach((condition) => {
    if (!CERTIFIED_EXPORT_OPERATORS.has(condition.operator)) {
      reasons.push(`${condition.operator || 'Unknown operator'} is not certified for MQ5 export.`);
    }
    [condition.left, condition.rightMode === 'value' ? null : condition.rightOperand]
      .filter((operand) => operand && !['open', 'high', 'low', 'close'].includes(operand))
      .forEach((operand) => {
        const indicatorId = String(operand).split(':')[0];
        if (!indicatorIds.has(indicatorId)) {
          reasons.push(`Condition operand ${operand} references a missing indicator.`);
        }
      });
  });
  if (rules?.indicatorEntriesEnabled === false && !rules?.rangeBreakoutEnabled && !rules?.gridEnabled && !rules?.structuralBreakoutEnabled && !rules?.candlePatternEntryEnabled && !rules?.patternEntryEnabled) {
    reasons.push('Enable Entry Conditions, Range Breakout, Structural Breakout, Candle Patterns, Pattern by Example, or Grid before exporting.');
  }

  return {
    supported: reasons.length === 0,
    reasons,
    label: reasons.length === 0 ? 'MQ5 export-ready' : 'MQ5 export blocked',
    message: reasons[0] || 'This strategy can be generated by the current MQ5 exporter.',
  };
}

function normalizeBrokerProfileIdForSource() {
  return getDefaultBrokerProfileId();
}

function getEffectiveReportSetup(setup, result) {
  const effectiveSetup = result?.engine?.setup || null;
  const requestedMode = result?.meta?.requestedModelingMode || setup?.modelingMode;
  const effectiveMode = result?.meta?.effectiveModelingMode || effectiveSetup?.modelingMode || setup?.modelingMode;
  const brokerSpec = result?.meta?.brokerSpec || result?.engine?.profile?.instrumentSpec || null;
  const historyQuality = result?.meta?.historyQuality || null;
  const brokerProfileId = brokerSpec?.brokerProfileId
    || normalizeBrokerProfileIdForSource(setup?.dataSource, effectiveSetup?.brokerProfileId || setup?.brokerProfileId);
  return {
    ...setup,
    ...(effectiveSetup || {}),
    modelingMode: effectiveMode,
    requestedModelingMode: requestedMode,
    brokerProfileId,
    brokerProfileLabel: brokerSpec?.brokerName || getBrokerProfileLabel(brokerProfileId),
    brokerSpecConfidence: brokerSpec?.specConfidence || '',
    brokerSpecSource: brokerSpec?.source || '',
    brokerSpecMissingFields: Array.isArray(brokerSpec?.missingFields) ? brokerSpec.missingFields : [],
    historyQualityGrade: historyQuality?.grade || '',
    historyQualityLabel: historyQuality?.label || '',
    dataConfidenceGrade: historyQuality?.dataConfidence?.grade || '',
    dataConfidenceScore: Number.isFinite(Number(historyQuality?.dataConfidence?.score))
      ? Number(historyQuality.dataConfidence.score)
      : null,
    dataConfidenceReasons: Array.isArray(historyQuality?.dataConfidence?.reasons)
      ? historyQuality.dataConfidence.reasons
      : [],
  };
}

function getReportTimezoneOffsetHours(rules) {
    if (rules?.timezoneMode === 'custom') {
      return Number(rules.customUtcOffsetHours) || 0;
    }
    return 0;
  }

function formatReportTimestamp(timestamp, rules) {
  if (!timestamp) {
    return '—';
  }
  const shiftedDate = new Date(timestamp + (getReportTimezoneOffsetHours(rules) * 60 * 60 * 1000));
  const year = shiftedDate.getUTCFullYear();
  const month = String(shiftedDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(shiftedDate.getUTCDate()).padStart(2, '0');
  const hours = String(shiftedDate.getUTCHours()).padStart(2, '0');
  const minutes = String(shiftedDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(shiftedDate.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function describeReportTimezone(rules) {
    if (rules?.timezoneMode === 'broker') {
      const offset = Number(rules?.brokerUtcOffsetHours) || 0;
      return `Broker Server (UTC${offset >= 0 ? '+' : ''}${offset})`;
    }
    if (rules?.timezoneMode === 'custom') {
      const offset = getReportTimezoneOffsetHours(rules);
      return `UTC Offset (UTC${offset >= 0 ? '+' : ''}${offset})`;
    }
    return 'UTC';
  }

function buildBacktestSignature(setup, indicators, rules, sessions) {
  return JSON.stringify({
    setup: {
      dataSource: setup.dataSource,
      brokerProfileId: normalizeBrokerProfileIdForSource(setup.dataSource, setup.brokerProfileId),
      assetClass: setup.assetClass,
      symbol: setup.symbol,
      timeframe: setup.timeframe,
      from: setup.from,
      to: setup.to,
      initialBalance: Number(setup.initialBalance),
      lotSize: Number(setup.lotSize),
      commission: Number(setup.commission),
      slippage: Number(setup.slippage),
      modelingMode: setup.modelingMode,
      stopLevelPoints: Number(setup.stopLevelPoints || 0),
      freezeLevelPoints: Number(setup.freezeLevelPoints || 0),
      swapLongPerLot: Number(setup.swapLongPerLot || 0),
      swapShortPerLot: Number(setup.swapShortPerLot || 0),
    },
    indicators: indicators.map((indicator) => ({
      type: indicator.type,
      placement: indicator.placement,
      params: indicator.params,
    })),
    rules,
    sessions: [...sessions].sort(),
  });
}

function normalizeSummaryShape(rawSummary) {
  const metrics = rawSummary?.metrics || {};
  const analysis = rawSummary?.analysis || {};
  return {
    metrics: {
      netProfit: Number(metrics.netProfit || 0),
      winRate: Number(metrics.winRate || 0),
      maxDrawdown: Number(metrics.maxDrawdown || 0),
      profitFactor: Number(metrics.profitFactor || 0),
      totalTrades: Number(metrics.totalTrades || 0),
      sharpe: Number(metrics.sharpe || 0),
      averageWin: Number(metrics.averageWin || 0),
      averageLoss: Number(metrics.averageLoss || 0),
    },
    analysis: {
      grossProfit: Number(analysis.grossProfit || 0),
      grossLoss: Number(analysis.grossLoss || 0),
      expectancy: Number(analysis.expectancy || 0),
      largestWin: Number(analysis.largestWin || 0),
      largestLoss: Number(analysis.largestLoss || 0),
      maxConsecutiveWins: Number(analysis.maxConsecutiveWins || 0),
      maxConsecutiveLosses: Number(analysis.maxConsecutiveLosses || 0),
      buyTrades: Number(analysis.buyTrades || 0),
      sellTrades: Number(analysis.sellTrades || 0),
      buyWinRate: Number(analysis.buyWinRate || 0),
      sellWinRate: Number(analysis.sellWinRate || 0),
      buyNetProfit: Number(analysis.buyNetProfit || 0),
      sellNetProfit: Number(analysis.sellNetProfit || 0),
      averageTrade: Number(analysis.averageTrade || 0),
      averageBarsHeld: Number(analysis.averageBarsHeld || 0),
      averageDurationMinutes: Number(analysis.averageDurationMinutes || 0),
      maxDrawdownValue: Number(analysis.maxDrawdownValue || 0),
      recoveryFactor: Number(analysis.recoveryFactor || 0),
      bestDay: analysis.bestDay || { label: '-', pnl: 0, trades: 0, wins: 0, losses: 0 },
      worstDay: analysis.worstDay || { label: '-', pnl: 0, trades: 0, wins: 0, losses: 0 },
      profitByDay: Array.isArray(analysis.profitByDay) ? analysis.profitByDay : [],
      entriesByHour: Array.isArray(analysis.entriesByHour) ? analysis.entriesByHour : [],
    },
    trades: Array.isArray(rawSummary?.trades) ? rawSummary.trades : [],
    monthly: Array.isArray(rawSummary?.monthly) ? rawSummary.monthly : [...months].map(() => 0),
    equity: Array.isArray(rawSummary?.equity) ? rawSummary.equity : [],
  };
}

function normalizeLoadedStrategy(strategy) {
  const normalizedSetup = { ...DEFAULT_SETUP, ...(strategy?.setup || {}), ...getRollingOneYearDateRange() };
  normalizedSetup.dataSource = 'deriv';
  normalizedSetup.brokerProfileId = normalizeBrokerProfileIdForSource();
  return {
    id: strategy?.id ? String(strategy.id) : '',
    name: strategy?.name || DEFAULT_STRATEGY_NAME,
    description: strategy?.description || '',
    setup: normalizedSetup,
    indicators: Array.isArray(strategy?.indicators) ? strategy.indicators : [],
    rules: normalizeRules(strategy?.rules || DEFAULT_RULES),
    sessions: Array.isArray(strategy?.sessions) && strategy.sessions.length ? strategy.sessions : [...DEFAULT_SESSION_IDS],
  };
}

function buildSavedPayloadKey(normalized) {
  return JSON.stringify({
    name: (normalized.name || DEFAULT_STRATEGY_NAME).trim(),
    description: (normalized.description || '').trim(),
    setup: { ...normalized.setup },
    indicators: (normalized.indicators || []).map((ind) => ({ ...ind, params: { ...ind.params } })),
    rules: JSON.parse(JSON.stringify(normalized.rules || {})),
    sessions: [...(normalized.sessions || [])],
  });
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function buildTesterReportHtml({ strategyName, strategyDescription, setup, rules, sessions, summary, engine }) {
  const generatedAt = new Date().toLocaleString();
  const modelLabel = setup.requestedModelingMode && setup.requestedModelingMode !== setup.modelingMode
    ? `${getModelLabel(setup.modelingMode)} (requested ${getModelLabel(setup.requestedModelingMode)})`
    : getModelLabel(setup.modelingMode);
  const brokerMissingFields = Array.isArray(setup.brokerSpecMissingFields) ? setup.brokerSpecMissingFields : [];
  const brokerQuality = setup.brokerSpecConfidence
    ? `${String(setup.brokerSpecConfidence).replace(/_/g, ' ')}${brokerMissingFields.length ? ` (${brokerMissingFields.length} missing)` : ''}`
    : 'unknown';
  const runtimeRows = [
    ['Model', modelLabel],
    ['Broker', setup.brokerProfileLabel || getBrokerProfileLabel(setup.brokerProfileId)],
    ['Broker Spec Quality', brokerQuality],
    ['Broker Spec Source', setup.brokerSpecSource || 'unknown'],
    ...(brokerMissingFields.length ? [['Missing Broker Fields', brokerMissingFields.join(', ')]] : []),
    ...(setup.historyQualityGrade ? [['History Grade', String(setup.historyQualityGrade).replace(/_/g, ' ')]] : []),
    ...(setup.historyQualityLabel ? [['History Quality', setup.historyQualityLabel]] : []),
    ...(setup.dataConfidenceGrade
      ? [['Data Confidence', `${String(setup.dataConfidenceGrade).replace(/_/g, ' ')}${setup.dataConfidenceScore != null ? ` (${setup.dataConfidenceScore}/100)` : ''}`]]
      : []),
    ...(Array.isArray(setup.dataConfidenceReasons) && setup.dataConfidenceReasons.length
      ? [['Data Notes', setup.dataConfidenceReasons.join(' ')]]
      : []),
    ['Lot Size', String(setup.lotSize ?? '0.1')],
    ['Initial Balance', formatCurrency(setup.initialBalance)],
    ['Commission / Lot', formatCurrency(setup.commission)],
    ['Slippage (points)', String(setup.slippage ?? 0)],
    ['Stop Loss (points)', String(rules?.stopLoss ?? 0)],
    ['Take Profit (points)', String(rules?.takeProfit ?? 0)],
    ['Trailing Stop (points)', String(rules?.trailingStop ?? 0)],
    ['Break Even Trigger (points)', String(rules?.breakEvenTrigger ?? 0)],
    ['Break Even Offset (points)', String(rules?.breakEvenOffset ?? 0)],
    ['Close On Opposite', rules?.closeOnOpposite ? 'Enabled' : 'Disabled'],
    ['Max Open Trades', String(rules?.maxOpenTrades ?? 1)],
    ['Martingale', rules?.martingaleEnabled ? `Enabled (${rules.martingaleFactor || 1}x, max ${rules.martingaleMaxSteps || 0})` : 'Disabled'],
    ['Range Breakout', rules?.rangeBreakoutEnabled ? `Enabled (${rules.rangeStartHour}:${String(rules.rangeStartMinute || 0).padStart(2, '0')} to ${rules.rangeEndHour}:${String(rules.rangeEndMinute || 0).padStart(2, '0')})` : 'Disabled'],
    ['Time Exit', rules?.timeExitEnabled ? `Enabled (${rules.timeExitHour}:${String(rules.timeExitMinute || 0).padStart(2, '0')})` : 'Disabled'],
  ];
  const metricsRows = [
    ['Net Profit', formatCurrency(summary.metrics.netProfit)],
    ['Win Rate', `${summary.metrics.winRate.toFixed(1)}%`],
    ['Max Drawdown', `${summary.metrics.maxDrawdown.toFixed(2)}%`],
    ['Profit Factor', Number(summary.metrics.profitFactor || 0).toFixed(2)],
    ['Total Trades', String(summary.metrics.totalTrades || 0)],
    ['Sharpe Ratio', Number(summary.metrics.sharpe || 0).toFixed(2)],
    ['Average Win', formatCurrency(summary.metrics.averageWin)],
    ['Average Loss', formatCurrency(summary.metrics.averageLoss)],
    ['Largest Win', formatCurrency(summary.analysis.largestWin)],
    ['Largest Loss', formatCurrency(summary.analysis.largestLoss)],
    ['Buy Win %', `${summary.analysis.buyWinRate.toFixed(1)}%`],
    ['Sell Win %', `${summary.analysis.sellWinRate.toFixed(1)}%`],
    ['Max Consecutive Wins', String(summary.analysis.maxConsecutiveWins || 0)],
    ['Max Consecutive Losses', String(summary.analysis.maxConsecutiveLosses || 0)],
    ['Average Trade', formatCurrency(summary.analysis.averageTrade)],
    ['Expectancy', formatCurrency(summary.analysis.expectancy)],
    ['Recovery Factor', Number(summary.analysis.recoveryFactor || 0).toFixed(2)],
  ];
  const dayRows = summary.analysis.profitByDay.map((day) => `
    <tr>
      <td>${escapeHtml(day.label)}</td>
      <td>${day.trades}</td>
      <td>${day.wins}</td>
      <td>${day.losses}</td>
      <td>${formatCurrency(day.pnl)}</td>
    </tr>
  `).join('');

  const hourRows = summary.analysis.entriesByHour.map((hour) => `
    <tr>
      <td>${escapeHtml(hour.label)}</td>
      <td>${hour.entries}</td>
      <td>${hour.buyEntries}</td>
      <td>${hour.sellEntries}</td>
      <td>${hour.wins}</td>
      <td>${hour.losses}</td>
      <td>${formatCurrency(hour.pnl)}</td>
    </tr>
  `).join('');

  const tradeRows = summary.trades.map((trade) => `
    <tr>
      <td>${escapeHtml(trade.side)}</td>
      <td>${escapeHtml(formatReportTimestamp(trade.entryTime, rules))}</td>
      <td>${escapeHtml(formatReportTimestamp(trade.exitTime, rules))}</td>
      <td>${formatPrice(trade.entry, engine.profile)}</td>
      <td>${formatPrice(trade.exit, engine.profile)}</td>
      <td>${formatCurrency(trade.pnl)}</td>
      <td>${escapeHtml(trade.reason)}</td>
    </tr>
  `).join('');

  const weekdayChart = buildReportBarChartSvg({
    items: summary.analysis.profitByDay,
    valueKey: 'pnl',
    valueType: 'currency',
    positiveNegative: true,
    tooltipFormatter: (day) => `${day.label}: ${formatCurrency(day.pnl)} | ${day.trades} trades | ${day.wins} wins | ${day.losses} losses`,
  });

  const hourlyChart = buildReportBarChartSvg({
    items: summary.analysis.entriesByHour,
    valueKey: 'entries',
    valueType: 'number',
    tooltipFormatter: (hour) => `${hour.label}: ${hour.entries} entries | ${hour.buyEntries} buy | ${hour.sellEntries} sell | ${hour.wins} wins | ${hour.losses} losses | ${formatCurrency(hour.pnl)}`,
  });

  const monthlyChart = buildReportBarChartSvg({
    items: months.map((month, index) => ({ label: month, pnl: summary.monthly[index] })),
    valueKey: 'pnl',
    valueType: 'currency',
    positiveNegative: true,
    tooltipFormatter: (month) => `${month.label}: ${formatCurrency(month.pnl)}`,
  });

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(strategyName)} Backtest Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 32px; color: #12243f; }
    h1, h2 { margin: 0 0 12px; }
    p { margin: 0 0 8px; }
    .meta, .grid { margin-bottom: 24px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
    .card { border: 1px solid #d5e0ef; border-radius: 12px; padding: 12px 14px; background: #f8fbff; }
    .chart-card { border: 1px solid #d5e0ef; border-radius: 14px; padding: 12px 14px; background: #fbfdff; margin-bottom: 24px; }
    .chart-card p { margin: 0 0 10px; }
    svg { width: 100%; height: auto; display: block; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    th, td { border: 1px solid #d5e0ef; padding: 8px 10px; text-align: left; }
    th { background: #eef4fb; }
    .muted { color: #627494; }
  </style>
</head>
<body>
  <h1>${escapeHtml(strategyName)} Backtest Report</h1>
  <p class="muted">Generated ${escapeHtml(generatedAt)}</p>
  <div class="meta">
    <p><strong>Source:</strong> ${escapeHtml(setup.dataSource)}</p>
    <p><strong>Market:</strong> ${escapeHtml(setup.symbol)} ${escapeHtml(setup.timeframe)} (${escapeHtml(setup.from)} to ${escapeHtml(setup.to)})</p>
      <p><strong>Strategy Time:</strong> ${escapeHtml(describeReportTimezone(rules))}</p>
      <p><strong>Sessions:</strong> ${escapeHtml(sessions.join(', '))}</p>
    <p><strong>Description:</strong> ${escapeHtml(strategyDescription || 'No description')}</p>
  </div>
  <h2>Runtime Configuration</h2>
  <div class="grid">
    ${runtimeRows.map(([label, value]) => `<div class="card"><strong>${escapeHtml(label)}</strong><p>${escapeHtml(value)}</p></div>`).join('')}
  </div>
  <h2>Performance Summary</h2>
  <div class="grid">
    ${metricsRows.map(([label, value]) => `<div class="card"><strong>${escapeHtml(label)}</strong><p>${escapeHtml(value)}</p></div>`).join('')}
  </div>
  <h2>Profits by Day</h2>
  <div class="chart-card">
    <p class="muted">Weekday profit distribution</p>
    ${weekdayChart}
  </div>
  <table>
    <thead><tr><th>Day</th><th>Trades</th><th>Wins</th><th>Losses</th><th>P/L</th></tr></thead>
    <tbody>${dayRows}</tbody>
  </table>
  <h2>Entries by Time</h2>
  <div class="chart-card">
    <p class="muted">Hourly entry distribution</p>
    ${hourlyChart}
  </div>
  <table>
    <thead><tr><th>Hour</th><th>Entries</th><th>Buy</th><th>Sell</th><th>Wins</th><th>Losses</th><th>P/L</th></tr></thead>
    <tbody>${hourRows}</tbody>
  </table>
  <h2>Monthly</h2>
  <div class="chart-card">
    <p class="muted">Monthly net performance</p>
    ${monthlyChart}
  </div>
  <h2>Trades</h2>
  <table>
    <thead><tr><th>Side</th><th>Entry Time</th><th>Exit Time</th><th>Entry</th><th>Exit</th><th>P/L</th><th>Reason</th></tr></thead>
    <tbody>${tradeRows || '<tr><td colspan="7">No trades</td></tr>'}</tbody>
  </table>
</body>
</html>`;
}

function formatMetric(type, value) {
  if (type === 'currency') {
    return formatCurrency(value);
  }
  if (type === 'percent') {
    return `${Math.abs(value).toFixed(1)}%`;
  }
  if (type === 'decimal') {
    return Number(value || 0).toFixed(2);
  }
  return `${Math.round(value || 0)}`;
}

function formatChartAxisValue(value, mode = 'number') {
  const absolute = Math.abs(Number(value) || 0);
  const sign = value < 0 ? '-' : '';
  if (mode === 'currency') {
    if (absolute >= 1000000) {
      return `${sign}$${(absolute / 1000000).toFixed(1)}M`;
    }
    if (absolute >= 1000) {
      return `${sign}$${(absolute / 1000).toFixed(1)}K`;
    }
    return `${sign}$${absolute.toFixed(0)}`;
  }
  if (absolute >= 1000000) {
    return `${sign}${(absolute / 1000000).toFixed(1)}M`;
  }
  if (absolute >= 1000) {
    return `${sign}${(absolute / 1000).toFixed(1)}K`;
  }
  return `${sign}${absolute.toFixed(0)}`;
}

function buildReportBarChartSvg({
  items,
  valueKey,
  labelKey = 'label',
  valueType = 'number',
  positiveNegative = false,
  tooltipFormatter = null,
}) {
  if (!Array.isArray(items) || !items.length) {
    return '<p class="muted">No chart data available.</p>';
  }

  const values = items.map((item) => Number(item?.[valueKey]) || 0);
  const width = 760;
  const height = 260;
  const margin = { top: 18, right: 14, bottom: 42, left: 58 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const maxAbs = Math.max(1, ...values.map((value) => Math.abs(value)));
  const maxValue = positiveNegative ? maxAbs : Math.max(1, ...values);
  const minValue = positiveNegative ? -maxAbs : 0;
  const range = Math.max(1, maxValue - minValue);
  const step = plotWidth / Math.max(items.length, 1);
  const barWidth = Math.max(8, Math.min(26, step * 0.62));
  const tickCount = 4;
  const labelStep = items.length > 18 ? 2 : 1;

  const toY = (value) => margin.top + ((maxValue - value) / range) * plotHeight;
  const zeroY = toY(0);

  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => {
    const ratio = index / tickCount;
    const value = maxValue - (range * ratio);
    const y = toY(value);
    return `
      <g>
        <line x1="${margin.left}" x2="${width - margin.right}" y1="${y}" y2="${y}" stroke="#dbe5f0" stroke-width="1" />
        <text x="${margin.left - 10}" y="${y + 4}" text-anchor="end" font-size="11" fill="#627494">${escapeHtml(formatChartAxisValue(value, valueType))}</text>
      </g>
    `;
  }).join('');

  const bars = items.map((item, index) => {
    const value = Number(item?.[valueKey]) || 0;
    const label = item?.[labelKey] ?? `Item ${index + 1}`;
    const x = margin.left + (index * step) + ((step - barWidth) / 2);
    const y = value >= 0 ? toY(value) : zeroY;
    const barHeight = Math.max(2, Math.abs(toY(value) - zeroY));
    const fill = positiveNegative
      ? value >= 0 ? '#38f2c1' : '#ff6b6b'
      : '#6aa5ff';
    const tooltip = tooltipFormatter ? tooltipFormatter(item) : `${label}: ${value}`;
    const labelMarkup = index % labelStep === 0
      ? `<text x="${x + (barWidth / 2)}" y="${height - 14}" text-anchor="middle" font-size="11" fill="#627494">${escapeHtml(label)}</text>`
      : '';

    return `
      <g>
        <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" rx="4" fill="${fill}">
          <title>${escapeHtml(tooltip)}</title>
        </rect>
        ${labelMarkup}
      </g>
    `;
  }).join('');

  return `
    <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Backtest report chart">
      ${ticks}
      <line x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${height - margin.bottom}" stroke="#9fb2c9" stroke-width="1.2" />
      <line x1="${margin.left}" x2="${width - margin.right}" y1="${zeroY}" y2="${zeroY}" stroke="#9fb2c9" stroke-width="1.2" />
      ${bars}
    </svg>
  `;
}

function AxisBarChart({
  items,
  valueKey = 'value',
  labelKey = 'label',
  valueType = 'number',
  positiveNegative = false,
  tooltipFormatter,
  caption,
  onExpand,
  expanded = false,
}) {
  if (!Array.isArray(items) || !items.length) {
    return <p className="muted-text">No chart data yet.</p>;
  }

  const values = items.map((item) => Number(item?.[valueKey]) || 0);
  const chartWidth = 640;
  const chartHeight = expanded ? 420 : 208;
  const margin = { top: 12, right: 10, bottom: 34, left: 46 };
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const maxAbs = Math.max(1, ...values.map((value) => Math.abs(value)));
  const maxValue = positiveNegative ? maxAbs : Math.max(1, ...values);
  const minValue = positiveNegative ? -maxAbs : 0;
  const valueRange = Math.max(1, maxValue - minValue);
  const step = plotWidth / Math.max(items.length, 1);
  const barWidth = Math.max(8, Math.min(24, step * 0.62));
  const tickCount = 4;
  const labelStep = items.length > 18 ? 2 : 1;

  const toY = (value) => margin.top + ((maxValue - value) / valueRange) * plotHeight;
  const zeroY = toY(0);

  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => {
    const ratio = index / tickCount;
    const value = maxValue - (valueRange * ratio);
    return {
      id: `tick-${index}`,
      value,
      y: toY(value),
    };
  });

  const chartContent = (
    <div className={`tester-axis-chart ${onExpand ? 'interactive' : ''} ${expanded ? 'expanded' : ''}`}>
      <svg
        className="tester-axis-chart-svg"
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        role="img"
        aria-label="Backtest analysis chart"
      >
        {ticks.map((tick) => (
          <g key={tick.id}>
            <line
              x1={margin.left}
              x2={chartWidth - margin.right}
              y1={tick.y}
              y2={tick.y}
              className="tester-axis-chart-gridline"
            />
            <text
              x={margin.left - 8}
              y={tick.y + 4}
              textAnchor="end"
              className="tester-axis-chart-tick"
            >
              {formatChartAxisValue(tick.value, valueType)}
            </text>
          </g>
        ))}

        <line
          x1={margin.left}
          x2={margin.left}
          y1={margin.top}
          y2={chartHeight - margin.bottom}
          className="tester-axis-chart-axis"
        />
        <line
          x1={margin.left}
          x2={chartWidth - margin.right}
          y1={zeroY}
          y2={zeroY}
          className="tester-axis-chart-axis"
        />

        {items.map((item, index) => {
          const value = Number(item?.[valueKey]) || 0;
          const label = item?.[labelKey] ?? `Item ${index + 1}`;
          const x = margin.left + (index * step) + ((step - barWidth) / 2);
          const y = value >= 0 ? toY(value) : zeroY;
          const height = Math.max(2, Math.abs(toY(value) - zeroY));
          const tone = positiveNegative
            ? value >= 0
              ? 'positive'
              : 'negative'
            : 'neutral';
          const tooltip = tooltipFormatter ? tooltipFormatter(item) : `${label}: ${value}`;

          return (
            <g key={`${label}-${index}`}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={height}
                rx="4"
                className={`tester-axis-chart-bar ${tone}`}
              >
                <title>{tooltip}</title>
              </rect>
              {index % labelStep === 0 && (
                <text
                  x={x + (barWidth / 2)}
                  y={chartHeight - 12}
                  textAnchor="middle"
                  className="tester-axis-chart-label"
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {caption ? <div className="tester-axis-chart-caption">{caption}</div> : null}
    </div>
  );

  if (!onExpand) {
    return chartContent;
  }

  return (
    <div
      className="tester-axis-chart-button"
      role="button"
      tabIndex={0}
      onClick={onExpand}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onExpand();
        }
      }}
      aria-label="Expand chart"
      title="Click to expand chart"
    >
      {chartContent}
    </div>
  );
}


function getPreviewBarLimit(timeframe) {
  switch (timeframe) {
    case 'M1':
      return 15000;
    case 'M5':
      return 12000;
    case 'M15':
      return 8000;
    case 'H1':
      return 5000;
    case 'H4':
      return 3000;
    case 'D1':
      return 2000;
    default:
      return 5000;
  }
}

function formatMinutesAsDuration(totalMinutes) {
  const safeMinutes = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  if (hours && minutes) {
    return `${hours}h ${minutes}m`;
  }
  if (hours) {
    return `${hours}h`;
  }
  return `${minutes}m`;
}

function getDateRangeEpochs(from, to) {
  const startEpoch = Math.floor(new Date(`${from}T00:00:00Z`).getTime() / 1000);
  const endEpoch = Math.floor(new Date(`${to}T23:59:59Z`).getTime() / 1000);
  return { startEpoch, endEpoch };
}

function dateRangeIncludesNow(from, to) {
  const { startEpoch, endEpoch } = getDateRangeEpochs(from, to);
  const nowEpoch = Math.floor(Date.now() / 1000);
  return Number.isFinite(startEpoch) && Number.isFinite(endEpoch) && nowEpoch >= startEpoch && nowEpoch <= endEpoch;
}

function formatSymbolDisplayName(symbol) {
  const raw = String(symbol || '').trim();
  if (!raw) {
    return '';
  }
  if (/\s/.test(raw)) {
    return raw;
  }
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (/^[A-Z]{3,6}$/.test(compact) && !/(BOOM|CRASH|VOLATILITY|STEP|JUMP|INDEX|RANGE|DRIFT|SWITCH)/.test(compact)) {
    return compact;
  }

  const spaced = raw
    .replace(/[_-]+/g, ' ')
    .replace(/([A-Za-z]+)(\d+)/g, '$1 $2')
    .replace(/(\d+)([A-Za-z]+)/g, '$1 $2')
    .replace(/\b(BOOM|CRASH|VOLATILITY|STEP|JUMP|INDEX|RANGE|BREAK|DRIFT|SWITCH)\b/gi, (match) => (
      match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
    ))
    .replace(/\s+/g, ' ')
    .trim();

  return spaced || raw;
}

function requestBacktestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return;
  }
  if (window.Notification.permission === 'default') {
    const permissionRequest = window.Notification.requestPermission();
    if (permissionRequest?.catch) {
      permissionRequest.catch(() => {});
    }
  }
}

function notifyBacktestComplete({ setup, summary }) {
  if (
    typeof window === 'undefined'
    || !('Notification' in window)
    || window.Notification.permission !== 'granted'
    || (typeof document !== 'undefined' && !document.hidden)
  ) {
    return;
  }

  const metrics = summary?.metrics || {};
  const symbol = formatSymbolDisplayName(setup?.symbol);
  const timeframe = setup?.timeframe || '';
  const netProfit = Number.isFinite(Number(metrics.netProfit))
    ? formatCurrency(metrics.netProfit)
    : 'n/a';
  const totalTrades = Number.isFinite(Number(metrics.totalTrades))
    ? `${metrics.totalTrades} trades`
    : 'complete';

  try {
    new window.Notification('VixAlgoBuilder backtest complete', {
      body: `${symbol} ${timeframe} · ${totalTrades} · Net ${netProfit}`.trim(),
      tag: 'vixalgobuilder-backtest-complete',
    });
  } catch (error) {
    // Browser notification support is best-effort and must not affect test completion.
  }
}

function getPreviewWindow(setup) {
  const timeframeSeconds = TIMEFRAME_SECONDS[setup.timeframe] || TIMEFRAME_SECONDS.H1;
  const previewBarLimit = getPreviewBarLimit(setup.timeframe);
  const { startEpoch, endEpoch } = getDateRangeEpochs(setup.from, setup.to);
  const previewStartEpoch = Math.max(
    startEpoch,
    endEpoch - (previewBarLimit * timeframeSeconds) + timeframeSeconds,
  );

  return {
    previewBarLimit,
    startEpoch,
    endEpoch,
    previewStartEpoch,
    previewEndEpoch: endEpoch,
  };
}

function formatDateForInput(timestamp) {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function formatLocalDateForInput(date) {
  const nextDate = date instanceof Date ? date : new Date(date);
  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
  const day = String(nextDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTodayForInput() {
  return formatLocalDateForInput(new Date());
}

function formatOneYearAgoForInput() {
  const today = new Date();
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  return formatLocalDateForInput(oneYearAgo);
}

function getRollingOneYearDateRange() {
  return {
    from: formatOneYearAgoForInput(),
    to: formatTodayForInput(),
  };
}

function getJobStageLabel(job) {
  if (!job) {
    return 'Idle';
  }
  if (job.status === 'queued') {
    return 'Queued';
  }
  if (job.status === 'failed') {
    return 'Failed';
  }
  if (job.status === 'cancel_requested') {
    return 'Cancelling';
  }
  if (job.status === 'cancelled') {
    return 'Cancelled';
  }
  if (job.status === 'completed') {
    return 'Completed';
  }
  const stage = job.currentStep?.stage;
  if (stage === 'loading-history') {
    return 'Loading History';
  }
  if (stage === 'simulating') {
    return 'Simulating';
  }
  return 'Running';
}

function mergeOlderMarketData(existingData, olderData) {
  if (!existingData?.candles?.length) {
    return olderData;
  }
  if (!olderData?.candles?.length) {
    return existingData;
  }

  const merged = [];
  const seenTimes = new Set();
  [...olderData.candles, ...existingData.candles].forEach((candle) => {
    if (!seenTimes.has(candle.time)) {
      seenTimes.add(candle.time);
      merged.push(candle);
    }
  });

  return {
    ...existingData,
    ...olderData,
    coverage: {
      candleCount: merged.length,
      firstEpoch: Math.floor((merged[0]?.time || 0) / 1000),
      lastEpoch: Math.floor((merged[merged.length - 1]?.time || 0) / 1000),
      cached: Boolean(existingData.coverage?.cached && olderData.coverage?.cached),
    },
    candles: merged.map((candle, index) => ({
      ...candle,
      index,
    })),
  };
}

// Live feed status.
//
// The Builder has always had a real tick stream, but nothing ever rendered its state: the
// status line said "Live ticks connected separately" whenever the subscription GATE passed,
// which is a claim about configuration rather than an observation about data. A feed you
// cannot see is worth nothing for trust — which is the only reason to stream ticks into a
// backtesting tool at all.
//
// Four distinct states, because they need different actions from the user:
//   off       the date range does not reach today, so no subscription was attempted
//   waiting   subscribed, no tick yet — normal for a few seconds, suspicious after a minute
//   stale     ticks ARE arriving but the merge is refusing them (history too far behind)
//   live      ticks arriving and reaching the chart
function LiveFeedIndicator({ enabled, tick, timeframeLabel }) {
  if (!enabled) {
    return (
      <span className="tester-live-pill is-off" title="The tested date range does not include today, so no live feed is subscribed.">
        <i />
        Live off
      </span>
    );
  }

  const secondsAgo = tick.at ? Math.max(0, Math.round((Date.now() - tick.at) / 1000)) : null;
  const receiving = secondsAgo !== null && secondsAgo < 90;
  // Every tick refused means the feed is connected but invisible — worth calling out rather
  // than showing a healthy green dot over a chart that is not moving.
  const allDropped = tick.count > 0 && tick.dropped === tick.count;

  if (!tick.count) {
    return (
      <span className="tester-live-pill is-waiting" title="Subscribed to the tick stream, waiting for the first tick.">
        <i />
        Connecting…
      </span>
    );
  }

  if (allDropped) {
    return (
      <span
        className="tester-live-pill is-stale"
        title={`Receiving ticks, but the loaded history ends more than six ${timeframeLabel || ''} bars ago, so they cannot be attached to the chart. Reload the history to catch up.`}
      >
        <i />
        Feed ahead of history
      </span>
    );
  }

  // Label stays a single word; the timing that decides live-vs-idle moves to the tooltip. The
  // pulsing dot already carries "data is arriving", so a counter next to it was noise.
  return (
    <span
      className={`tester-live-pill ${receiving ? 'is-live' : 'is-waiting'}`}
      title={`${tick.count} ticks received${tick.dropped ? `, ${tick.dropped} not applied` : ''}`
        + `. Last tick ${secondsAgo === 0 ? 'just now' : `${secondsAgo}s ago`}.`}
    >
      <i />
      {receiving ? 'Live' : 'Idle'}
    </span>
  );
}

function mergeLiveTickIntoMarketData(existingData, tick, timeframe) {
  if (!existingData?.candles?.length || !tick?.price) {
    return existingData;
  }

  const timeframeSeconds = TIMEFRAME_SECONDS[timeframe] || TIMEFRAME_SECONDS.H1;
  const nextCandles = existingData.candles.slice();
  const lastCandle = nextCandles[nextCandles.length - 1];
  const tickEpoch = Math.floor(tick.time / 1000);
  const lastEpoch = Math.floor(lastCandle.time / 1000);
  const nextBoundary = lastEpoch + timeframeSeconds;

  if (tickEpoch >= nextBoundary) {
    const candleStart = tickEpoch - (tickEpoch % timeframeSeconds);
    const tooFarFromHistory = candleStart - lastEpoch > timeframeSeconds * 6;
    if (tooFarFromHistory) {
      return existingData;
    }
    const spread = Number.isFinite(Number(tick.ask)) && Number.isFinite(Number(tick.bid))
      ? Math.max(0, Math.round((Number(tick.ask) - Number(tick.bid)) / Math.max(existingData.profile?.point || 0.00001, 0.00001)))
      : lastCandle.spread ?? null;
    const open = candleStart === nextBoundary ? lastCandle.close : tick.price;
    nextCandles.push({
      index: nextCandles.length,
      time: candleStart * 1000,
      label: new Date(candleStart * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      open,
      high: Math.max(open, tick.price),
      low: Math.min(open, tick.price),
      close: tick.price,
      spread,
    });
  } else {
    const spread = Number.isFinite(Number(tick.ask)) && Number.isFinite(Number(tick.bid))
      ? Math.max(0, Math.round((Number(tick.ask) - Number(tick.bid)) / Math.max(existingData.profile?.point || 0.00001, 0.00001)))
      : lastCandle.spread ?? null;
    nextCandles[nextCandles.length - 1] = {
      ...lastCandle,
      high: Math.max(lastCandle.high, tick.price),
      low: Math.min(lastCandle.low, tick.price),
      close: tick.price,
      spread,
    };
  }

  return {
    ...existingData,
    candles: nextCandles,
  };
}

// Live optimization visualization shown in the chart area: the best variant's equity curve
// plus a pass-vs-metric scatter with the best-so-far envelope (MT5-tester style).
function OptimizationVizPanel({ viz, metricLabel }) {
  const W = 960;
  const equityH = 300;
  const scatterH = 240;
  const pad = { left: 56, right: 20, top: 20, bottom: 28 };
  const series = Array.isArray(viz.series) ? viz.series : [];
  const equity = Array.isArray(viz.equity) ? viz.equity : [];

  const equityValues = equity.map((point) => point.equity).filter((value) => Number.isFinite(value));
  const eMin = equityValues.length ? Math.min(...equityValues) : 0;
  const eMax = equityValues.length ? Math.max(...equityValues) : 1;
  const eRange = Math.max(1e-6, eMax - eMin);
  const eX = (i) => pad.left + (equity.length <= 1 ? 0 : (i / (equity.length - 1)) * (W - pad.left - pad.right));
  const eY = (v) => pad.top + (1 - (v - eMin) / eRange) * (equityH - pad.top - pad.bottom);
  const equityPath = equity.map((point, i) => `${i === 0 ? 'M' : 'L'} ${eX(i).toFixed(1)} ${eY(point.equity).toFixed(1)}`).join(' ');

  const values = series.map((point) => point.value).filter((value) => Number.isFinite(value));
  const vMin = values.length ? Math.min(...values, 0) : 0;
  const vMax = values.length ? Math.max(...values, 0) : 1;
  const vRange = Math.max(1e-6, vMax - vMin);
  const maxPass = series.length ? Math.max(...series.map((point) => point.pass)) : 1;
  const sX = (p) => pad.left + (maxPass <= 0 ? 0 : (p / maxPass) * (W - pad.left - pad.right));
  const sY = (v) => pad.top + (1 - (v - vMin) / vRange) * (scatterH - pad.top - pad.bottom);
  let bestSoFar = -Infinity;
  const envelope = series.map((point) => {
    bestSoFar = Math.max(bestSoFar, point.value);
    return { pass: point.pass, value: bestSoFar };
  });
  const envelopePath = envelope.map((point, i) => `${i === 0 ? 'M' : 'L'} ${sX(point.pass).toFixed(1)} ${sY(point.value).toFixed(1)}`).join(' ');
  const zeroY = vMin < 0 && vMax > 0 ? sY(0) : null;

  return (
    <div className="tester-optimize-viz">
      <div className="tester-optimize-viz-head">
        <strong>{viz.done !== undefined && viz.total ? `Optimizing ${viz.done} / ${viz.total}` : 'Optimization results'}</strong>
        <span className="muted-text">Best {metricLabel}: {Number.isFinite(Number(viz.bestValue)) ? Number(viz.bestValue) : '—'}</span>
      </div>
      {equity.length > 1 && (
        <div className="tester-optimize-viz-block">
          <span className="tester-optimize-viz-label">Best variant equity</span>
          <svg viewBox={`0 0 ${W} ${equityH}`} preserveAspectRatio="none" className="tester-optimize-viz-svg">
            <line x1={pad.left} x2={W - pad.right} y1={eY(eMin)} y2={eY(eMin)} className="tester-grid-line" />
            <line x1={pad.left} x2={W - pad.right} y1={eY(eMax)} y2={eY(eMax)} className="tester-grid-line" />
            <text x={8} y={eY(eMax) + 4} className="tester-axis-text">{Math.round(eMax)}</text>
            <text x={8} y={eY(eMin) + 4} className="tester-axis-text">{Math.round(eMin)}</text>
            <path d={equityPath} className="tester-optimize-equity-line" fill="none" />
          </svg>
        </div>
      )}
      {series.length > 0 && (
        <div className="tester-optimize-viz-block">
          <span className="tester-optimize-viz-label">Each pass ({metricLabel}) · best-so-far envelope</span>
          <svg viewBox={`0 0 ${W} ${scatterH}`} preserveAspectRatio="none" className="tester-optimize-viz-svg">
            {zeroY !== null && <line x1={pad.left} x2={W - pad.right} y1={zeroY} y2={zeroY} className="tester-guide-line tester-guide-line-zero" />}
            <line x1={pad.left} x2={W - pad.right} y1={sY(vMax)} y2={sY(vMax)} className="tester-grid-line" />
            <text x={8} y={sY(vMax) + 4} className="tester-axis-text">{Number(vMax.toFixed(2))}</text>
            <text x={8} y={sY(vMin) + 4} className="tester-axis-text">{Number(vMin.toFixed(2))}</text>
            {series.map((point, i) => (
              <circle key={i} cx={sX(point.pass)} cy={sY(point.value)} r="2.4" className={point.value >= bestSoFar - 1e-9 ? 'tester-optimize-dot-best' : 'tester-optimize-dot'} />
            ))}
            <path d={envelopePath} className="tester-optimize-envelope" fill="none" />
          </svg>
        </div>
      )}
    </div>
  );
}

function StrategyTester() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [setup, setSetup] = useState(DEFAULT_SETUP);
  const [indicators, setIndicators] = useState(DEFAULT_INDICATORS);
  const [rules, setRules] = useState(() => normalizeRules(DEFAULT_RULES));
  const [sessions, setSessions] = useState(DEFAULT_SESSION_IDS);
  const [mobileView, setMobileView] = useState('chart');
  const [lightMode, setLightMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedChart, setExpandedChart] = useState('');
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [serverRunPending, setServerRunPending] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState('1x');
  const [leftTab, setLeftTab] = useState('market');
  const [rightTab, setRightTab] = useState('summary');
  const [leftWidth, setLeftWidth] = useState(340);
  const [rightWidth, setRightWidth] = useState(360);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(true);
  const [resizing, setResizing] = useState(null);
  const [marketData, setMarketData] = useState(null);
  // Chart capture target: '', 'pattern:long'/'pattern:short' (Pattern by Example),
  // or 'conditions:long'/'conditions:short' (Conditions by Example).
  const [captureTarget, setCaptureTarget] = useState('');
  const [pendingConditionCapture, setPendingConditionCapture] = useState(null); // { side, conditions } awaiting replace/append
  const [captureStrictness, setCaptureStrictness] = useState(2); // bars of depth kept from a capture
  const [conditionCollapsed, setConditionCollapsed] = useState({ long: false, short: false });
  const [marketReloadNonce, setMarketReloadNonce] = useState(0);
  const [marketState, setMarketState] = useState({
    status: 'ready',
    error: '',
    providerSymbol: '',
    lastLoadedAt: null,
    candleCount: 0,
    loadingStartedAt: null,
    loadingFrom: '',
    loadingTo: '',
    loadingTimeframe: '',
    loadingSymbol: '',
    liveTicksEnabled: false,
    historyQuality: null,
  });
  // Live tick telemetry, kept separate from marketState so a tick arriving cannot churn the
  // heavier market-status object.
  //
  // This exists because "Live ticks connected separately" was a claim, not an observation: it
  // was printed whenever the subscription GATE passed, whether or not a single tick had ever
  // arrived. `dropped` counts ticks the merge discarded — the merge refuses any tick more than
  // six timeframes past the last loaded candle, so stale history silently swallows the feed and
  // the chart looks identical to having no connection at all.
  const [liveTick, setLiveTick] = useState({ at: null, count: 0, dropped: 0 });
  const [, setMarketStatusClock] = useState(0);
  const [derivCatalog, setDerivCatalog] = useState(null);
  const [engine, setEngine] = useState(INITIAL_ENGINE);
  const [inspectedBar, setInspectedBar] = useState(null);
  const [aiRecSelections, setAiRecSelections] = useState({});
  const [optimizeParams, setOptimizeParams] = useState([]);
  const [optimizeMetric, setOptimizeMetric] = useState('profitFactor');
  const [optimizeMinTrades, setOptimizeMinTrades] = useState('30');
  const [optimizeJob, setOptimizeJob] = useState(null);
  const [optimizePending, setOptimizePending] = useState(false);
  const [optimizeMode, setOptimizeMode] = useState('grid'); // 'grid' | 'walkforward'
  const [spreadSampling, setSpreadSampling] = useState(false);
  const [spreadNotice, setSpreadNotice] = useState('');
  const [walkForwardWindows, setWalkForwardWindows] = useState('4');
  const optimizePollRef = useRef(null);
  const [currentBar, setCurrentBar] = useState(INITIAL_ENGINE.candles.length - 1);
  const [serverResult, setServerResult] = useState(null);
  const [pendingServerResult, setPendingServerResult] = useState(null);
  const [hasRunBacktest, setHasRunBacktest] = useState(false);
  const [backfillJob, setBackfillJob] = useState(null);
  const [backtestJob, setBacktestJob] = useState(null);
  const [backfillStartPending, setBackfillStartPending] = useState(false);
  const [olderHistoryPending, setOlderHistoryPending] = useState(false);
  const [chartViewRevision, setChartViewRevision] = useState(0);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('userToken') || '');
  const [userProfile, setUserProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile') || 'null');
    } catch (error) {
      return null;
    }
  });
  const [strategyName, setStrategyName] = useState(DEFAULT_STRATEGY_NAME);
  const [strategyDescription, setStrategyDescription] = useState('');
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [savedStrategiesLoading, setSavedStrategiesLoading] = useState(false);
  const [savedStrategiesError, setSavedStrategiesError] = useState('');
  const [strategyActionPending, setStrategyActionPending] = useState('');
  const [strategyNotice, setStrategyNotice] = useState('');
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [activeStrategyId, setActiveStrategyId] = useState('');
  const [builderAiMessages, setBuilderAiMessages] = useState(() => ([
    { role: 'assistant', content: BUILDER_AI_INITIAL_MESSAGE },
  ]));
  const [builderAiInput, setBuilderAiInput] = useState('');
  const [builderAiConversationId, setBuilderAiConversationId] = useState(null);
  const [builderAiPending, setBuilderAiPending] = useState(false);
  const [, setBuilderAiNotice] = useState('');
  const [builderAiToolPendingId, setBuilderAiToolPendingId] = useState('');
  const [autoRunAfterBackfill, setAutoRunAfterBackfill] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [walkthroughOpen, setWalkthroughOpen] = useState(() => {
    try {
      return localStorage.getItem(WALKTHROUGH_STORAGE_KEY) !== '1';
    } catch (error) {
      return false;
    }
  });
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const timerRef = useRef(null);
  const replayClockRef = useRef(0);
  const runningRef = useRef(false);
  const serverRunPendingRef = useRef(false);
  const setupRef = useRef(setup);
  const workspaceRef = useRef(null);
  const chartDataSigRef = useRef('');
  const derivCatalogRef = useRef(null);
  const derivTickStreamRef = useRef(null);
  const olderHistoryAnchorRef = useRef(null);
  const runRequestTokenRef = useRef(0);
  const activeRunSignatureRef = useRef('');
  const serverResultCacheRef = useRef(new Map());
  const walkthroughInitializedRef = useRef(false);
  const savedPayloadKeyRef = useRef(null);
  if (savedPayloadKeyRef.current === null) {
    const _defaultNormalized = normalizeLoadedStrategy({});
    savedPayloadKeyRef.current = buildSavedPayloadKey({ ..._defaultNormalized, indicators: DEFAULT_INDICATORS });
  }
  const requestedStrategyId = searchParams.get('strategy') || '';
  // Deep-link date range, used by "Replay" from a live signal so the Builder opens on the
  // window containing that trade rather than on the strategy's saved range (which predates
  // every live trade by definition).
  const requestedFrom = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('from') || '') ? searchParams.get('from') : '';
  const requestedTo = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('to') || '') ? searchParams.get('to') : '';
  // Epoch ms marking where a live deployment began. Purely visual: the chart draws a boundary
  // and de-emphasises trades before it, so a replay makes the backtest/forward split obvious.
  const requestedLiveFrom = Number(searchParams.get('liveFrom')) || null;

  // Live deployments owned by this user, so the Builder can tell when the strategy on screen is
  // already running as a cloud signal.
  const [userDeployments, setUserDeployments] = useState([]);
  useEffect(() => {
    if (!authToken) { setUserDeployments([]); return; }
    let cancelled = false;
    listDeployments()
      .then((payload) => { if (!cancelled) setUserDeployments(payload.deployments || []); })
      .catch(() => { if (!cancelled) setUserDeployments([]); });
    return () => { cancelled = true; };
  }, [authToken]);

  const activeDeployment = useMemo(() => (
    activeStrategyId
      ? userDeployments.find((deployment) => (
        String(deployment.strategyId) === String(activeStrategyId) && deployment.status !== 'stopped'
      )) || null
      : null
  ), [userDeployments, activeStrategyId]);
  const testerPlan = userProfile?.testerPlan || 'free';
  const strategyLimit = Number.isFinite(Number(userProfile?.testerStrategyLimit))
    ? Number(userProfile.testerStrategyLimit)
    : testerPlan === 'premium' ? 100 : 1;
  const canExportMq5 = !!userProfile?.canExportMq5;
  const canAttemptMq5Export = canExportMq5 || testerPlan !== 'premium';
  const remainingStrategySlots = Math.max(0, strategyLimit - savedStrategies.length);
  const atStrategyLimit = !activeStrategyId && savedStrategies.length >= strategyLimit;

  const buildStrategyPayload = useCallback((overrides = {}) => ({
    name: (overrides.name ?? strategyName).trim() || DEFAULT_STRATEGY_NAME,
    description: (overrides.description ?? strategyDescription).trim(),
    setup: { ...setup, dataSource: 'deriv', brokerProfileId: 'deriv-svg-server-03' },
    indicators: indicators.map((indicator) => ({ ...indicator, params: { ...indicator.params } })),
    rules: JSON.parse(JSON.stringify(rules)),
    sessions: [...sessions],
  }), [indicators, rules, sessions, setup, strategyDescription, strategyName]);
  const currentBacktestSignature = useMemo(
    () => buildBacktestSignature(setup, indicators, rules, sessions),
    [indicators, rules, sessions, setup],
  );

  const focusWalkthroughStep = useCallback((stepIndex) => {
    const boundedIndex = Math.max(0, Math.min(WALKTHROUGH_STEPS.length - 1, stepIndex));
    const step = WALKTHROUGH_STEPS[boundedIndex];
    setWalkthroughStep(boundedIndex);
    if (step.panel === 'setup') {
      setMobileView('setup');
      setLeftCollapsed(false);
      setRightCollapsed(true);
      if (step.tab) {
        setLeftTab(step.tab);
      }
    } else if (step.panel === 'results') {
      setMobileView('results');
      setRightCollapsed(false);
      if (step.tab) {
        setRightTab(step.tab);
      }
    } else {
      setMobileView('chart');
      setLeftCollapsed(true);
      setRightCollapsed(true);
    }
  }, []);

  const openWalkthrough = useCallback(() => {
    setWalkthroughOpen(true);
    focusWalkthroughStep(0);
  }, [focusWalkthroughStep]);

  const closeWalkthrough = useCallback(() => {
    setWalkthroughOpen(false);
    try {
      localStorage.setItem(WALKTHROUGH_STORAGE_KEY, '1');
    } catch (error) {
      // Ignore unavailable storage; the guide can still be dismissed for this session.
    }
  }, []);

  const openDialog = useCallback((nextDialog) => {
    setDialog({
      title: 'Confirm Action',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: '',
      ...nextDialog,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);

  const advanceWalkthrough = useCallback(() => {
    if (walkthroughStep >= WALKTHROUGH_STEPS.length - 1) {
      closeWalkthrough();
      return;
    }
    focusWalkthroughStep(walkthroughStep + 1);
  }, [closeWalkthrough, focusWalkthroughStep, walkthroughStep]);

  useEffect(() => {
    if (!walkthroughOpen || walkthroughInitializedRef.current) {
      return;
    }
    walkthroughInitializedRef.current = true;
    focusWalkthroughStep(walkthroughStep);
  }, [focusWalkthroughStep, walkthroughOpen, walkthroughStep]);

  useEffect(() => {
    const requestedPanel = searchParams.get('panel');
    if (requestedPanel === 'ai') {
      setLeftTab('ai');
      setLeftCollapsed(false);
      setMobileView('setup');
    }
  }, [searchParams]);

  const applySavedStrategy = useCallback((strategy, options = {}) => {
    const { updateQuery = true } = options;
    const normalized = normalizeLoadedStrategy(strategy);
    setSetup(normalized.setup);
    setIndicators(normalized.indicators);
    setRules(normalized.rules);
    setSessions(normalized.sessions);
    setStrategyName(normalized.name);
    setStrategyDescription(normalized.description);
    setSelectedStrategyId(normalized.id);
    setActiveStrategyId(normalized.id);
    setHasRunBacktest(false);
    setChartViewRevision((previous) => previous + 1);
    setServerResult(null);
    setBacktestJob(null);
    setBackfillJob(null);
    setLeftCollapsed(false);
    setRightCollapsed(false);
    setLeftTab('market');
    setMobileView('setup');
    savedPayloadKeyRef.current = buildSavedPayloadKey(normalized);
    if (updateQuery && normalized.id) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('strategy', normalized.id);
      // Signal-replay context belongs to the strategy it was opened with. Carrying it to a
      // different strategy would draw another deployment's boundary over an unrelated symbol.
      nextParams.delete('liveFrom');
      nextParams.delete('from');
      nextParams.delete('to');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const applyDefaultStrategy = useCallback(() => {
    const normalized = normalizeLoadedStrategy({});
    const defaultNormalized = { ...normalized, indicators: DEFAULT_INDICATORS };
    setSetup(normalized.setup);
    setIndicators(DEFAULT_INDICATORS);
    setRules(normalized.rules);
    setSessions(normalized.sessions);
    setStrategyName(normalized.name);
    setStrategyDescription(normalized.description);
    setSelectedStrategyId('');
    setActiveStrategyId('');
    setHasRunBacktest(false);
    setChartViewRevision((previous) => previous + 1);
    setServerResult(null);
    setPendingServerResult(null);
    setBacktestJob(null);
    setBackfillJob(null);
    setLeftCollapsed(false);
    setRightCollapsed(true);
    setLeftTab('indicators');
    setMobileView('setup');
    savedPayloadKeyRef.current = buildSavedPayloadKey(defaultNormalized);
    // Hard reload to /tester clears all URL state and prevents the load-from-URL effect from restoring the old strategy
    window.location.replace('/tester');
  }, []);

  const applyBuilderPayload = useCallback((payload) => {
    const normalized = normalizeLoadedStrategy({
      id: '',
      name: payload?.name || payload?.strategyName || strategyName,
      description: payload?.description || strategyDescription,
      setup: payload?.setup || {},
      indicators: payload?.indicators,
      rules: payload?.rules,
      sessions: payload?.sessions,
    });
    // normalizeLoadedStrategy forces a rolling 1-year range (right for loading saved
    // strategies). When VixAI explicitly set a backtest date, honor it instead.
    if (payload?.setup?.from) normalized.setup.from = payload.setup.from;
    if (payload?.setup?.to) normalized.setup.to = payload.setup.to;
    setSetup(normalized.setup);
    setIndicators(normalized.indicators);
    setRules(normalized.rules);
    setSessions(normalized.sessions);
    setStrategyName(normalized.name);
    setStrategyDescription(normalized.description);
    setSelectedStrategyId('');
    setActiveStrategyId('');
    setHasRunBacktest(false);
    setChartViewRevision((previous) => previous + 1);
    setServerResult(null);
    setPendingServerResult(null);
    setBacktestJob(null);
    setBackfillJob(null);
    setLeftCollapsed(false);
    setRightCollapsed(true);
    setLeftTab('rules');
    setMobileView('setup');
  }, [strategyDescription, strategyName]);

  const clearBuilderRunState = useCallback(() => {
    setHasRunBacktest(false);
    setChartViewRevision((previous) => previous + 1);
    setServerResult(null);
    setPendingServerResult(null);
    setBacktestJob(null);
    setBackfillJob(null);
    setBackfillStartPending(false);
  }, []);

  const applyEmbeddedBuilderAiEdits = useCallback((message) => {
    const lower = String(message || '').toLowerCase();
    const changes = [];
    const warnings = [];
    let changed = false;

    const setModelingModeFromAi = (mode) => {
      const label = getModelLabel(mode);
      setSetup((previous) => {
        if (previous.modelingMode === mode && previous.dataSource === 'deriv') {
          return previous;
        }
        return {
          ...previous,
          modelingMode: mode,
          dataSource: 'deriv',
          brokerProfileId: 'deriv-svg-server-03',
        };
      });
      changes.push(`Modeling mode set to ${label}.`);
      changed = true;
    };

    if (/\b(1|one)[\s-]*(minute|min)\b|\bminute\s+ohlc\b|\bm1\s+ohlc\b|\bevery\s+tick\b/.test(lower)) {
      // Every Tick was removed; map any such request to the most precise available model.
      setModelingModeFromAi('minute_ohlc');
    } else if (/\bopen\s+prices?\b|\bopen\s+prices?\s+only\b/.test(lower)) {
      setModelingModeFromAi('open_prices');
    }

    if (/\bstochastic\b|\bstoch\b/.test(lower)) {
      const hasStochastic = indicators.some((indicator) => indicator.type === 'STOCHASTIC');
      if (!hasStochastic) {
        setIndicators((previous) => (
          previous.some((indicator) => indicator.type === 'STOCHASTIC')
            ? previous
            : [...previous, createIndicator('STOCHASTIC', previous.length)]
        ));
        changes.push('Added a Stochastic oscillator.');
        changed = true;
      } else {
        warnings.push('Stochastic is already in this setup.');
      }
    }

    if (/\btrail(?:ing)?(?:\s+stop)?\b/.test(lower)) {
      const explicitTrailing = lower.match(/(?:trail(?:ing)?(?:\s+stop)?(?:\s+(?:to|at|of|=))?\s*)(\d+(?:\.\d+)?)/)
        || lower.match(/(\d+(?:\.\d+)?)\s*(?:points?|pts?)\s+(?:trail|trailing)/);
      const fallbackTrailing = Number(rules.trailingStop) > 0 ? String(rules.trailingStop) : '500';
      const nextTrailingStop = explicitTrailing?.[1] || (/conservative/.test(lower) ? fallbackTrailing : '');
      if (nextTrailingStop) {
        setRules((previous) => ({
          ...previous,
          trailingStop: nextTrailingStop,
        }));
        changes.push(`Trailing stop set to ${nextTrailingStop} points.`);
        if (!explicitTrailing?.[1] && /conservative/.test(lower)) {
          warnings.push('No distance was specified, so I used 500 points as a conservative starting value. Adjust it for the symbol before exporting.');
        }
        changed = true;
      } else {
        warnings.push('Tell me the trailing stop distance in points and I will set it.');
      }
    }

    if (/\b(info\s+panel|dashboard|status\s+panel)\b/.test(lower) && /\b(ea|strategy|name)\b/.test(lower)) {
      const note = 'Requested visual: info panel showing the EA name.';
      setStrategyDescription((previous) => {
        const current = String(previous || '').trim();
        if (current.toLowerCase().includes(note.toLowerCase())) {
          return previous;
        }
        return current ? `${current}\n${note}` : note;
      });
      changes.push('Added the EA-name info panel request to the strategy description.');
      warnings.push('Custom info panels are planned but not available in chart visuals or MQ5 export yet.');
      changed = true;
    }

    if (changed) {
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('ai');
      setMobileView('setup');
    }

    return { changed, changes, warnings };
  }, [clearBuilderRunState, indicators, rules.trailingStop]);

  function formatVixAiToolResults(toolResults = []) {
    return toolResults
      .map((entry) => {
        if (entry.tool === 'tester.inspect_report' && entry.result?.summary) {
          return `Report inspection: ${entry.result.summary}`;
        }
        if (entry.tool === 'web.search' && Array.isArray(entry.result?.results)) {
          const lines = entry.result.results.slice(0, 5).map((result, index) => (
            `${index + 1}. ${result.title} - ${result.url}`
          ));
          return `Web search results for "${entry.result.query}":\n${lines.join('\n')}`;
        }
        return `${entry.tool}: ${entry.status || 'completed'}`;
      })
      .filter(Boolean)
      .join('\n\n');
  }

  function formatVixAiCompileResponse(response = {}) {
    const compileResult = response.compileResult || response.artifact?.compileResult || {};
    const status = response.status || compileResult.status || 'failed';
    const lines = [
      status === 'compiled' || compileResult.success
        ? 'MQL5 compile passed.'
        : `MQL5 compile did not pass: ${response.message || compileResult.message || 'compile failed.'}`,
    ];
    if (Number.isFinite(Number(compileResult.errors))) {
      lines.push(`Errors: ${compileResult.errors}`);
    }
    if (Number.isFinite(Number(compileResult.warnings))) {
      lines.push(`Warnings: ${compileResult.warnings}`);
    }
    if (compileResult.filename) {
      lines.push(`File: ${compileResult.filename}`);
    }
    if (Array.isArray(compileResult.importantLines) && compileResult.importantLines.length) {
      lines.push(`MetaEditor log:\n${compileResult.importantLines.slice(-12).join('\n')}`);
    }
    return lines.join('\n');
  }

  function formatOptimizationResult(results) {
    if (!results.length) {
      return 'No optimization variants completed.';
    }
    const sorted = [...results].sort((left, right) => Number(right.netProfit || 0) - Number(left.netProfit || 0));
    const lines = sorted.map((result, index) => (
      `${index + 1}. ${result.name}: net ${formatCurrency(result.netProfit)}, PF ${Number(result.profitFactor || 0).toFixed(2)}, DD ${Number(result.maxDrawdown || 0).toFixed(2)}%, trades ${result.totalTrades || 0}`
    ));
    return `Optimization variants completed.\n${lines.join('\n')}`;
  }

  async function executeVixAiToolAction(action) {
    if (!action || action.status === 'blocked') {
      return action?.blockedReason || 'This VixAI action is blocked.';
    }

    const params = action.params || {};
    if (action.tool === 'builder.update_setup') {
      setSetup((previous) => ({
        ...previous,
        ...(params.setupPatch || {}),
        dataSource: 'deriv',
        brokerProfileId: 'deriv-svg-server-03',
      }));
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('market');
      setMobileView('setup');
      return action.description || 'Builder setup updated.';
    }

    if (action.tool === 'builder.add_indicator') {
      const type = String(params.type || '').toUpperCase();
      if (!INDICATOR_LIBRARY[type]) {
        throw new Error(`${type || 'Requested indicator'} is not available in the Builder indicator library.`);
      }
      let added = false;
      setIndicators((previous) => {
        if (previous.some((indicator) => indicator.type === type)) {
          return previous;
        }
        added = true;
        return [...previous, createIndicator(type, previous.length)];
      });
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('indicators');
      setMobileView('setup');
      return added ? `${INDICATOR_LIBRARY[type].label} added to the Builder.` : `${INDICATOR_LIBRARY[type].label} is already in the Builder.`;
    }

    if (action.tool === 'builder.update_rules') {
      setRules((previous) => normalizeRules({
        ...previous,
        ...(params.rulesPatch || {}),
      }));
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('rules');
      setMobileView('setup');
      return action.description || 'Builder rules updated.';
    }

    if (action.tool === 'builder.add_rule_condition') {
      const side = params.side === 'short' ? 'short' : 'long';
      const key = side === 'long' ? 'longConditions' : 'shortConditions';
      setRules((previous) => normalizeRules({
        ...previous,
        indicatorEntriesEnabled: true,
        [key]: [
          ...(Array.isArray(previous[key]) ? previous[key] : []),
          createRuleCondition(side, params.condition || {}),
        ],
      }));
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('rules');
      setMobileView('setup');
      return action.description || `Added a ${side} rule condition.`;
    }

    if (action.tool === 'builder.add_custom_feature_spec') {
      const spec = params.spec || {};
      const label = spec.label || 'Custom feature';
      const block = [
        `Custom Feature: ${label}`,
        `Status: ${spec.status || 'requirements_needed'}`,
        `Certification: ${spec.certification || 'not_implemented'}`,
        `Request: ${spec.request || action.description || ''}`,
      ].filter(Boolean).join('\n');
      setStrategyDescription((previous) => {
        const current = String(previous || '').trim();
        if (current.includes(block)) {
          return previous;
        }
        return current ? `${current}\n\n${block}` : block;
      });
      clearBuilderRunState();
      setLeftCollapsed(false);
      setRightCollapsed(true);
      setLeftTab('ai');
      setMobileView('setup');
      return `${label} spec added to this strategy. It still needs Builder and MQ5 support before it can be exported as a working feature.`;
    }

    if (action.tool === 'tester.run_backtest') {
      await startRun();
      return 'Backtest started through the normal Builder run path.';
    }

    if (action.tool === 'tester.save_strategy') {
      await handleSaveStrategy();
      return 'Save requested through the normal strategy vault path.';
    }

    if (action.tool === 'tester.export_mq5') {
      await handleExportStrategy();
      return 'MQ5 export requested through the existing exporter gates.';
    }

    if (action.tool === 'web.search') {
      const response = await runVixAiWebSearch({
        query: params.query || action.description,
        count: params.count || 5,
      }, authToken);
      return formatVixAiToolResults([{ tool: 'web.search', status: response.status, result: response.result }]);
    }

    if (action.tool === 'code.generate_module') {
      try {
        const response = await requestVixAiCodeDraft({
          language: params.language || 'mq5',
          purpose: params.purpose || action.description,
          strategy: buildStrategyPayload(),
        }, authToken);
        return formatVixAiCompileResponse(response);
      } catch (error) {
        return error.payload?.compileResult
          ? formatVixAiCompileResponse(error.payload)
          : error.payload?.message || error.message || 'Code module tool is not ready.';
      }
    }

    if (action.tool === 'tester.optimize_variants') {
      const variants = Array.isArray(params.variants) ? params.variants.slice(0, 8) : [];
      const results = [];
      for (const variant of variants) {
        const variantRules = normalizeRules({
          ...rules,
          ...(variant.rulesPatch || {}),
        });
        const result = await runServerBacktest({
          setup,
          indicators,
          rules: variantRules,
          sessions,
          maxChartBars: 600,
        });
        const normalizedSummary = result?.summary ? normalizeSummaryShape(result.summary) : null;
        results.push({
          name: variant.name || 'Variant',
          netProfit: normalizedSummary?.metrics?.netProfit || 0,
          profitFactor: normalizedSummary?.metrics?.profitFactor || 0,
          maxDrawdown: normalizedSummary?.metrics?.maxDrawdown || 0,
          totalTrades: normalizedSummary?.metrics?.totalTrades || 0,
        });
      }
      return formatOptimizationResult(results);
    }

    return `This VixAI action is not available in the Builder yet.`;
  }

  async function runBuilderAiAction(action, options = {}) {
    const { skipApproval = false } = options;
    if (!skipApproval && action.approvalRequired) {
      openDialog({
        title: action.expensive ? 'Approve VixAI Tool Run' : 'Approve VixAI Action',
        message: action.description || 'Run this VixAI action?',
        confirmLabel: action.expensive ? 'Run Tool' : 'Approve',
        tone: action.risk === 'high' ? 'danger' : '',
        onConfirm: () => {
          closeDialog();
          runBuilderAiAction(action, { skipApproval: true });
        },
      });
      return;
    }

    setBuilderAiToolPendingId(action.id);
    try {
      const resultMessage = await executeVixAiToolAction(action);
      setBuilderAiMessages((previous) => [...previous, {
        role: 'assistant',
        content: resultMessage,
        toolResult: true,
      }]);
      setBuilderAiNotice(resultMessage);
    } catch (error) {
      const message = error.message || 'VixAI tool action failed.';
      setBuilderAiMessages((previous) => [...previous, {
        role: 'assistant',
        content: message,
        toolResult: true,
      }]);
      setBuilderAiNotice(message);
    } finally {
      setBuilderAiToolPendingId('');
    }
  }

  const refreshSavedStrategies = useCallback(async () => {
    if (!authToken) {
      setSavedStrategies([]);
      setSavedStrategiesError('');
      return;
    }

    setSavedStrategiesLoading(true);
    try {
      const response = await listSavedTesterStrategies(authToken);
      const list = Array.isArray(response.strategies) ? response.strategies : [];
      setSavedStrategies(list);
      setSavedStrategiesError('');
      if (!selectedStrategyId && activeStrategyId) {
        setSelectedStrategyId(activeStrategyId);
      }
    } catch (error) {
      setSavedStrategiesError(error.message || 'Failed to load saved strategies.');
    } finally {
      setSavedStrategiesLoading(false);
    }
  }, [activeStrategyId, authToken, selectedStrategyId]);

  function buildVixAiChangeSummary(before, after) {
    const changes = [];
    const bSetup = before?.setup || {};
    const aSetup = after?.setup || {};
    const bRules = before?.rules || {};
    const aRules = after?.rules || {};
    if (aSetup.symbol && bSetup.symbol !== aSetup.symbol) changes.push(`symbol → ${aSetup.symbol}`);
    if (aSetup.timeframe && bSetup.timeframe !== aSetup.timeframe) changes.push(`timeframe → ${aSetup.timeframe}`);
    if (aSetup.lotSize && bSetup.lotSize !== aSetup.lotSize) changes.push(`lot size → ${aSetup.lotSize}`);
    if (aSetup.from && bSetup.from !== aSetup.from) changes.push(`from → ${aSetup.from}`);
    if (aSetup.to && bSetup.to !== aSetup.to) changes.push(`to → ${aSetup.to}`);
    if (after?.name && before?.name !== after?.name) changes.push(`name → "${after.name}"`);
    if (aRules.gridEnabled !== undefined && bRules.gridEnabled !== aRules.gridEnabled) {
      changes.push(`grid ${aRules.gridEnabled ? 'enabled' : 'disabled'}`);
    }
    if (aRules.martingaleEnabled !== undefined && bRules.martingaleEnabled !== aRules.martingaleEnabled) {
      changes.push(`martingale ${aRules.martingaleEnabled ? 'enabled' : 'disabled'}`);
    }
    if (aRules.indicatorEntriesEnabled !== undefined && bRules.indicatorEntriesEnabled !== aRules.indicatorEntriesEnabled) {
      changes.push(`entry conditions ${aRules.indicatorEntriesEnabled ? 'enabled' : 'disabled'}`);
    }
    if (aRules.rangeBreakoutEnabled !== undefined && bRules.rangeBreakoutEnabled !== aRules.rangeBreakoutEnabled) {
      changes.push(`range breakout ${aRules.rangeBreakoutEnabled ? 'enabled' : 'disabled'}`);
    }
    if (aRules.pendingOrderEnabled !== undefined && bRules.pendingOrderEnabled !== aRules.pendingOrderEnabled) {
      changes.push(`pending orders ${aRules.pendingOrderEnabled ? 'enabled' : 'disabled'}`);
    }
    if (aRules.stopLoss && bRules.stopLoss !== aRules.stopLoss) changes.push(`SL → ${aRules.stopLoss}pt`);
    if (aRules.takeProfit && bRules.takeProfit !== aRules.takeProfit) changes.push(`TP → ${aRules.takeProfit}pt`);
    return changes;
  }

  // Clickable quick-reply chips under an assistant message: { label, send }. Clicking one
  // simply sends `send` as a chat message, so chips are typing shortcuts — the request
  // still flows through the normal VixAI routing and tools.
  function normalizeVixAiSuggestions(raw) {
    if (!Array.isArray(raw)) return [];
    const legacyMap = {
      run_backtest: { label: 'Test it', send: 'Test it' },
      export_mq5: { label: 'Export MQ5', send: 'Export the MQ5 for this strategy' },
    };
    return raw
      .map((entry) => {
        if (entry && typeof entry === 'object' && entry.label && entry.send) {
          return { label: String(entry.label).slice(0, 60), send: String(entry.send).slice(0, 300) };
        }
        if (typeof entry === 'string' && entry.trim()) {
          if (entry in legacyMap) return legacyMap[entry];
          // Old id-style suggestions (build_strategy, ...) aren't actionable as chat — drop them.
          return /^[a-z0-9_]+$/.test(entry) ? null : { label: entry.slice(0, 60), send: entry.slice(0, 300) };
        }
        return null;
      })
      .filter(Boolean)
      .slice(0, 5);
  }

  // Numbered recommendations inside an assistant reply ("1. Tighten the filter…") become a
  // checkbox list with one Apply button, so picking them never depends on the model
  // remembering its own list. The sent message carries the full item texts.
  function extractRecommendationItems(content) {
    const items = [];
    const seen = new Set();
    for (const line of String(content || '').split(/\r?\n/)) {
      const match = line.match(/^\s*(?:#+\s*)?(?:\*\*)?(\d{1,2})[.)]\s*(?:\*\*)?\s*(.{8,180})/);
      if (!match) continue;
      const key = match[1];
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ key, text: match[2].replace(/\*+/g, '').replace(/\s*[—–-]\s*$/, '').trim() });
      if (items.length >= 8) break;
    }
    return items.length >= 2 ? items : [];
  }

  function toggleAiRecommendation(messageIndex, key) {
    setAiRecSelections((previous) => {
      const id = `${messageIndex}:${key}`;
      return { ...previous, [id]: !previous[id] };
    });
  }

  function applySelectedRecommendations(messageIndex, items) {
    const selected = items.filter((item) => aiRecSelections[`${messageIndex}:${item.key}`]);
    if (!selected.length) return;
    const body = selected.map((item) => `${item.key}) ${item.text}`).join('\n');
    setAiRecSelections({});
    handleSendBuilderAi(`Apply these recommendations from your last reply:\n${body}`);
  }

  async function handleSendBuilderAi(overrideMessage = '') {
    const message = (overrideMessage || builderAiInput).trim();
    if (!message || builderAiPending) {
      return;
    }
    if (!authToken) {
      setBuilderAiNotice('Sign in to use VixAI inside the Builder.');
      navigate('/login');
      return;
    }
    if (testerPlan !== 'premium' && !canExportMq5) {
      setBuilderAiNotice('VixAI is available with VixPremium.');
      return;
    }

    const userMessage = { role: 'user', content: message };
    setBuilderAiMessages((previous) => [...previous, userMessage]);
    setBuilderAiInput('');
    setBuilderAiPending(true);
    setBuilderAiNotice('');
    try {
      const response = await sendVixAiMessage({
        message,
        conversationId: builderAiConversationId,
        context: {
          mode: 'builder_embedded',
          currentBuilder: buildStrategyPayload(),
          latestBacktest: (() => {
            const activeResult = pendingServerResult || serverResult;
            const activeSummary = activeResult?.summary ? normalizeSummaryShape(activeResult.summary) : null;
            if (!activeSummary) {
              return { hasResult: false };
            }
            return {
              hasResult: true,
              meta: activeResult?.meta || {},
              metrics: activeSummary.metrics,
              analysis: {
                grossProfit: activeSummary.analysis.grossProfit,
                grossLoss: activeSummary.analysis.grossLoss,
                expectancy: activeSummary.analysis.expectancy,
                largestWin: activeSummary.analysis.largestWin,
                largestLoss: activeSummary.analysis.largestLoss,
                buyWinRate: activeSummary.analysis.buyWinRate,
                sellWinRate: activeSummary.analysis.sellWinRate,
                buyNetProfit: activeSummary.analysis.buyNetProfit,
                sellNetProfit: activeSummary.analysis.sellNetProfit,
                averageTrade: activeSummary.analysis.averageTrade,
                averageBarsHeld: activeSummary.analysis.averageBarsHeld,
                recoveryFactor: activeSummary.analysis.recoveryFactor,
                bestDay: activeSummary.analysis.bestDay,
                worstDay: activeSummary.analysis.worstDay,
              },
              monthly: activeSummary.monthly,
              recentTrades: activeSummary.trades.slice(-20).map((trade) => ({
                side: trade.side,
                entryTime: trade.entryTime,
                exitTime: trade.exitTime,
                entryPrice: trade.entryPrice,
                exitPrice: trade.exitPrice,
                pnl: trade.pnl,
                exitReason: trade.exitReason,
              })),
            };
          })(),
        },
      }, authToken);
      setBuilderAiConversationId(response.conversationId || builderAiConversationId);
      const vixAiResponse = response.vixai || {};
      const compileResult = response.compileResult || vixAiResponse.compileResult || null;
      const builderPayload = compileResult?.builderPayload || null;
      const agentActions = Array.isArray(vixAiResponse.agent?.actions) ? vixAiResponse.agent.actions : [];
      const agentToolResults = Array.isArray(vixAiResponse.agent?.toolResults) ? vixAiResponse.agent.toolResults : [];
      const hasServerBuilderActions = agentActions.some((action) => (
        ['builder.update_setup', 'builder.add_indicator', 'builder.update_rules'].includes(action.tool)
        && action.status === 'ready'
      ));
      const localEdit = builderPayload
        ? { changed: false, changes: [], warnings: [] }
        : hasServerBuilderActions
          ? { changed: false, changes: [], warnings: [] }
        : applyEmbeddedBuilderAiEdits(message);
      const serverContent = response.assistant?.content
        || response.message
        || vixAiResponse.assistantMessage
        || response.dsl?.meta?.description
        || vixAiResponse.dsl?.meta?.description
        || '';
      const responseParts = [];
      if (localEdit.changes.length) {
        responseParts.push(`Applied in Builder:\n${localEdit.changes.map((change) => `- ${change}`).join('\n')}`);
      }
      if (serverContent) {
        responseParts.push(serverContent);
      }
      const formattedToolResults = formatVixAiToolResults(agentToolResults);
      if (formattedToolResults) {
        responseParts.push(formattedToolResults);
      }
      if (agentActions.length) {
        responseParts.push(`Available actions:\n${agentActions.map((action) => `- ${action.label}: ${action.status}${action.blockedReason ? ` (${action.blockedReason})` : ''}`).join('\n')}`);
      }
      if (Array.isArray(vixAiResponse.clarifyingQuestions) && vixAiResponse.clarifyingQuestions.length) {
        responseParts.push('Select the options that best match how this feature should work.');
      }
      if (vixAiResponse.providerError) {
        responseParts.push(`AI provider issue:\n- ${vixAiResponse.providerError}`);
      }
      if (localEdit.warnings.length) {
        responseParts.push(`Check before export:\n${localEdit.warnings.map((warning) => `- ${warning}`).join('\n')}`);
      }
      const assistantContent = responseParts.join('\n\n') || 'I reviewed the setup, but I could not map that request to a supported Builder control yet.';
      setBuilderAiMessages((previous) => [...previous, {
        role: 'assistant',
        content: assistantContent,
        actions: agentActions.length ? agentActions : (response.assistant?.actions || []),
        suggestions: normalizeVixAiSuggestions(vixAiResponse.suggestedActions),
        clarifyingQuestions: vixAiResponse.clarifyingQuestions || [],
      }]);
      if (builderPayload) {
        const previousState = buildStrategyPayload();
        applyBuilderPayload(builderPayload);
        const changes = buildVixAiChangeSummary(previousState, builderPayload);
        setBuilderAiNotice(changes.length
          ? `Applied: ${changes.slice(0, 4).join(', ')}${changes.length > 4 ? ` (+${changes.length - 4} more)` : ''}`
          : 'VixAI updated the Builder setup.');
        const intendedDsl = vixAiResponse.dsl || null;
        sendVixAiVerify({
          message,
          intendedDsl,
          appliedPayload: builderPayload,
          context: { mode: 'builder_embedded', currentBuilder: previousState },
        }, authToken).then((verifyResult) => {
          if (verifyResult?.correction?.builderPayload) {
            applyBuilderPayload(verifyResult.correction.builderPayload);
            const fixed = verifyResult.mismatches || [];
            const fixedMsg = fixed.length
              ? `Auto-corrected ${fixed.length} item(s):\n${fixed.map((m) => `- ${m}`).join('\n')}`
              : 'VixAI auto-corrected the setup.';
            setBuilderAiNotice('VixAI applied and self-corrected the Builder setup.');
            setBuilderAiMessages((previous) => [...previous, { role: 'assistant', content: fixedMsg }]);
          }
        }).catch(() => {});
      } else if (hasServerBuilderActions) {
        const autoActions = agentActions.filter((action) => action.status === 'ready' && !action.approvalRequired);
        for (const action of autoActions) {
          await runBuilderAiAction(action, { skipApproval: true });
        }
      } else if (localEdit.changed) {
        setBuilderAiNotice('VixAI updated the supported Builder controls.');
      } else if (compileResult?.plan?.length) {
        setBuilderAiNotice('This idea needs features that are not fully mapped to Builder controls yet. Use the guidance before exporting.');
      }
    } catch (error) {
      setBuilderAiMessages((previous) => [...previous, {
        role: 'assistant',
        content: error.message || 'VixAI could not process that request.',
      }]);
      setBuilderAiNotice(error.message || 'VixAI could not process that request.');
    } finally {
      setBuilderAiPending(false);
    }
  }

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    const syncToken = () => {
      setAuthToken(localStorage.getItem('userToken') || '');
      try {
        setUserProfile(JSON.parse(localStorage.getItem('userProfile') || 'null'));
      } catch (error) {
        setUserProfile(null);
      }
    };
    window.addEventListener('auth-changed', syncToken);
    window.addEventListener('storage', syncToken);
    return () => {
      window.removeEventListener('auth-changed', syncToken);
      window.removeEventListener('storage', syncToken);
    };
  }, []);

  useEffect(() => {
    if (!authToken) {
      setUserProfile(null);
      return;
    }

    let cancelled = false;
    fetchTesterUserProfile(authToken)
      .then((response) => {
        if (cancelled || !response?.user) {
          return;
        }
        setUserProfile(response.user);
        localStorage.setItem('userProfile', JSON.stringify(response.user));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [authToken]);

  useEffect(() => {
    refreshSavedStrategies();
  }, [refreshSavedStrategies]);

  useEffect(() => {
    if (!requestedStrategyId) {
      return;
    }
    if (!authToken) {
      setStrategyNotice('Sign in to load saved strategies.');
      return;
    }
    if (requestedStrategyId === activeStrategyId) {
      return;
    }

    let cancelled = false;
    fetchSavedTesterStrategy(requestedStrategyId, authToken)
      .then((response) => {
        if (cancelled || !response.strategy) {
          return;
        }
        applySavedStrategy(response.strategy, { updateQuery: false });
        // Must come after applySavedStrategy — that call replaces setup wholesale, including
        // the saved from/to, so overriding beforehand would be discarded.
        if (requestedFrom && requestedTo) {
          setSetup((previous) => ({ ...previous, from: requestedFrom, to: requestedTo }));
        }
        setStrategyNotice(`Loaded "${response.strategy.name}".`);
      })
      .catch((error) => {
        if (!cancelled) {
          setStrategyNotice(error.message || 'Failed to load the requested strategy.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeStrategyId, applySavedStrategy, authToken, requestedStrategyId, requestedFrom, requestedTo]);

  useEffect(() => {
    if (requestedStrategyId || activeStrategyId) {
      return;
    }
    // An explicit ?from/?to must win over the default rolling year.
    if (requestedFrom && requestedTo) {
      setSetup((previous) => (
        previous.from === requestedFrom && previous.to === requestedTo
          ? previous
          : { ...previous, from: requestedFrom, to: requestedTo }
      ));
      return;
    }
    const rollingRange = getRollingOneYearDateRange();
    setSetup((previous) => {
      if (previous.from === rollingRange.from && previous.to === rollingRange.to) {
        return previous;
      }
      return {
        ...previous,
        ...rollingRange,
      };
    });
  }, [activeStrategyId, requestedStrategyId, requestedFrom, requestedTo]);

  useEffect(() => {
    setSetup((previous) => {
      const nextSymbol = ensureSymbol(previous);
      return nextSymbol === previous.symbol ? previous : { ...previous, symbol: nextSymbol };
    });
  }, [setup.assetClass]);

  useEffect(() => {
    const validOperands = getOperandOptions(indicators).map((option) => option.id);
    setRules((previous) => normalizeRules(previous, validOperands));
  }, [indicators]);
  useEffect(() => {
    if (runningRef.current || serverRunPendingRef.current) {
      return;
    }
    setServerResult(null);
    setPendingServerResult(null);
    setHasRunBacktest(false);
    setBacktestJob(null);
    olderHistoryAnchorRef.current = null;
    // Only reset the chart view (scroll/zoom/follow) when the underlying CHART DATA changes —
    // symbol/timeframe/date range/source. Editing rules, indicators or sessions just re-runs the
    // backtest on the same candles, so the user's scrolled-back position must be preserved.
    const dataSig = [setup.symbol, setup.timeframe, setup.from, setup.to, setup.dataSource, setup.brokerProfileId, setup.modelingMode].join('|');
    if (chartDataSigRef.current !== dataSig) {
      chartDataSigRef.current = dataSig;
      setChartViewRevision((previous) => previous + 1);
    }
  }, [setup, indicators, rules, sessions]);

  useEffect(() => {
    // Also ticks while the live feed is subscribed, so the "last tick Ns ago" readout counts
    // up instead of freezing at whatever it said when the last tick landed — a frozen counter
    // is exactly how a dead feed would look, which defeats the point of showing it.
    if (marketState.status !== 'loading' && !marketState.liveTicksEnabled) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      setMarketStatusClock((previous) => previous + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [marketState.status, marketState.liveTicksEnabled]);

  useEffect(() => {
    derivCatalogRef.current = null;
    setDerivCatalog(null);
  }, [setup.dataSource]);

  // Every Tick was removed — it can't be modeled accurately without a real tick feed.
  // Silently downgrade any legacy strategy that still carries it to 1-Minute OHLC.
  useEffect(() => {
    if (setup.modelingMode !== 'every_tick') {
      return;
    }
    setSetup((previous) => (
      previous.modelingMode === 'every_tick'
        ? { ...previous, modelingMode: 'minute_ohlc' }
        : previous
    ));
  }, [setup.modelingMode]);

  const providerAvailableSymbols = useMemo(() => {
    if (!Array.isArray(derivCatalog?.options)) {
      return [];
    }
    return derivCatalog.options
      .filter((option) => option.assetClass === setup.assetClass)
      .map((option) => option.uiSymbol);
  }, [derivCatalog, setup.assetClass]);

  const providerSymbolLabelByValue = useMemo(() => {
    const labels = new Map();
    if (Array.isArray(derivCatalog?.options)) {
      derivCatalog.options.forEach((option) => {
        if (option?.uiSymbol) {
          labels.set(option.uiSymbol, option.label || formatSymbolDisplayName(option.uiSymbol));
        }
      });
    }
    return labels;
  }, [derivCatalog]);

  const symbolOptions = providerAvailableSymbols.length
    ? providerAvailableSymbols
    : SYMBOL_MAP[setup.assetClass];
  const selectedSymbolLabel = providerSymbolLabelByValue.get(setup.symbol) || formatSymbolDisplayName(setup.symbol);

  useEffect(() => {
    if (!providerAvailableSymbols.length || providerAvailableSymbols.includes(setup.symbol)) {
      return;
    }
    setSetup((previous) => ({ ...previous, symbol: providerAvailableSymbols[0] }));
  }, [providerAvailableSymbols, setup.dataSource, setup.symbol]);

  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  useEffect(() => {
    serverRunPendingRef.current = serverRunPending;
  }, [serverRunPending]);

  useEffect(() => {
    setupRef.current = setup;
  }, [setup]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;

    async function loadMarketData() {
        if (runningRef.current || serverRunPendingRef.current) {
          return;
        }

      const previewWindow = getPreviewWindow({
        from: setup.from,
        to: setup.to,
        timeframe: setup.timeframe,
      });
      setMarketState((previous) => ({
        ...previous,
        status: 'loading',
        error: '',
        providerSymbol: '',
        loadingStartedAt: new Date().toISOString(),
        loadingFrom: formatDateForInput(previewWindow.previewStartEpoch * 1000),
        loadingTo: formatDateForInput(previewWindow.previewEndEpoch * 1000),
        loadingTimeframe: setup.timeframe,
        loadingSymbol: setup.symbol,
        liveTicksEnabled: false,
        historyQuality: null,
      }));

      try {
        let catalog = derivCatalogRef.current;
        if (!catalog) {
          catalog = await fetchServerSymbolCatalog(setup.dataSource);
          if (cancelled) {
            return;
          }
          derivCatalogRef.current = catalog;
          setDerivCatalog(catalog);
        }

        const providerConfig = catalog.byUiSymbol?.[setup.symbol];
        if (!providerConfig) {
          throw new Error(`${setup.symbol} is not currently available from ${setup.dataSource}.`);
        }

        const nextMarketData = await fetchServerCandles({
          provider: setup.dataSource,
          ...providerConfig,
          timeframe: setup.timeframe,
          from: formatDateForInput(previewWindow.previewStartEpoch * 1000),
          to: formatDateForInput(previewWindow.previewEndEpoch * 1000),
          startEpoch: previewWindow.previewStartEpoch,
          endEpoch: previewWindow.previewEndEpoch,
          maxBars: previewWindow.previewBarLimit,
          brokerProfileId: normalizeBrokerProfileIdForSource(setup.dataSource, setup.brokerProfileId),
          modelingMode: setup.modelingMode,
          cacheOnly: false,
        });

        if (cancelled || runningRef.current || serverRunPendingRef.current) {
          return;
        }

        setMarketData(nextMarketData);
        setMarketState({
          status: 'ready',
          error: '',
          providerSymbol: providerConfig.providerSymbol,
          lastLoadedAt: new Date().toISOString(),
          candleCount: nextMarketData.coverage?.candleCount || nextMarketData.candles.length,
          loadingStartedAt: null,
          loadingFrom: '',
          loadingTo: '',
          loadingTimeframe: '',
          loadingSymbol: '',
          liveTicksEnabled: setup.dataSource === 'deriv' && dateRangeIncludesNow(setup.from, setup.to),
          historyQuality: nextMarketData.historyQuality || null,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setMarketData(null);
        setMarketState({
          status: 'error',
          error: error.message || `Failed to load ${setup.dataSource} market data.`,
          providerSymbol: '',
          lastLoadedAt: null,
          candleCount: 0,
          loadingStartedAt: null,
          loadingFrom: '',
          loadingTo: '',
          loadingTimeframe: '',
          loadingSymbol: '',
          liveTicksEnabled: false,
          historyQuality: null,
        });
      }
    }

    loadMarketData();
    return () => {
      cancelled = true;
    };
  }, [setup.brokerProfileId, setup.dataSource, setup.from, setup.modelingMode, setup.symbol, setup.timeframe, setup.to, marketReloadNonce]);

  useEffect(() => {
    if (marketState.status !== 'ready') {
      return;
    }
    const instrumentSpec = marketData?.profile?.instrumentSpec;
    const seededLongSwap = instrumentSpec?.swapLongPerLot;
    const seededShortSwap = instrumentSpec?.swapShortPerLot;
    if (!Number.isFinite(Number(seededLongSwap)) && !Number.isFinite(Number(seededShortSwap))) {
      return;
    }
    setSetup((previous) => {
      const longIsLegacyDefault = previous.swapLongPerLot === '' || previous.swapLongPerLot === '0' || previous.swapLongPerLot === 0 || previous.swapLongPerLot === null || previous.swapLongPerLot === undefined;
      const shortIsLegacyDefault = previous.swapShortPerLot === '' || previous.swapShortPerLot === '0' || previous.swapShortPerLot === 0 || previous.swapShortPerLot === null || previous.swapShortPerLot === undefined;
      if (!longIsLegacyDefault && !shortIsLegacyDefault) {
        return previous;
      }
      const next = { ...previous };
      if (longIsLegacyDefault && Number.isFinite(Number(seededLongSwap))) {
        next.swapLongPerLot = String(seededLongSwap);
      }
      if (shortIsLegacyDefault && Number.isFinite(Number(seededShortSwap))) {
        next.swapShortPerLot = String(seededShortSwap);
      }
      return next.swapLongPerLot === previous.swapLongPerLot && next.swapShortPerLot === previous.swapShortPerLot
        ? previous
        : next;
    });
  }, [marketData, marketState.status, setup.dataSource]);

  useEffect(() => {
    if (running || serverRunPending) {
      return undefined;
    }
    const cachedServerResult = serverResultCacheRef.current.get(currentBacktestSignature);
    if (cachedServerResult?.engine) {
      setServerResult(cachedServerResult);
      setPendingServerResult(null);
      setHasRunBacktest(true);
      setEngine(cachedServerResult.engine);
      setCurrentBar(cachedServerResult.engine.candles.length - 1);
      return undefined;
    }
    if (serverResult?.engine) {
      return undefined;
    }
    if (marketState.status !== 'ready') {
      return undefined;
    }

    const nextEngine = simulateBacktest(
      setup,
      indicators,
      rules,
      sessions,
      marketData,
    );
    setEngine(nextEngine);
    setCurrentBar(nextEngine.candles.length - 1);
    return undefined;
  }, [setup, indicators, rules, sessions, running, serverRunPending, serverResult, marketData, marketState.status, currentBacktestSignature]);

  useEffect(() => {
    derivTickStreamRef.current?.();
    derivTickStreamRef.current = null;
    // Counters belong to one subscription. Carrying them across a symbol or timeframe change
    // would report the previous stream's health as if it were this one's.
    setLiveTick({ at: null, count: 0, dropped: 0 });

    const liveTicksEnabled = setup.dataSource === 'deriv' && dateRangeIncludesNow(setup.from, setup.to);
    if (!liveTicksEnabled || marketState.status !== 'ready' || !marketState.providerSymbol) {
      return undefined;
    }

    derivTickStreamRef.current = subscribeDerivTicks({
      providerSymbol: marketState.providerSymbol,
      onTick: (tick) => {
        // Record whether the merge actually took the tick. Identity means it was refused —
        // almost always the staleness guard — and that is the difference between "connected"
        // and "connected but showing you nothing", which the user has no other way to tell.
        setMarketData((previous) => {
          const next = mergeLiveTickIntoMarketData(previous, tick, setup.timeframe);
          const merged = next !== previous;
          setLiveTick((info) => ({
            at: Date.now(),
            count: info.count + 1,
            dropped: merged ? info.dropped : info.dropped + 1,
          }));
          return next;
        });
        setMarketState((previous) => ({
          ...previous,
          lastLoadedAt: new Date(tick.time).toISOString(),
        }));
      },
      onError: () => {},
    });

    return () => {
      derivTickStreamRef.current?.();
      derivTickStreamRef.current = null;
    };
  }, [marketState.providerSymbol, marketState.status, setup.dataSource, setup.from, setup.timeframe, setup.to]);

  useEffect(() => {
    if (!backfillJob?.id || !['queued', 'running', 'cancel_requested'].includes(backfillJob.status)) {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetchServerBackfillJob(backfillJob.id);
        if (!cancelled) {
          setBackfillJob(response.job || null);
        }
      } catch (error) {
        if (!cancelled) {
          setBackfillJob((previous) => (previous ? { ...previous, status: 'failed', error: error.message || 'Failed to refresh the backfill job.' } : previous));
        }
      }
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [backfillJob]);

  useEffect(() => {
    if (!autoRunAfterBackfill || !backfillReadyForSetup(backfillJob, setupRef.current)) {
      return undefined;
    }
    setAutoRunAfterBackfill(false);
    const timer = window.setTimeout(() => {
      startRun();
    }, 250);
    return () => window.clearTimeout(timer);
    // startRun intentionally runs after the completed job state settles; including it would re-arm this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunAfterBackfill, backfillJob?.status, backfillJob?.id]);

  useEffect(() => {
    if (!autoRunAfterBackfill || backfillJob?.status !== 'failed') {
      return;
    }
    setAutoRunAfterBackfill(false);
    const errorMessage = backfillJob.error || 'The source provider could not prepare enough execution history for this range.';
    if (isDerivRetentionError(errorMessage)) {
      const recentRange = getDerivRecentRange(setup.to, executionHistoryRequirement.executionTimeframe);
      openDialog({
        title: 'History Range Is Too Long',
        message: recentRange
          ? `The source provider cannot provide enough ${executionHistoryRequirement.executionTimeframe} history for ${setup.from} to ${setup.to}. Use ${recentRange.from} to ${recentRange.to} instead?`
          : errorMessage,
        confirmLabel: recentRange ? 'Use Recent Range' : 'Close',
        cancelLabel: 'Cancel',
        tone: 'warning',
        onConfirm: () => {
          if (recentRange) {
            setSetup((previous) => ({
              ...previous,
              from: recentRange.from,
              to: recentRange.to,
              dataSource: 'deriv',
              brokerProfileId: 'deriv-svg-server-03',
            }));
            setBackfillJob(null);
            setStrategyNotice('Date range adjusted. Run again when the history preview reloads.');
          }
          closeDialog();
        },
      });
    }
    // This handles the terminal failed state for the active auto-backfill attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunAfterBackfill, backfillJob?.status, backfillJob?.id]);

  const activeBacktestJobId = backtestJob?.id;
  const activeBacktestJobStatus = backtestJob?.status;
  useEffect(() => {
    if (!activeBacktestJobId || !['queued', 'running', 'cancel_requested'].includes(activeBacktestJobStatus)) {
      return undefined;
    }

    let cancelled = false;
    const pollBacktestJob = async () => {
      try {
        const response = await fetchServerBacktestJob(activeBacktestJobId);
        if (cancelled) {
          return;
        }
        const nextJob = response.job || null;
        setBacktestJob(nextJob);
        if (nextJob?.status === 'completed' && nextJob.result) {
          const completedEngine = nextJob.result.engine;
          const replayStartBar = Math.max(0, completedEngine.startIndex || 0);
          serverResultCacheRef.current.set(activeRunSignatureRef.current, nextJob.result);
          setEngine(completedEngine);
          // Replay the completed server ledger as a chart animation from the first
          // executed bar. The replay-loop effect advances currentBar at the chosen
          // speed and commits the authoritative result (pendingServerResult) when it
          // reaches the end. Stop/skip jumps straight to the final bar.
          setCurrentBar(replayStartBar);
          setPendingServerResult(nextJob.result);
          setServerResult(null);
          setHasRunBacktest(true);
          setRunning(true);
          setPaused(false);
          setServerRunPending(false);
          setRightCollapsed(false);
          setRightTab('summary');
          setMobileView('chart');
        } else if (nextJob?.status === 'failed') {
          setPendingServerResult(null);
          setHasRunBacktest(false);
          setServerRunPending(false);
          setRunning(false);
          setPaused(false);
          setLeftTab('market');
          setLeftCollapsed(false);
          setMobileView('setup');
          setMarketState((previous) => ({
            ...previous,
            error: nextJob.error || 'Failed to run the server backtest.',
          }));
        } else if (nextJob?.status === 'cancelled') {
          setPendingServerResult(null);
          setHasRunBacktest(false);
          setServerRunPending(false);
          setRunning(false);
          setPaused(false);
        }
      } catch (error) {
        if (!cancelled) {
          setPendingServerResult(null);
          setHasRunBacktest(false);
          setServerRunPending(false);
          setBacktestJob((previous) => (previous ? { ...previous, status: 'failed', error: error.message || 'Failed to refresh the backtest job.' } : previous));
          setLeftTab('market');
          setLeftCollapsed(false);
          setMobileView('setup');
          setMarketState((previous) => ({
            ...previous,
            error: error.message || 'Failed to refresh the backtest job.',
          }));
        }
      }
    };

    pollBacktestJob();
    const timer = window.setInterval(pollBacktestJob, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeBacktestJobId, activeBacktestJobStatus]);


  useEffect(() => {
    if (!resizing) {
      return undefined;
    }

    function handleMove(event) {
      const rect = workspaceRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      if (resizing === 'left') {
        const maxLeft = Math.min(MAX_LEFT, Math.max(MIN_LEFT, rect.width - 160));
        setLeftCollapsed(false);
        setLeftWidth(clamp(event.clientX - rect.left, MIN_LEFT, maxLeft));
      }

      if (resizing === 'right') {
        const maxRight = Math.min(MAX_RIGHT, Math.max(MIN_RIGHT, rect.width - 160));
        setRightCollapsed(false);
        setRightWidth(clamp(rect.right - event.clientX, MIN_RIGHT, maxRight));
      }
    }

    function handleUp() {
      setResizing(null);
    }

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizing]);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    derivTickStreamRef.current?.();
  }, []);

  const speedProfile = PLAYBACK_SPEED_OPTIONS.find((option) => option.id === playbackSpeed) || PLAYBACK_SPEED_OPTIONS[1];

  useEffect(() => {
    clearInterval(timerRef.current);
    if (!running || paused) {
      return undefined;
    }

    replayClockRef.current = Date.now();
    timerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.max(speedProfile.intervalMs, now - (replayClockRef.current || now));
        const intervalCount = Math.max(1, Math.floor(elapsed / Math.max(1, speedProfile.intervalMs)));
        const barStep = Math.max(1, intervalCount * speedProfile.step);
        replayClockRef.current = now;
        setCurrentBar((previous) => {
          const next = Math.min(engine.candles.length - 1, previous + barStep);
          if (next >= engine.candles.length - 1) {
            clearInterval(timerRef.current);
            if (pendingServerResult) {
              serverResultCacheRef.current.set(activeRunSignatureRef.current, pendingServerResult);
              setServerResult(pendingServerResult);
              setPendingServerResult(null);
              setHasRunBacktest(true);
            }
          setRunning(false);
          setPaused(false);
          setRightCollapsed(false);
          setRightTab('summary');
          setMobileView('results');
          notifyBacktestComplete({
            setup,
            summary: pendingServerResult?.summary || summarizeBacktest(engine, engine.candles.length - 1),
          });
        }
        return next;
      });
    }, speedProfile.intervalMs);

    return () => clearInterval(timerRef.current);
  }, [running, paused, speedProfile, engine, serverRunPending, pendingServerResult, setup]);

  const replaySummary = useMemo(() => normalizeSummaryShape(summarizeBacktest(engine, currentBar)), [engine, currentBar]);
  const summary = useMemo(() => {
    if (serverResult?.summary && !running && !pendingServerResult) {
      return normalizeSummaryShape(serverResult.summary);
    }
    return replaySummary;
  }, [pendingServerResult, replaySummary, running, serverResult]);
  const journalErrorCount = useMemo(
    () => (Array.isArray(engine?.journal) ? engine.journal.filter((entry) => entry.level === 'error').length : 0),
    [engine],
  );
  const progress = serverRunPending
    ? Number(backtestJob?.progress || 0)
    : autoRunAfterBackfill && ['queued', 'running', 'cancel_requested'].includes(backfillJob?.status)
      ? Number(backfillJob?.progress || 0)
      : ((currentBar - engine.startIndex) / Math.max(1, engine.candles.length - 1 - engine.startIndex)) * 100;
  const showResults = hasRunBacktest || running || !!pendingServerResult;
  const operandOptions = getOperandOptions(indicators);
  const certifiedExportSupport = useMemo(
    () => getCertifiedExportSupport(setup, indicators, rules),
    [indicators, rules, setup],
  );
  const operandOptionGroups = operandOptions.reduce((groups, option) => {
    const key = option.group || 'Signals';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(option);
    return groups;
  }, {});
  const conditionTimeframeOptions = useMemo(() => {
    const setupIndex = Math.max(0, CONDITION_TIMEFRAME_OPTIONS.indexOf(setup.timeframe));
    // 'Current' = PERIOD_CURRENT (the chart timeframe). Only frames at or above the
    // chart timeframe are offered because higher frames are built by aggregating the
    // chart's candles; lower frames cannot be synthesised from them.
    return [
      { id: 'chart', label: `Current (${setup.timeframe})` },
      ...CONDITION_TIMEFRAME_OPTIONS
        .filter((option, index) => index > setupIndex)
        .map((option) => ({ id: option, label: option })),
    ];
  }, [setup.timeframe]);
  const executionHistoryRequirement = useMemo(
    () => getExecutionHistoryRequirement({ modelingMode: setup.modelingMode, timeframe: setup.timeframe }),
    [setup.modelingMode, setup.timeframe],
  );
  const leftPanelWidth = leftCollapsed ? COLLAPSED_SIZE : leftWidth;
  const rightPanelWidth = rightCollapsed ? COLLAPSED_SIZE : rightWidth;
  const canRun = marketState.status === 'ready' && marketData;
  const sourceDescription = describeMarketDataSource(setup.dataSource, marketState);
  const sourceInlineError = marketState.error && marketState.status !== 'error' ? marketState.error : '';
  const hasActiveBackfillJob = ['queued', 'running', 'cancel_requested'].includes(backfillJob?.status);
  const hasActiveBacktestJob = ['queued', 'running', 'cancel_requested'].includes(backtestJob?.status);

  // Cancel a running backfill when the setup key changes (symbol, execution timeframe, date range)
  useEffect(() => {
    if (!hasActiveBackfillJob || !backfillJob) return;
    const jobMatchesCurrent = (
      (backfillJob.symbol || '').toUpperCase() === (setup.symbol || '').toUpperCase()
      && (backfillJob.timeframe || '').toUpperCase() === (executionHistoryRequirement.executionTimeframe || '').toUpperCase()
      && backfillJob.from === setup.from
      && backfillJob.to === setup.to
    );
    if (!jobMatchesCurrent) {
      requestBackfillCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.symbol, executionHistoryRequirement.executionTimeframe, setup.from, setup.to]);

  // Auto-start backfill in the background whenever execution history is needed
  useEffect(() => {
    if (!executionHistoryRequirement.requiresSeparateExecutionHistory) return;
    if (!setup.symbol || !setup.from || !setup.to) return;
    if (hasActiveBackfillJob || backfillStartPending) return;
    if (backfillReadyForSetup(backfillJob, setup)) return;
    startBackfill({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    executionHistoryRequirement.requiresSeparateExecutionHistory,
    executionHistoryRequirement.executionTimeframe,
    setup.symbol,
    setup.from,
    setup.to,
    backfillJob?.id,
    backfillJob?.status,
    backfillStartPending,
  ]);
  const backtestJobExecutionTimeframe = backtestJob
    ? getExecutionTimeframeForSetup(backtestJob.payload?.setup || { timeframe: backtestJob.timeframe, modelingMode: setup.modelingMode })
    : '';
  const backtestJobTimeframeLabel = backtestJobExecutionTimeframe && backtestJobExecutionTimeframe !== backtestJob?.timeframe
    ? `${backtestJob.timeframe} signals / ${backtestJobExecutionTimeframe} execution`
    : backtestJob?.timeframe;
  const runButtonBusy = serverRunPending || backfillStartPending || (autoRunAfterBackfill && hasActiveBackfillJob);
  const runButtonActive = running || runButtonBusy;
  const runButtonLabel = backfillStartPending || (autoRunAfterBackfill && hasActiveBackfillJob)
    ? 'Cancel backfill'
    : serverRunPending
      ? 'Cancel server backtest'
      : running
        ? 'Stop backtest'
        : 'Start backtest';
  const runButtonTitle = runButtonBusy
    ? 'Preparing'
    : running
      ? 'Stop'
      : marketState.status === 'loading'
        ? 'Loading'
        : 'Run';
  const runFeedback = (() => {
    if (backfillStartPending && !backfillJob) {
      return {
        visible: true,
        tone: 'loading',
        label: 'Starting Backfill',
        detail: `${getModelLabel(setup.modelingMode)} needs ${executionHistoryRequirement.executionTimeframe} execution history.`,
        meta: 'Preparing history job',
        spinning: true,
      };
    }
    if (autoRunAfterBackfill && backfillJob) {
      return {
        visible: true,
        tone: backfillJob.status === 'failed' ? 'error' : backfillJob.status === 'completed' ? 'ready' : 'loading',
        label: getJobStageLabel(backfillJob),
        detail: `${formatSymbolDisplayName(backfillJob.symbol)} ${backfillJob.timeframe} ${Math.round(backfillJob.progress || 0)}%`,
        meta: backfillJob.currentStep?.from && backfillJob.currentStep?.to
          ? `${backfillJob.currentStep.from} to ${backfillJob.currentStep.to}`
          : 'Backtest will start automatically after history is ready',
        spinning: hasActiveBackfillJob,
      };
    }
    if (serverRunPending && !backtestJob) {
      return {
        visible: true,
        tone: 'loading',
        label: 'Starting Backtest',
        detail: 'Creating server job and preparing history.',
        meta: getModelLabel(setup.modelingMode),
        spinning: true,
      };
    }
    if (serverRunPending && backtestJob) {
      return {
        visible: true,
        tone: backtestJob.status === 'failed' ? 'error' : backtestJob.status === 'completed' ? 'ready' : 'loading',
        label: getJobStageLabel(backtestJob),
        detail: `${formatSymbolDisplayName(backtestJob.symbol)} ${backtestJobTimeframeLabel} ${Math.round(backtestJob.progress || 0)}%`,
        meta: backtestJob.currentStep?.stage
          ? `${backtestJob.currentStep.stage}${backtestJob.currentStep.totalCandles ? ` ${backtestJob.currentStep.loadedCandles || 0}/${backtestJob.currentStep.totalCandles}` : ''}`
          : 'Server backtest running',
        spinning: hasActiveBacktestJob,
      };
    }
    if (running) {
      return {
        visible: true,
        tone: paused ? 'ready' : 'loading',
        label: paused ? 'Replay Paused' : 'Replaying Result',
        detail: `${Math.round(progress || 0)}%`,
        meta: paused ? 'Press resume to continue replay' : 'Rendering completed server ledger',
        spinning: !paused,
      };
    }
    if (strategyNotice) {
      return {
        visible: true,
        tone: 'ready',
        label: 'Status',
        detail: strategyNotice,
        meta: '',
        spinning: false,
      };
    }
    if (marketState.error) {
      return {
        visible: true,
        tone: 'error',
        label: 'History unavailable',
        detail: 'History could not load. Check your connection and press Retry in the Market panel.',
        meta: '',
        spinning: false,
      };
    }
    return { visible: false };
  })();
  const activeHistoryQuality = pendingServerResult?.meta?.historyQuality
    || serverResult?.meta?.historyQuality
    || backtestJob?.result?.meta?.historyQuality
    || marketData?.historyQuality
    || marketState.historyQuality
    || null;
  const historyQualityBadge = getHistoryQualityBadge(activeHistoryQuality);
  const dataConfidenceBadge = getDataConfidenceBadge(activeHistoryQuality);
  const downloadReport = useCallback(() => {
    const activeReportResult = pendingServerResult || serverResult;
    const reportSetup = getEffectiveReportSetup(setup, activeReportResult);
    const reportHtml = buildTesterReportHtml({
      strategyName,
      strategyDescription,
      setup: reportSetup,
      rules,
      sessions,
      summary,
      engine,
    });
    const safeName = (strategyName || DEFAULT_STRATEGY_NAME).replace(/[^\w-]+/g, '_');
    downloadFile(`${safeName}_backtest_report.html`, reportHtml, 'text/html;charset=utf-8');
  }, [engine, pendingServerResult, rules, serverResult, sessions, setup, strategyDescription, strategyName, summary]);
  const hasOlderHistory = useMemo(() => {
    if (!marketData?.candles?.length) {
      return false;
    }
    const requestedStart = new Date(`${setup.from}T00:00:00Z`).getTime();
    const earliestLoaded = marketData.candles[0]?.time || 0;
    return earliestLoaded > requestedStart;
  }, [marketData, setup.from]);


  async function loadOlderHistory() {
    if (
      olderHistoryPending
      || !marketData?.candles?.length
      || !hasOlderHistory
      || !derivCatalogRef.current?.byUiSymbol?.[setup.symbol]
    ) {
      return;
    }

    const earliestLoadedTime = marketData.candles[0].time;
    if (olderHistoryAnchorRef.current === earliestLoadedTime) {
      return;
    }

    olderHistoryAnchorRef.current = earliestLoadedTime;
    setOlderHistoryPending(true);

    try {
      const providerConfig = derivCatalogRef.current.byUiSymbol[setup.symbol];
      const timeframeSeconds = TIMEFRAME_SECONDS[setup.timeframe] || TIMEFRAME_SECONDS.H1;
      const previewBarLimit = getPreviewBarLimit(setup.timeframe);
      const requestedStartEpoch = Math.floor(new Date(`${setup.from}T00:00:00Z`).getTime() / 1000);
      const requestedEndEpoch = Math.floor(earliestLoadedTime / 1000) - timeframeSeconds;

      if (requestedEndEpoch <= requestedStartEpoch) {
        olderHistoryAnchorRef.current = requestedStartEpoch * 1000;
        return;
      }

      const requestedChunkStartEpoch = Math.max(
        requestedStartEpoch,
        requestedEndEpoch - (previewBarLimit * timeframeSeconds) + timeframeSeconds,
      );
      const olderChunk = await fetchServerCandles({
        provider: setup.dataSource,
        ...providerConfig,
        timeframe: setup.timeframe,
        from: formatDateForInput(requestedChunkStartEpoch * 1000),
        to: formatDateForInput(requestedEndEpoch * 1000),
        startEpoch: requestedChunkStartEpoch,
        endEpoch: requestedEndEpoch,
        maxBars: previewBarLimit,
        brokerProfileId: normalizeBrokerProfileIdForSource(setup.dataSource, setup.brokerProfileId),
        modelingMode: setup.modelingMode,
        cacheOnly: false,
      });

      let mergedCount = 0;
      setMarketData((previous) => {
        const mergedData = mergeOlderMarketData(previous, olderChunk);
        mergedCount = mergedData.candles.length;
        return mergedData;
      });
      setMarketState((previous) => ({
        ...previous,
        candleCount: mergedCount || previous.candleCount,
        historyQuality: olderChunk.historyQuality || previous.historyQuality || null,
      }));
    } catch (error) {
      olderHistoryAnchorRef.current = null;
    } finally {
      setOlderHistoryPending(false);
    }
  }
  function updateSetup(field, value) {
    setSetup((previous) => {
      const next = { ...previous, [field]: value, dataSource: 'deriv', brokerProfileId: 'deriv-svg-server-03' };
      const nextMode = field === 'modelingMode' ? value : previous.modelingMode;
      if (nextMode === 'every_tick') {
        next.modelingMode = 'minute_ohlc';
      }
      return next;
    });
  }

  function updateRule(field, value) {
    setRules((previous) => ({ ...previous, [field]: value }));
  }

  // Called by the chart after the user rectangle-selects a candle range. The closes from the
  // selection start to the entry bar become the pattern template (look-ahead safe: the template
  // ends at the entry bar, and matching only ever uses closed bars up to that point).
  // Dispatched when the user rectangle-selects a candle range on the chart. The active
  // captureTarget decides whether the range becomes a pattern template or inferred conditions.
  function handleRangeSelected(lo, hi) {
    const target = captureTarget;
    setCaptureTarget('');
    const [kind, side] = String(target).split(':');
    if ((side !== 'long' && side !== 'short') || lo == null || hi == null || hi - lo + 1 < 2) {
      return;
    }
    if (kind === 'pattern') {
      const mode = rules.patternMatchMode === 'ohlc' ? 'ohlc' : 'close';
      const sel = [];
      for (let i = lo; i <= hi; i += 1) {
        const candle = engine?.candles?.[i];
        if (candle) sel.push(candle);
      }
      if (sel.length < 2) return;
      const vec = mode === 'ohlc'
        ? sel.flatMap((c) => [Number(c.open), Number(c.high), Number(c.low), Number(c.close)])
        : sel.map((c) => Number(c.close));
      if (vec.length < 2 || vec.some((n) => !Number.isFinite(n))) return;
      const key = side === 'short' ? 'patternShortTemplates' : 'patternLongTemplates';
      setRules((previous) => ({
        ...previous,
        patternEntryEnabled: true,
        [key]: [...(Array.isArray(previous[key]) ? previous[key] : []), vec],
      }));
    } else if (kind === 'conditions') {
      const conditions = inferConditionsFromSelection(engine, lo, hi, side);
      if (conditions.length) {
        const maxDepth = conditions.reduce((m, c) => Math.max(m, Math.max(Number(c.leftShift) || 0, Number(c.rightShift) || 0)), 0);
        setCaptureStrictness(Math.min(2, maxDepth + 1)); // default: entry bar + one prior bar
        setPendingConditionCapture({ side, conditions, selection: { lo, hi } });
      }
    }
  }

  // A condition's "depth" is how many bars back it reaches; the strictness slider keeps only
  // conditions within the chosen depth (1 = entry bar only) so a capture isn't over-constrained.
  // Every captured condition is anchored on the entry bar, so any strictness level still
  // matches the selected entry candle.
  const conditionDepth = (c) => Math.max(Number(c.leftShift) || 0, Number(c.rightShift) || 0);

  const captureMaxStrictness = pendingConditionCapture
    ? Math.max(1, pendingConditionCapture.conditions.reduce((m, c) => Math.max(m, conditionDepth(c) + 1), 1))
    : 1;
  const captureStrict = Math.min(Math.max(1, captureStrictness), captureMaxStrictness);
  const captureKeptConditions = useMemo(() => {
    if (!pendingConditionCapture) return [];
    return pendingConditionCapture.conditions.filter((c) => conditionDepth(c) <= captureStrict - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConditionCapture, captureStrict]);
  // Live signal preview while the capture panel is open: the candles where the kept conditions
  // hold, recomputed on every slider move so the chart shows the effect in real time.
  const capturePreview = useMemo(() => {
    if (!pendingConditionCapture || !captureKeptConditions.length) return null;
    const mode = pendingConditionCapture.side === 'short' ? rules.shortMode : rules.longMode;
    return {
      side: pendingConditionCapture.side,
      selection: pendingConditionCapture.selection || null,
      indices: findConditionSignalIndices(engine, captureKeptConditions, mode),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConditionCapture, captureKeptConditions, engine, rules.longMode, rules.shortMode]);

  // Strategy debugger: clicking a candle in inspect mode explains that bar's signal
  // evaluation from the engine-recorded trace.
  function handleBarInspect(index) {
    setInspectedBar(index);
    setRightCollapsed(false);
    setRightTab('debug');
  }

  const debugInfo = useMemo(() => (
    inspectedBar === null ? null : explainSignalBar(engine, inspectedBar)
  ), [engine, inspectedBar]);

  // Data for the chart-area optimization plot (live during a run, final on completion).
  const optimizeViz = useMemo(() => {
    if (optimizeMode !== 'grid' || !optimizeJob) return null;
    const live = ['queued', 'running'].includes(optimizeJob.status) ? optimizeJob.currentStep : null;
    const done = optimizeJob.status === 'completed' ? optimizeJob.result : null;
    const source = done || live;
    if (!source) return null;
    const series = source.series || [];
    const equity = source.bestEquity || done?.bestEquity || [];
    if (!series.length && equity.length < 2) return null;
    return {
      series,
      equity,
      bestValue: source.bestValue ?? done?.best?.metrics?.[optimizeMetric],
      done: source.done,
      total: source.total,
    };
  }, [optimizeJob, optimizeMode, optimizeMetric]);
  const showOptimizeViz = rightTab === 'optimize' && !!optimizeViz;
  const optimizeMetricLabel = ({
    profitFactor: 'Profit Factor', netProfit: 'Net Profit', expectancy: 'Expectancy',
    sharpe: 'Sharpe', winRate: 'Win Rate', recoveryFactor: 'Recovery Factor', maxDrawdown: 'Drawdown',
  })[optimizeMetric] || optimizeMetric;

  // Which strategy parameters can be optimized — only fields the CURRENT strategy actually
  // uses (via its enabled features), each with a data type so the UI can show the right
  // control. `when` gates a field on the relevant feature flags.
  const optimizableParams = useMemo(() => {
    const num = (rangeMin) => ({ type: 'number', rangeMin });
    const bool = () => ({ type: 'boolean' });
    const en = (values) => ({ type: 'enum', values });
    const catalog = [
      // Trade management (always relevant)
      { key: 'stopLoss', label: 'Stop Loss (pts)', ...num() },
      { key: 'takeProfit', label: 'Take Profit (pts)', when: (r) => !r.breakevenOnlyEnabled, ...num() },
      { key: 'trailingStop', label: 'Trailing Stop (pts)', ...num() },
      { key: 'breakEvenTrigger', label: 'Breakeven Trigger (pts)', ...num() },
      { key: 'breakEvenOffset', label: 'Breakeven Offset (pts)', when: (r) => Number(r.breakEvenTrigger) > 0, ...num() },
      { key: 'structureTrailingLookback', label: 'Swing Lookback (bars)', when: (r) => r.structureTrailingEnabled, ...num() },
      { key: 'structureTrailingBuffer', label: 'Structure Buffer (pts)', when: (r) => r.structureTrailingEnabled, ...num() },
      { key: 'partialTpPoints', label: 'Partial TP1 Distance (pts)', when: (r) => r.partialTpEnabled, ...num() },
      { key: 'partialTpPercent', label: 'Partial Close %', when: (r) => r.partialTpEnabled, ...num() },
      { key: 'partialTpMoveBreakeven', label: 'Breakeven After TP1', when: (r) => r.partialTpEnabled, ...bool() },
      { key: 'maxOpenTrades', label: 'Max Open Trades', ...num() },
      { key: 'cooldownBars', label: 'Cooldown (bars)', ...num() },
      { key: 'closeOnOpposite', label: 'Close On Opposite', ...bool() },
      { key: 'breakevenOnlyEnabled', label: 'Breakeven Only', ...bool() },
      // Risk sizing
      { key: 'riskAmount', label: 'Risk Amount', when: (r) => r.riskMode && r.riskMode !== 'flat_lot', ...num() },
      { key: 'maxDailyLossValue', label: 'Max Daily Loss', when: (r) => r.maxDailyLossMode && r.maxDailyLossMode !== 'off', ...num() },
      { key: 'maxDrawdownPct', label: 'Max Drawdown %', when: (r) => Number(r.maxDrawdownPct) > 0, ...num() },
      { key: 'maxConsecutiveLosses', label: 'Max Consecutive Losses', when: (r) => Number(r.maxConsecutiveLosses) > 0, ...num() },
      // Entry: indicator conditions
      { key: 'longMode', label: 'Long Match', when: (r) => r.indicatorEntriesEnabled, ...en(['all', 'any']) },
      { key: 'shortMode', label: 'Short Match', when: (r) => r.indicatorEntriesEnabled, ...en(['all', 'any']) },
      // Range breakout
      { key: 'rangeStartHour', label: 'Range Start Hour', when: (r) => r.rangeBreakoutEnabled, ...num() },
      { key: 'rangeEndHour', label: 'Range End Hour', when: (r) => r.rangeBreakoutEnabled, ...num() },
      { key: 'rangeBreakoutBuffer', label: 'Range Buffer (pts)', when: (r) => r.rangeBreakoutEnabled, ...num() },
      { key: 'rangeBreakoutSide', label: 'Range Side', when: (r) => r.rangeBreakoutEnabled, ...en(['both', 'long', 'short']) },
      { key: 'rangeBreakoutOnce', label: 'Trade Range Once', when: (r) => r.rangeBreakoutEnabled, ...bool() },
      // Structural breakout
      { key: 'structuralBreakoutBuffer', label: 'Structural Buffer (pts)', when: (r) => r.structuralBreakoutEnabled, ...num() },
      { key: 'structuralBreakoutSide', label: 'Structural Side', when: (r) => r.structuralBreakoutEnabled, ...en(['both', 'long', 'short']) },
      { key: 'structuralBreakoutRef', label: 'Structural Ref', when: (r) => r.structuralBreakoutEnabled, ...en(['prevDayHL', 'prevWeekHL', 'donchian', 'atrVolatility']) },
      { key: 'structuralBreakoutOnce', label: 'Structural Once', when: (r) => r.structuralBreakoutEnabled, ...bool() },
      { key: 'donchianPeriod', label: 'Donchian Period', when: (r) => r.structuralBreakoutEnabled && r.structuralBreakoutRef === 'donchian', ...num() },
      { key: 'atrBreakoutPeriod', label: 'ATR Breakout Period', when: (r) => r.structuralBreakoutEnabled && r.structuralBreakoutRef === 'atrVolatility', ...num() },
      { key: 'atrBreakoutMultiple', label: 'ATR Multiple', when: (r) => r.structuralBreakoutEnabled && r.structuralBreakoutRef === 'atrVolatility', ...num() },
      // Candle patterns
      { key: 'candlePatternSide', label: 'Candle Pattern Side', when: (r) => r.candlePatternEntryEnabled, ...en(['both', 'long', 'short']) },
      // Pattern by example
      { key: 'patternThreshold', label: 'Pattern Threshold', when: (r) => r.patternEntryEnabled, ...num() },
      // Grid / DCA
      { key: 'gridOrderCount', label: 'Grid Orders', when: (r) => r.gridEnabled, ...num() },
      { key: 'gridSpacingPoints', label: 'Grid Spacing (pts)', when: (r) => r.gridEnabled, ...num() },
      { key: 'gridLotMultiplier', label: 'Grid Lot Mult', when: (r) => r.gridEnabled, ...num() },
      { key: 'gridBasketTpPoints', label: 'Grid Basket TP (pts)', when: (r) => r.gridEnabled, ...num() },
      { key: 'gridBasketSlPoints', label: 'Grid Basket SL (pts)', when: (r) => r.gridEnabled, ...num() },
      // Martingale
      { key: 'martingaleFactor', label: 'Martingale Factor', when: (r) => r.martingaleEnabled, ...num() },
      { key: 'martingaleMaxSteps', label: 'Martingale Max Steps', when: (r) => r.martingaleEnabled, ...num() },
      { key: 'antiMartingaleFactor', label: 'Anti-Martingale Factor', when: (r) => r.antiMartingaleEnabled, ...num() },
      // Pending orders
      { key: 'pendingOffsetPoints', label: 'Pending Offset (pts)', when: (r) => r.pendingOrderEnabled, ...num() },
      { key: 'pendingExpiryBars', label: 'Pending Expiry (bars)', when: (r) => r.pendingOrderEnabled, ...num() },
      { key: 'pendingOrderType', label: 'Pending Type', when: (r) => r.pendingOrderEnabled, ...en(['stop', 'limit']) },
      { key: 'pendingDeleteOpposite', label: 'Delete Opposite (OCO)', when: (r) => r.pendingOrderEnabled, ...bool() },
      // Filters
      { key: 'maxSpreadPoints', label: 'Max Spread (pts)', when: (r) => r.spreadFilterEnabled, ...num() },
      { key: 'trendFilterMaPeriod', label: 'Trend Filter MA Period', when: (r) => r.trendFilterEnabled, ...num() },
      { key: 'trendFilterMaType', label: 'Trend Filter MA Type', when: (r) => r.trendFilterEnabled, ...en(['EMA', 'SMA']) },
      { key: 'trendFilterMode', label: 'Trend Filter Mode', when: (r) => r.trendFilterEnabled, ...en(['slope', 'price']) },
      { key: 'timedEntryStartHour', label: 'Timed Entry Start Hour', when: (r) => r.timedEntryEnabled, ...num() },
      { key: 'timedEntryEndHour', label: 'Timed Entry End Hour', when: (r) => r.timedEntryEnabled, ...num() },
      // Overlays
      { key: 'basketTpUsd', label: 'Basket TP (USD)', when: (r) => r.basketTpEnabled, ...num() },
      { key: 'basketSlUsd', label: 'Basket SL (USD)', when: (r) => r.basketTpEnabled, ...num() },
      { key: 'equityProtectionValue', label: 'Equity Protection', when: (r) => r.equityProtectionEnabled, ...num() },
    ];
    const ruleFields = catalog
      .filter((field) => rules[field.key] !== undefined && (typeof field.when !== 'function' || field.when(rules)))
      .map((field) => ({
        id: `rule:${field.key}`,
        target: 'rule',
        key: field.key,
        label: field.label,
        type: field.type,
        enumValues: field.values || null,
        current: rules[field.key],
      }));
    // Indicator params only matter when indicators actually drive the strategy.
    const indicatorFields = (rules.indicatorEntriesEnabled || rules.scriptEnabled)
      ? indicators.flatMap((indicator) => Object.keys(indicator.params || {}).map((key) => ({
        id: `ind:${indicator.id}:${key}`,
        target: 'indicator',
        indicatorId: indicator.id,
        key,
        label: `${indicator.type} ${key}`,
        type: 'number',
        enumValues: null,
        current: indicator.params[key],
      })))
      : [];
    return [...indicatorFields, ...ruleFields];
  }, [indicators, rules]);

  function seedOptimizeParam(meta) {
    if (!meta) return null;
    if (meta.type === 'boolean') return { id: meta.id };
    if (meta.type === 'enum') return { id: meta.id, values: [...(meta.enumValues || [])] };
    const base = Number(meta.current) || 0;
    return {
      id: meta.id,
      min: String(base ? Math.max(0, Math.round(base * 0.5)) : 0),
      max: String(base ? Math.round(base * 1.5) : 10),
      step: String(base ? Math.max(1, Math.round(base * 0.25)) : 1),
    };
  }

  function addOptimizeParam() {
    const used = new Set(optimizeParams.map((param) => param.id));
    const next = optimizableParams.find((param) => !used.has(param.id));
    const seeded = seedOptimizeParam(next);
    if (seeded) setOptimizeParams((prev) => [...prev, seeded]);
  }

  function updateOptimizeParam(index, field, value) {
    if (field === 'id') {
      const meta = optimizableParams.find((param) => param.id === value);
      const seeded = seedOptimizeParam(meta);
      if (seeded) setOptimizeParams((prev) => prev.map((param, i) => (i === index ? seeded : param)));
      return;
    }
    setOptimizeParams((prev) => prev.map((param, i) => (i === index ? { ...param, [field]: value } : param)));
  }

  function toggleOptimizeEnumValue(index, value) {
    setOptimizeParams((prev) => prev.map((param, i) => {
      if (i !== index) return param;
      const values = Array.isArray(param.values) ? param.values : [];
      const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
      return { ...param, values: next };
    }));
  }

  function removeOptimizeParam(index) {
    setOptimizeParams((prev) => prev.filter((_, i) => i !== index));
  }

  // Click-drag to pan a scroll container (horizontal + vertical) — for the results table.
  function attachDragScroll(node) {
    if (!node || node.dataset.dragScroll === '1') return;
    node.dataset.dragScroll = '1';
    let active = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    node.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, input, select, a')) return;
      active = true;
      startX = event.clientX;
      startY = event.clientY;
      scrollLeft = node.scrollLeft;
      scrollTop = node.scrollTop;
      node.classList.add('is-grabbing');
    });
    const end = () => { active = false; node.classList.remove('is-grabbing'); };
    node.addEventListener('pointermove', (event) => {
      if (!active) return;
      node.scrollLeft = scrollLeft - (event.clientX - startX);
      node.scrollTop = scrollTop - (event.clientY - startY);
    });
    node.addEventListener('pointerup', end);
    node.addEventListener('pointerleave', end);
  }

  const optimizeCombos = useMemo(() => optimizeParams.reduce((total, param) => {
    const meta = optimizableParams.find((entry) => entry.id === param.id);
    if (!meta) return total;
    if (meta.type === 'boolean') return total * 2;
    if (meta.type === 'enum') return total * Math.max(1, (param.values || []).length);
    const min = Number(param.min);
    const max = Number(param.max);
    const step = Math.abs(Number(param.step)) || 1;
    if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return total;
    return total * Math.min(400, Math.floor((max - min) / step) + 1);
  }, 1), [optimizeParams, optimizableParams]);

  const stopOptimizePoll = useCallback(() => {
    if (optimizePollRef.current) {
      clearInterval(optimizePollRef.current);
      optimizePollRef.current = null;
    }
  }, []);

  async function runOptimize() {
    if (optimizePending || !optimizeParams.length) return;
    const catalog = new Map(optimizableParams.map((param) => [param.id, param]));
    const parameters = optimizeParams.map((param) => {
      const meta = catalog.get(param.id);
      if (!meta) return null;
      const base = { target: meta.target, key: meta.key, indicatorId: meta.indicatorId, label: meta.label, type: meta.type };
      if (meta.type === 'boolean') return base;
      if (meta.type === 'enum') return { ...base, values: (param.values && param.values.length) ? param.values : meta.enumValues };
      return { ...base, min: Number(param.min), max: Number(param.max), step: Number(param.step) };
    }).filter((param) => param && (param.type !== 'enum' || (param.values && param.values.length)));
    if (!parameters.length) return;
    setOptimizePending(true);
    setOptimizeJob(null);
    stopOptimizePoll();
    const walkForward = optimizeMode === 'walkforward';
    try {
      const strategy = buildStrategyPayload();
      const strategyPayload = { setup: strategy.setup, indicators: strategy.indicators, rules: strategy.rules, sessions: strategy.sessions };
      const response = walkForward
        ? await startWalkForward({ strategy: strategyPayload, parameters, metric: optimizeMetric, minTrades: Number(optimizeMinTrades) || 0, windows: Number(walkForwardWindows) || 4 })
        : await startOptimization({ strategy: strategyPayload, parameters, metric: optimizeMetric, minTrades: Number(optimizeMinTrades) || 0 });
      const jobId = response.job?.id;
      setOptimizeJob(response.job || null);
      if (!jobId) {
        setOptimizePending(false);
        return;
      }
      optimizePollRef.current = setInterval(async () => {
        try {
          const poll = walkForward ? await fetchWalkForward(jobId) : await fetchOptimization(jobId);
          setOptimizeJob(poll.job || null);
          if (['completed', 'failed', 'cancelled'].includes(poll.job?.status)) {
            stopOptimizePoll();
            setOptimizePending(false);
          }
        } catch (error) {
          stopOptimizePoll();
          setOptimizePending(false);
        }
      }, 900);
    } catch (error) {
      setOptimizeJob({ status: 'failed', error: error.message || 'Optimization failed to start.' });
      setOptimizePending(false);
    }
  }

  async function stopOptimize() {
    const jobId = optimizeJob?.id;
    stopOptimizePoll();
    setOptimizePending(false);
    if (jobId) {
      try {
        await (optimizeMode === 'walkforward' ? cancelWalkForward(jobId) : cancelOptimization(jobId));
      } catch (error) { /* noop */ }
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopOptimizePoll(), []);

  function applyOptimizeResult(row) {
    if (!row?.params) return;
    const catalog = new Map(optimizableParams.map((param) => [param.label, param]));
    setRules((prevRules) => {
      const nextRules = { ...prevRules };
      Object.entries(row.params).forEach(([label, value]) => {
        const meta = catalog.get(label);
        if (meta?.target === 'rule') {
          nextRules[meta.key] = meta.type === 'boolean' ? (value === true || value === 'true') : String(value);
        }
      });
      return nextRules;
    });
    setIndicators((prevIndicators) => prevIndicators.map((indicator) => {
      let changed = false;
      const params = { ...indicator.params };
      Object.entries(row.params).forEach(([label, value]) => {
        const meta = catalog.get(label);
        if (meta?.target === 'indicator' && meta.indicatorId === indicator.id) {
          params[meta.key] = String(value);
          changed = true;
        }
      });
      return changed ? { ...indicator, params } : indicator;
    }));
    setBuilderAiNotice('Applied optimized parameters. Run the backtest to confirm.');
  }

  function applyCapturedConditions(replace) {
    const capture = pendingConditionCapture;
    setPendingConditionCapture(null);
    if (!capture) return;
    const { side, conditions } = capture;
    const filtered = conditions.filter((c) => conditionDepth(c) <= captureStrict - 1);
    const applied = filtered.length ? filtered : conditions;
    const key = side === 'short' ? 'shortConditions' : 'longConditions';
    const keyOf = (c) => `${c.left}|${c.leftShift}|${c.operator}|${c.rightMode}|${c.rightMode === 'value' ? c.rightValue : `${c.rightOperand}@${c.rightShift}`}`;
    setRules((previous) => {
      const existing = Array.isArray(previous[key]) ? previous[key] : [];
      let next;
      if (replace) {
        next = applied;
      } else {
        const have = new Set(existing.map(keyOf));
        next = [...existing, ...applied.filter((c) => !have.has(keyOf(c)))];
      }
      return { ...previous, indicatorEntriesEnabled: true, [key]: next };
    });
  }

  function toggleRuleDay(dayId) {
    setRules((previous) => ({
      ...previous,
      dayFilters: previous.dayFilters.includes(dayId)
        ? previous.dayFilters.filter((entry) => entry !== dayId)
        : [...previous.dayFilters, dayId],
    }));
  }

  function updateRuleCondition(side, conditionId, field, value) {
    const key = side === 'long' ? 'longConditions' : 'shortConditions';
    setRules((previous) => ({
      ...previous,
      [key]: previous[key].map((condition) => (
        condition.id === conditionId ? { ...condition, [field]: value } : condition
      )),
    }));
  }

  function addRuleCondition(side) {
    const key = side === 'long' ? 'longConditions' : 'shortConditions';
    setRules((previous) => ({
      ...previous,
      [key]: [...previous[key], createRuleCondition(side)],
    }));
  }

  function removeRuleCondition(side, conditionId) {
    const key = side === 'long' ? 'longConditions' : 'shortConditions';
    setRules((previous) => ({
      ...previous,
      [key]: previous[key].length > 1
        ? previous[key].filter((condition) => condition.id !== conditionId)
        : previous[key],
    }));
  }

  function updateIndicator(indicatorId, paramKey, value) {
    setIndicators((previous) => previous.map((indicator) => (
      indicator.id === indicatorId
        ? { ...indicator, params: { ...indicator.params, [paramKey]: value } }
        : indicator
    )));
  }

  function setIndicatorApplyTo(indicatorId, value) {
    setIndicators((previous) => previous.map((indicator) => (
      indicator.id === indicatorId ? { ...indicator, applyTo: value } : indicator
    )));
  }

  function removeIndicator(indicatorId) {
    // Any MA applied to the removed indicator falls back to price.
    setIndicators((previous) => previous
      .filter((indicator) => indicator.id !== indicatorId)
      .map((indicator) => (indicator.applyTo === indicatorId ? { ...indicator, applyTo: 'price' } : indicator)));
  }

  function toggleSession(sessionId) {
    setSessions((previous) => (
      previous.includes(sessionId)
        ? previous.filter((session) => session !== sessionId)
        : [...previous, sessionId]
    ));
  }

  async function startRun(options = {}) {
    if (!canRun || serverRunPending) {
      return;
    }
    const acknowledgeDataQuality = Boolean(options.acknowledgeDataQuality);

    if (
      executionHistoryRequirement.requiresSeparateExecutionHistory
      && !backfillReadyForSetup(backfillJob, setup)
      && !hasActiveBackfillJob
    ) {
      setAutoRunAfterBackfill(true);
      setStrategyNotice(`${getModelLabel(setup.modelingMode)} needs ${executionHistoryRequirement.executionTimeframe} execution history. Preparing required history first.`);
      await startBackfill({ auto: true });
      return;
    }

    if (executionHistoryRequirement.requiresSeparateExecutionHistory && hasActiveBackfillJob) {
      setAutoRunAfterBackfill(true);
      setStrategyNotice('Backfill is already running. The backtest will start when it finishes.');
      setLeftTab('market');
      setLeftCollapsed(false);
      return;
    }

    requestBacktestNotificationPermission();
    activeRunSignatureRef.current = currentBacktestSignature;
    const runToken = runRequestTokenRef.current + 1;
    runRequestTokenRef.current = runToken;
    serverResultCacheRef.current.delete(currentBacktestSignature);
    clearInterval(timerRef.current);
    setServerResult(null);
    setPendingServerResult(null);
    setBacktestJob(null);
    setMarketState((previous) => ({ ...previous, error: '' }));
    setChartViewRevision((previous) => previous + 1);

    setPaused(false);

    const previewEngine = simulateBacktest(
      setup,
      indicators,
      rules,
      sessions,
      marketData,
    );
    setHasRunBacktest(false);
    setEngine(previewEngine);
    setCurrentBar(previewEngine.candles.length - 1);
    setRunning(false);
    setServerRunPending(true);

    startServerBacktestJob({
      setup: acknowledgeDataQuality ? { ...setup, acknowledgeDataQuality: true } : setup,
      indicators,
      rules,
      sessions,
    }).then((response) => {
      if (runRequestTokenRef.current !== runToken) {
        if (response.job?.id) {
          cancelServerBacktestJob(response.job.id).catch(() => {});
        }
        return;
      }
      setBacktestJob(response.job || null);
    }).catch((error) => {
      if (runRequestTokenRef.current !== runToken) {
        return;
      }
      setMarketState((previous) => ({
        ...previous,
        error: error.message || 'Failed to run the server backtest.',
      }));
      setLeftTab('market');
      setLeftCollapsed(false);
      setMobileView('setup');
      setServerRunPending(false);
      setRunning(false);
      setPaused(false);
    });

  }

  function pauseRun() {
    if (!running) {
      return;
    }
    clearInterval(timerRef.current);
    setPaused(true);
  }

  function resumeRun() {
    if (!running) {
      return;
    }
    setPaused(false);
  }

  async function stopRun() {
    runRequestTokenRef.current += 1;
    clearInterval(timerRef.current);
    setRunning(false);
    setPaused(false);
    setCurrentBar(engine.candles.length - 1);
    // Skipping a replay should still surface the authoritative server result
    // rather than discarding it.
    if (pendingServerResult) {
      serverResultCacheRef.current.set(activeRunSignatureRef.current, pendingServerResult);
      setServerResult(pendingServerResult);
      setHasRunBacktest(true);
      notifyBacktestComplete({ setup: setupRef.current, summary: pendingServerResult.summary });
    }
    setPendingServerResult(null);
    if (hasActiveBacktestJob && backtestJob?.id) {
      try {
        const response = await cancelServerBacktestJob(backtestJob.id);
        setBacktestJob(response.job || null);
      } catch (error) {
        setBacktestJob((previous) => (previous ? { ...previous, error: error.message || 'Failed to cancel the backtest job.' } : previous));
      }
    } else {
      setServerRunPending(false);
    }
  }

  async function startBackfill(options = {}) {
    if (hasActiveBackfillJob) {
      return;
    }

    setBackfillStartPending(true);
    try {
      const executionTimeframe = getExecutionTimeframeForSetup(setup);
      const response = await startServerBackfillJob({
        provider: 'deriv',
        symbol: setup.symbol,
        timeframe: executionTimeframe,
        from: setup.from,
        to: setup.to,
      });
      setBackfillJob(response.job || null);
      if (!options.silent) {
        setLeftTab('market');
        setLeftCollapsed(false);
      }
      if (options.auto && !options.silent) {
        setStrategyNotice('Backfill started. The backtest will run automatically after history is ready.');
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to start the backfill job.';
      setAutoRunAfterBackfill(false);
      if (isDerivRetentionError(errorMessage)) {
        const recentRange = getDerivRecentRange(setup.to, executionHistoryRequirement.executionTimeframe);
        openDialog({
          title: 'History Range Is Too Long',
          message: recentRange
            ? `The source provider cannot provide enough ${executionHistoryRequirement.executionTimeframe} history for ${setup.from} to ${setup.to}. Use ${recentRange.from} to ${recentRange.to} instead?`
            : errorMessage,
          confirmLabel: recentRange ? 'Use Recent Range' : 'Close',
          cancelLabel: 'Cancel',
          tone: 'warning',
          onConfirm: () => {
            if (recentRange) {
              setSetup((previous) => ({
                ...previous,
                from: recentRange.from,
                to: recentRange.to,
                dataSource: 'deriv',
                brokerProfileId: 'deriv-svg-server-03',
              }));
              setBackfillJob(null);
              setAutoRunAfterBackfill(false);
              setStrategyNotice('Date range adjusted. Run again when the history preview reloads.');
            }
            closeDialog();
          },
        });
      }
      setMarketState((previous) => ({
        ...previous,
        error: errorMessage,
      }));
    } finally {
      setBackfillStartPending(false);
    }
  }

  async function requestBackfillCancel() {
    setAutoRunAfterBackfill(false);
    setBackfillStartPending(false);
    if (!backfillJob?.id || !hasActiveBackfillJob) {
      return;
    }
    try {
      const response = await cancelServerBackfillJob(backfillJob.id);
      setBackfillJob(response.job || null);
    } catch (error) {
      setBackfillJob((previous) => (previous ? { ...previous, error: error.message || 'Failed to cancel the backfill job.' } : previous));
    }
  }

  async function requestBacktestCancel() {
    await stopRun();
  }

  async function handleSaveStrategy({ asNew = false } = {}) {
    if (!authToken) {
      setStrategyNotice('Sign in to save strategies.');
      navigate('/login');
      return;
    }
    if ((asNew || !activeStrategyId) && savedStrategies.length >= strategyLimit) {
      setStrategyNotice(`Your ${testerPlan} plan allows up to ${strategyLimit} saved strategies.`);
      return;
    }

    const payload = buildStrategyPayload();
    setStrategyActionPending(asNew ? 'save-new' : 'save');
    setStrategyNotice('');
    try {
      const response = asNew || !activeStrategyId
        ? await createSavedTesterStrategy(payload, authToken)
        : await updateSavedTesterStrategy(activeStrategyId, payload, authToken);
      if (!response.strategy) {
        throw new Error('The server did not return the saved strategy.');
      }
      applySavedStrategy(response.strategy);
      await refreshSavedStrategies();
      setStrategyNotice(asNew || !activeStrategyId ? 'Strategy saved.' : 'Strategy updated.');
    } catch (error) {
      setStrategyNotice(error.message || 'Failed to save strategy.');
    } finally {
      setStrategyActionPending('');
    }
  }

  function handleCreateNew() {
    const currentKey = JSON.stringify(buildStrategyPayload());
    const hasUnsavedChanges = currentKey !== savedPayloadKeyRef.current;

    // If nothing is loaded and nothing was changed from defaults, just reset silently
    if (!activeStrategyId && !hasUnsavedChanges) {
      applyDefaultStrategy();
      setStrategyNotice('New strategy started.');
      return;
    }

    const messageDetail = hasUnsavedChanges
      ? `"${strategyName || 'This strategy'}" has unsaved changes.`
      : `"${strategyName || 'This strategy'}" is currently loaded.`;

    openDialog({
      title: 'Create New Strategy',
      message: `${messageDetail} Save or discard before starting a new strategy?`,
      confirmLabel: 'Save',
      thirdLabel: 'Discard',
      cancelLabel: 'Cancel',
      tone: 'warning',
      onConfirm: async () => {
        closeDialog();
        await handleSaveStrategy();
        applyDefaultStrategy();
        setStrategyNotice('New strategy started.');
      },
      onThird: () => {
        closeDialog();
        applyDefaultStrategy();
        setStrategyNotice('New strategy started.');
      },
    });
  }


  async function handleLoadSelectedStrategy() {
    if (!authToken) {
      setStrategyNotice('Sign in to load saved strategies.');
      navigate('/login');
      return;
    }
    if (!selectedStrategyId) {
      setStrategyNotice('Choose a saved strategy first.');
      return;
    }

    setStrategyActionPending('load');
    setStrategyNotice('');
    try {
      const response = await fetchSavedTesterStrategy(selectedStrategyId, authToken);
      if (!response.strategy) {
        throw new Error('The selected strategy could not be loaded.');
      }
      applySavedStrategy(response.strategy);
      setStrategyNotice(`Loaded "${response.strategy.name}".`);
    } catch (error) {
      setStrategyNotice(error.message || 'Failed to load strategy.');
    } finally {
      setStrategyActionPending('');
    }
  }

  async function handleDeleteActiveStrategy() {
    if (!authToken || !activeStrategyId) {
      return;
    }

    openDialog({
      title: 'Delete Saved Strategy',
      message: 'Delete this saved strategy from your account?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        closeDialog();
        setStrategyActionPending('delete');
        setStrategyNotice('');
        try {
          await deleteSavedTesterStrategy(activeStrategyId, authToken);
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('strategy');
          setSearchParams(nextParams, { replace: true });
          setSelectedStrategyId('');
          setActiveStrategyId('');
          await refreshSavedStrategies();
          setStrategyNotice('Saved strategy deleted.');
        } catch (error) {
          setStrategyNotice(error.message || 'Failed to delete strategy.');
        } finally {
          setStrategyActionPending('');
        }
      },
    });
  }

  async function handleExportStrategy() {
    if (!authToken) {
      setStrategyNotice('Sign in to export MQ5 EA source.');
      navigate('/login');
      return;
    }
    if (!certifiedExportSupport.supported) {
      setStrategyNotice(certifiedExportSupport.message);
      return;
    }
    setStrategyActionPending('export');
    setStrategyNotice('');
    try {
      const response = await exportStrategyEa(buildStrategyPayload(), authToken);
      if (!response.filename || !response.content) {
        throw new Error('The export response was incomplete.');
      }
      downloadTextFile(response.filename, response.content);
      setStrategyNotice(`EA source exported as ${response.filename}.`);
    } catch (error) {
      setStrategyNotice(error.message || 'Failed to export EA source.');
    } finally {
      setStrategyActionPending('');
    }
  }

  function addIndicator(type) {
    setIndicators((previous) => [...previous, createIndicator(type, previous.length)]);
    setModalOpen(false);
    setLeftTab('indicators');
    setLeftCollapsed(false);
  }

  function renderAiTab() {
    const examples = [
      'Add a trend filter using EMA 200.',
      'Set max open trades to 3 and add ATR.',
      'Switch to 1-minute OHLC modeling mode.',
    ];

    return (
      <div className="tester-chat-container">
        <div className="tester-chat-header">
          <FiCpu size={14} />
          <span>VixAI</span>
          <small>Builder assistant</small>
        </div>

        <div className="tester-chat-messages" aria-live="polite">
          {!builderAiMessages.length && (
            <div className="tester-chat-welcome">
              <p>Describe the EA you want. I can update the Builder when the request maps to supported controls, or give setup steps when it needs custom/MT5-side work.</p>
              <div className="tester-chat-examples">
                {examples.map((example) => (
                  <button
                    key={example}
                    type="button"
                    className="tester-chip"
                    onClick={() => setBuilderAiInput(example)}
                    disabled={builderAiPending}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
          {builderAiMessages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`tester-chat-row ${message.role}${message.toolResult ? ' tool-result' : ''}`}>
              <div className="tester-chat-avatar">
                {message.role === 'user' ? 'U' : <FiCpu size={11} />}
              </div>
              <div className="tester-chat-content">
                <div className="tester-chat-bubble">
                  {message.role === 'assistant'
                    ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    : <p>{message.content}</p>}
                </div>
                {Array.isArray(message.actions) && message.actions.some((action) => action && typeof action === 'object') && (
                  <div className="tester-ai-actions">
                    {message.actions
                      .filter((action) => action && typeof action === 'object')
                      .map((action) => (
                        <button
                          key={action.id || `${action.tool}-${action.label}`}
                          type="button"
                          className={`tester-ai-action ${action.status === 'blocked' ? 'blocked' : ''}`}
                          disabled={builderAiPending || !!builderAiToolPendingId || action.status === 'blocked' || action.status === 'completed'}
                          onClick={() => runBuilderAiAction(action)}
                          title={action.blockedReason || action.reason || action.description}
                        >
                          <strong>{builderAiToolPendingId === action.id ? 'Running...' : action.label}</strong>
                          <small>{action.approvalRequired ? 'Approval required' : action.status}</small>
                        </button>
                      ))}
                  </div>
                )}
                {message.role === 'assistant' && index === builderAiMessages.length - 1 && (() => {
                  const recommendationItems = extractRecommendationItems(message.content);
                  if (!recommendationItems.length) return null;
                  const anySelected = recommendationItems.some((item) => aiRecSelections[`${index}:${item.key}`]);
                  return (
                    <div className="tester-ai-recommendations">
                      {recommendationItems.map((item) => (
                        <label key={item.key} className="tester-ai-recommendation">
                          <input
                            type="checkbox"
                            checked={Boolean(aiRecSelections[`${index}:${item.key}`])}
                            onChange={() => toggleAiRecommendation(index, item.key)}
                            disabled={builderAiPending}
                          />
                          <span><strong>{item.key}.</strong> {item.text}</span>
                        </label>
                      ))}
                      <button
                        type="button"
                        className="primary-cta tester-ai-apply-recs"
                        disabled={builderAiPending || !anySelected}
                        onClick={() => applySelectedRecommendations(index, recommendationItems)}
                      >
                        Apply selected recommendations
                      </button>
                    </div>
                  );
                })()}
                {message.role === 'assistant'
                  && Array.isArray(message.suggestions)
                  && message.suggestions.length > 0
                  && index === builderAiMessages.length - 1 && (
                  <div className="tester-ai-suggestions">
                    {message.suggestions.map((suggestion) => (
                      <button
                        key={suggestion.label}
                        type="button"
                        className="tester-chip"
                        disabled={builderAiPending}
                        onClick={() => handleSendBuilderAi(suggestion.send)}
                        title={suggestion.send}
                      >
                        {suggestion.label}
                      </button>
                    ))}
                  </div>
                )}
                {Array.isArray(message.clarifyingQuestions) && message.clarifyingQuestions.length > 0 && (
                  <div className="tester-ai-questions">
                    {message.clarifyingQuestions.map((question) => (
                      <div key={question.id || question.question} className="tester-ai-question">
                        <strong>{question.question}</strong>
                        <div className="tester-ai-question-options">
                          {(question.options || []).map((option) => (
                            <button
                              key={option.value || option.label}
                              type="button"
                              className="tester-chip"
                              disabled={builderAiPending}
                              onClick={() => handleSendBuilderAi(`${question.question} ${option.value || option.label}`)}
                            >
                              {option.label || option.value}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {builderAiPending && (
            <div className="tester-chat-row assistant">
              <div className="tester-chat-avatar"><FiCpu size={11} /></div>
              <div className="tester-chat-content">
                <div className="tester-chat-bubble tester-chat-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
        </div>

        <form
          className="tester-chat-form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSendBuilderAi();
          }}
        >
          <textarea
            rows="1"
            value={builderAiInput}
            onChange={(event) => setBuilderAiInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSendBuilderAi();
              }
            }}
            placeholder="Message VixAI..."
          />
          <button type="submit" className="tester-chat-send" disabled={builderAiPending || !builderAiInput.trim()} title="Send">
            <FiSend size={15} />
          </button>
        </form>
      </div>
    );
  }

  function renderStrategiesTab() {
    return (
      <>
        <div className="tester-card-section">
          <div className="tester-section-title">Strategy</div>
          <label className="tester-field">
            <span>Name</span>
            <input
              type="text"
              value={strategyName}
              onChange={(event) => setStrategyName(event.target.value)}
              placeholder="Strategy name"
            />
          </label>
          <label className="tester-field">
            <span>Description</span>
            <textarea
              rows="3"
              value={strategyDescription}
              onChange={(event) => setStrategyDescription(event.target.value)}
              placeholder="Optional notes for this setup"
            />
          </label>
          {authToken ? (
            <>
              <label className="tester-field">
                <span>Saved</span>
                <select value={selectedStrategyId} onChange={(event) => setSelectedStrategyId(event.target.value)}>
                  <option value="">Select saved strategy</option>
                  {savedStrategies.map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name} {strategy.symbol ? `- ${strategy.symbol} ${strategy.timeframe}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div className="tester-source-meta">
                <span>{savedStrategiesLoading ? 'Refreshing saved strategies...' : `${savedStrategies.length} saved`}</span>
                <span>{testerPlan === 'premium' ? `VixPremium · ${strategyLimit} saves` : `${remainingStrategySlots} of ${strategyLimit} save slots left`}</span>
              </div>
              <div className="tester-source-status-actions">
                <button type="button" className="tester-ghost-button" onClick={handleCreateNew} disabled={!!strategyActionPending}>
                  <FiFile /> <span>Create New</span>
                </button>
                <button type="button" className="tester-ghost-button" onClick={handleLoadSelectedStrategy} disabled={!selectedStrategyId || !!strategyActionPending}>
                  <FiFolder /> <span>{strategyActionPending === 'load' ? 'Loading...' : 'Load'}</span>
                </button>
                <button
                  type="button"
                  className="tester-ghost-button"
                  onClick={() => handleSaveStrategy({ asNew: !!activeDeployment })}
                  disabled={!!strategyActionPending || (!!activeDeployment && atStrategyLimit)}
                  title={activeDeployment
                    ? 'This strategy is running as a live signal, so it is saved as a copy instead of being overwritten'
                    : undefined}
                >
                  <FiSave /> <span>{strategyActionPending === 'save' ? 'Saving...' : activeDeployment ? 'Save as Copy' : activeStrategyId ? 'Save Changes' : 'Save'}</span>
                </button>
                <button type="button" className="tester-ghost-button" onClick={() => handleSaveStrategy({ asNew: true })} disabled={!!strategyActionPending || atStrategyLimit}>
                  <FiPlus /> <span>{strategyActionPending === 'save-new' ? 'Saving...' : 'Save as New'}</span>
                </button>
                <button type="button" className="tester-ghost-button" onClick={refreshSavedStrategies} disabled={savedStrategiesLoading || !!strategyActionPending}>
                  <FiRefreshCw /> <span>Refresh</span>
                </button>
                {activeStrategyId && (
                  <button type="button" className="tester-ghost-button" onClick={handleDeleteActiveStrategy} disabled={!!strategyActionPending}>
                    <FiTrash2 /> <span>{strategyActionPending === 'delete' ? 'Deleting...' : 'Delete'}</span>
                  </button>
                )}
              </div>
              {!canExportMq5 ? <p className="muted-text">Free plan includes 3 MQ5 exports per month. VixPremium unlocks unlimited exports.</p> : null}
              {canAttemptMq5Export && (
                <div className={`tester-source-status ${certifiedExportSupport.supported ? 'ready' : 'error'}`}>
                  <strong>{certifiedExportSupport.label}</strong>
                  <p>{certifiedExportSupport.supported ? 'This setup can be exported with the current MQ5 generator.' : certifiedExportSupport.message}</p>
                </div>
              )}
              {savedStrategiesError ? <p className="muted-text">{savedStrategiesError}</p> : null}
            </>
          ) : (
            <div className="tester-source-status">
              <strong>Sign in required</strong>
              <p>Save and reload strategies from any device once you are signed in.</p>
              <div className="tester-source-status-actions">
                <button type="button" className="tester-ghost-button" onClick={() => navigate('/login')}>
                  <FiLogIn /> <span>Sign In</span>
                </button>
              </div>
            </div>
          )}
          {/* A running signal executes a SNAPSHOT frozen at deploy time, so nothing changed here
              can reach it. Saying so explicitly matters — the natural assumption is the
              opposite, and the honest answer is more reassuring than a lock would be. */}
          {activeDeployment ? (
            <div className="tester-deployed-notice">
              <strong>Running as a live signal</strong>
              <p>
                This strategy is deployed{activeDeployment.name ? ` as "${activeDeployment.name}"` : ''}.
                The live signal runs a copy frozen when you deployed it, so anything you change
                here is only a sandbox — it cannot alter the signal or its track record. Saving
                creates a copy so the deployed original stays intact.
              </p>
              <div className="tester-source-status-actions">
                <button
                  type="button"
                  className="tester-ghost-button"
                  onClick={() => navigate(`/signals/${activeDeployment.id}`)}
                >
                  <FiRadio /> <span>Open live signal</span>
                </button>
              </div>
            </div>
          ) : null}

          <div className="tester-source-status-actions">
            <button
              type="button"
              className="tester-add-button"
              onClick={handleExportStrategy}
              disabled={!!strategyActionPending || !authToken || !canAttemptMq5Export || !certifiedExportSupport.supported}
            >
              <FiDownload /> <span>{strategyActionPending === 'export' ? 'Exporting...' : 'Export MQ5'}</span>
            </button>
            {/* Deploy Live sits beside Export MQ5 on purpose: this panel is where a finished
                strategy already lives, so it is the one place a user is certain to see it.
                A dashboard-only entry point would simply be missed. */}
            {authToken && (
              activeDeployment ? (
                <button
                  type="button"
                  className="tester-ghost-button tester-deployed-button"
                  onClick={() => navigate(`/signals/${activeDeployment.id}`)}
                  title="This strategy is already running as a live signal — open it"
                >
                  <FiRadio /> <span>Deployed</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="tester-ghost-button"
                  onClick={() => navigate('/dashboard?tab=signals')}
                  title="Run this strategy on live data in the cloud and get notified when it trades"
                >
                  <FiRadio /> <span>Deploy Live</span>
                </button>
              )
            )}
            {authToken && (
              <button type="button" className="tester-ghost-button" onClick={() => navigate('/dashboard?tab=strategies')}>
                <FiFolder /> <span>Dashboard</span>
              </button>
            )}
          </div>
            {strategyNotice ? <p className="muted-text">{strategyNotice}</p> : null}
          </div>
        </>
    );
  }

  function renderMarketTab() {
    return (
      <>
        <div className="tester-card-section">
          <div className="tester-section-title">Source Provider</div>
          <div className="tester-field">
            <span>Provider</span>
            <strong>Deriv</strong>
          </div>
          <div className={`tester-source-status ${marketState.status}`}>
            <strong>{selectedSymbolLabel || marketState.providerSymbol || 'Market'}</strong>
            <p className="tester-source-line">
              {sourceDescription}
              <LiveFeedIndicator
                enabled={!!marketState.liveTicksEnabled}
                tick={liveTick}
                timeframeLabel={setup.timeframe}
              />
            </p>
            {sourceInlineError ? <p className="tester-source-error">{sourceInlineError}</p> : null}
            <div className="tester-source-meta">
              <span>{marketState.candleCount ? `${marketState.candleCount} candles` : 'Waiting'}</span>
              <span>Provider specs</span>
              {historyQualityBadge && (
                <span
                  className={`tester-history-quality-badge ${historyQualityBadge.className}`}
                  title={historyQualityBadge.title}
                >
                  {historyQualityBadge.label}
                </span>
              )}
              {dataConfidenceBadge && (
                <span
                  className={`tester-history-quality-badge ${dataConfidenceBadge.className}`}
                  title={dataConfidenceBadge.title}
                >
                  {dataConfidenceBadge.label}
                </span>
              )}
              <span>{formatMarketTimestamp(marketState.lastLoadedAt)}</span>
            </div>
            {marketState.status === 'error' && (
              <div className="tester-source-status-actions">
                <button
                  type="button"
                  className="tester-ghost-button"
                  onClick={() => setMarketReloadNonce((nonce) => nonce + 1)}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
        {/* The most honest placement on the site: this panel is where the user is looking at
            Deriv's own market data and symbol specs, so "trade it on Deriv" is context rather
            than an advert. Compact, and below the data panel so it never sits between the user
            and the Builder controls. */}
        <DerivAffiliateBanner variant="builder" compact />
        {backfillJob && (
          <div className="tester-card-section">
            <div className="tester-section-title">History Tools</div>
            <div className={`tester-source-status ${backfillJob.status === 'failed' ? 'error' : backfillJob.status === 'completed' ? 'ready' : backfillJob.status === 'cancelled' ? '' : 'loading'}`}>
              <strong>{getJobStageLabel(backfillJob)}</strong>
              <p>{formatSymbolDisplayName(backfillJob.symbol)} {backfillJob.timeframe} {Math.round(backfillJob.progress || 0)}%</p>
              <div className="tester-source-meta">
                <span>{backfillJob.processedItems || 0} candles</span>
                <span>
                  {backfillJob.currentStep?.from && backfillJob.currentStep?.to
                    ? `${backfillJob.currentStep.from} to ${backfillJob.currentStep.to}`
                    : formatMarketTimestamp(backfillJob.completedAt || backfillJob.startedAt || backfillJob.createdAt)}
                </span>
              </div>
              {hasActiveBackfillJob && (
                <div className="tester-source-status-actions">
                  <button type="button" className="tester-ghost-button" onClick={requestBackfillCancel}>Cancel</button>
                </div>
              )}
              {backfillJob.error ? <p>{backfillJob.error}</p> : null}
            </div>
          </div>
        )}
        {backtestJob && (
          <div className="tester-card-section">
            <div className="tester-section-title">Backtest Job</div>
            <div className={`tester-source-status ${backtestJob.status === 'failed' ? 'error' : backtestJob.status === 'completed' ? 'ready' : backtestJob.status === 'cancelled' ? '' : 'loading'}`}>
              <strong>{getJobStageLabel(backtestJob)}</strong>
              <p>{formatSymbolDisplayName(backtestJob.symbol)} {backtestJobTimeframeLabel} {Math.round(backtestJob.progress || 0)}%</p>
              <div className="tester-source-meta">
                <span>{backtestJob.processedItems || 0} candles</span>
                {historyQualityBadge && (
                  <span
                    className={`tester-history-quality-badge ${historyQualityBadge.className}`}
                    title={historyQualityBadge.title}
                  >
                    {historyQualityBadge.label}
                  </span>
                )}
                {dataConfidenceBadge && (
                  <span
                    className={`tester-history-quality-badge ${dataConfidenceBadge.className}`}
                    title={dataConfidenceBadge.title}
                  >
                    {dataConfidenceBadge.label}
                  </span>
                )}
                <span>
                  {backtestJob.currentStep?.stage
                    ? `${backtestJob.currentStep.stage}${backtestJob.currentStep.totalCandles ? ` ${backtestJob.currentStep.loadedCandles || 0}/${backtestJob.currentStep.totalCandles}` : ''}`
                    : formatMarketTimestamp(backtestJob.completedAt || backtestJob.startedAt || backtestJob.createdAt)}
                </span>
              </div>
              {hasActiveBacktestJob && (
                <div className="tester-source-status-actions">
                  <button type="button" className="tester-ghost-button" onClick={requestBacktestCancel}>Cancel</button>
                </div>
              )}
              {backtestJob.error ? <p>{backtestJob.error}</p> : null}
              {backtestJob.status === 'failed' && /data quality|Backtest blocked/i.test(backtestJob.error || '') && (
                <div className="tester-source-status-actions">
                  <button
                    type="button"
                    className="tester-ghost-button"
                    onClick={() => startRun({ acknowledgeDataQuality: true })}
                  >
                    Run anyway
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="tester-card-section">
          <div className="tester-section-title">Market</div>
          <label className="tester-field"><span>Class</span><select value={setup.assetClass} onChange={(event) => updateSetup('assetClass', event.target.value)}>{Object.keys(SYMBOL_MAP).map((key) => <option key={key} value={key}>{key}</option>)}</select></label>
          <label className="tester-field"><span>Symbol</span><select value={setup.symbol} onChange={(event) => updateSetup('symbol', event.target.value)}>{symbolOptions.map((symbol) => <option key={symbol} value={symbol}>{providerSymbolLabelByValue.get(symbol) || formatSymbolDisplayName(symbol)}</option>)}</select></label>
          <div className="tester-split-grid">
            <label className="tester-field"><span>Timeframe</span><select value={setup.timeframe} onChange={(event) => updateSetup('timeframe', event.target.value)}>{TIMEFRAME_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            <label className="tester-field"><span>Style</span><select value={setup.chartType} onChange={(event) => updateSetup('chartType', event.target.value)}><option value="Candles">Candles</option><option value="Line">Line</option><option value="Bars">Bars</option></select></label>
            <label className="tester-field">
              <span>Model</span>
              <select value={setup.modelingMode} onChange={(event) => updateSetup('modelingMode', event.target.value)}>
                <option value="open_prices">Open Prices Only</option>
                <option value="minute_ohlc">1-Minute OHLC</option>
              </select>
            </label>
          </div>
        </div>
        <div className="tester-card-section">
          <div className="tester-section-title">Range</div>
          <div className="tester-split-grid">
            <label className="tester-field"><span>From</span><input type="date" value={setup.from} onChange={(event) => updateSetup('from', event.target.value)} /></label>
            <label className="tester-field"><span>To</span><input type="date" value={setup.to} onChange={(event) => updateSetup('to', event.target.value)} /></label>
          </div>
        </div>
        <div className="tester-card-section">
          <div className="tester-section-title">Risk</div>
          <div className="tester-split-grid">
            <label className="tester-field"><span>Balance <em className="tester-unit">USD</em></span><input type="number" value={setup.initialBalance} onChange={(event) => updateSetup('initialBalance', event.target.value)} /></label>
            <label className="tester-field"><span>Commission <em className="tester-unit">USD / lot</em><InfoTip text="Round-turn commission charged per lot. This is broker/account-tier specific and is NOT part of MT5 symbol data, so it can't auto-fill per symbol — set it manually (often 0 for synthetic indices)." /></span><input type="number" step="0.1" value={setup.commission} onChange={(event) => updateSetup('commission', event.target.value)} /></label>
            <label className="tester-field"><span>Slippage <em className="tester-unit">points</em></span><input type="number" step="0.1" value={setup.slippage} onChange={(event) => updateSetup('slippage', event.target.value)} /></label>
          </div>
        </div>
        {marketData?.profile?.instrumentSpec && (() => {
          const spec = marketData.profile.instrumentSpec;
          const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
          const show = (v, suffix = '') => (num(v) === null ? '—' : `${num(v)}${suffix}`);
          const margin = num(spec.marginInitialPerLot) ?? num(spec.marginRequiredPerLot) ?? num(spec.marginPerLot);
          return (
            <div className="tester-card-section">
              <div className="tester-section-title">
                Symbol Spec{spec.specConfidence ? ` · ${String(spec.specConfidence).replace(/_/g, ' ')}` : ''}
              </div>
              <p className="tester-section-note">
                Broker symbol data for {setup.symbol}{spec.providerSymbol ? ` (${spec.providerSymbol})` : ''} — used by the backtest for margin, lot limits, stop/freeze levels and swaps. Commission is broker/account-specific (not in symbol data), so set it manually above.
              </p>
              <div className="tester-split-grid tester-spec-readout">
                <div className="tester-field"><span>Contract Size</span><strong>{show(spec.contractSize)}</strong></div>
                <div className="tester-field"><span>Margin / Lot <em className="tester-unit">USD</em></span><strong>{margin === null ? '—' : margin.toFixed(2)}</strong></div>
                <div className="tester-field"><span>Lot Min / Step / Max</span><strong>{show(spec.minLot)} / {show(spec.lotStep)} / {show(spec.maxLot)}</strong></div>
                <div className="tester-field"><span>Stop / Freeze <em className="tester-unit">pts</em></span><strong>{show(spec.stopLevelPoints)} / {show(spec.freezeLevelPoints)}</strong></div>
                <div className="tester-field"><span>Swap Long / Short</span><strong>{show(spec.swapLongPerLot)} / {show(spec.swapShortPerLot)}</strong></div>
                <div className="tester-field">
                  <span>Spread <em className="tester-unit">pts</em><InfoTip text="Spread sampled from Deriv's live bid/ask for this symbol (Deriv's historical feed carries no spread). Used as the default per-bar spread in the backtest. Refresh samples again live; markets that are closed keep their last value." /></span>
                  <strong>{num(marketData?.profile?.sampledSpreadPoints) === null ? 'auto' : num(marketData.profile.sampledSpreadPoints)}</strong>
                </div>
              </div>
              <div className="inline-actions" style={{ marginTop: 6 }}>
                <button
                  type="button"
                  className="ghost-button compact-button"
                  disabled={spreadSampling}
                  onClick={async () => {
                    setSpreadSampling(true);
                    setSpreadNotice('');
                    try {
                      const result = await sampleSymbolSpreads();
                      setSpreadNotice(result.sampled ? `Sampled ${result.sampled} symbol spread${result.sampled === 1 ? '' : 's'} (run a backtest to apply).` : 'No open markets to sample right now — try when markets are open.');
                    } catch (error) {
                      setSpreadNotice(error.message || 'Spread sampling failed.');
                    } finally {
                      setSpreadSampling(false);
                    }
                  }}
                >
                  {spreadSampling ? 'Sampling live spreads…' : 'Refresh live spreads'}
                </button>
                {spreadNotice ? <span className="muted-text" style={{ fontSize: '0.76rem' }}>{spreadNotice}</span> : null}
              </div>
            </div>
          );
        })()}
        <div className="tester-card-section">
          <div className="tester-section-title">Session</div>
          <div className="tester-chip-row">
            {SESSION_OPTIONS.map((session) => (
              <button key={session.id} type="button" className={`tester-chip ${sessions.includes(session.id) ? 'active' : ''}`} onClick={() => toggleSession(session.id)}>
                {session.label}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  function renderIndicatorsTab() {
    // MT5-style "apply to": MA types can run over an oscillator's data and render inside that
    // oscillator's subwindow instead of the price chart.
    const APPLY_TO_MA_TYPES = new Set(['EMA', 'SMA', 'WMA', 'LWMA', 'SMMA']);
    const oscillatorTargets = indicators.filter((candidate) => INDICATOR_LIBRARY[candidate.type]?.area === 'oscillator');
    return (
      <>
        <div className="tester-card-section">
          <div className="tester-section-title">Indicators</div>
          <div className="tester-indicator-list">
            {indicators.map((indicator) => {
              const appliedTarget = indicators.find((candidate) => candidate.id === indicator.applyTo);
              return (
              <div key={indicator.id} className="tester-indicator-card">
                <div className="tester-indicator-head">
                  <div className="tester-indicator-name">
                    <span className="tester-indicator-dot" style={{ backgroundColor: indicator.color }}></span>
                    {INDICATOR_LIBRARY[indicator.type].label}
                    {appliedTarget ? <span className="tester-indicator-applied"> on {INDICATOR_LIBRARY[appliedTarget.type]?.label || appliedTarget.type}</span> : null}
                  </div>
                  <button type="button" className="tester-icon-button" onClick={() => removeIndicator(indicator.id)}>x</button>
                </div>
                <div className="tester-inline-grid">
                  {INDICATOR_LIBRARY[indicator.type].params.map((param) => (
                    <label key={param.key} className="tester-field tester-field-compact"><span>{param.label}</span><input type="number" inputMode="decimal" value={indicator.params[param.key]} onChange={(event) => updateIndicator(indicator.id, param.key, event.target.value)} /></label>
                  ))}
                  {APPLY_TO_MA_TYPES.has(indicator.type) && oscillatorTargets.length > 0 && (
                    <label className="tester-field tester-field-compact">
                      <span>Apply to</span>
                      <select
                        value={indicator.applyTo && oscillatorTargets.some((candidate) => candidate.id === indicator.applyTo) ? indicator.applyTo : 'price'}
                        onChange={(event) => setIndicatorApplyTo(indicator.id, event.target.value)}
                      >
                        <option value="price">Price (main chart)</option>
                        {oscillatorTargets.map((candidate) => (
                          <option key={candidate.id} value={candidate.id}>
                            {INDICATOR_LIBRARY[candidate.type]?.label || candidate.type} subwindow
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
        <button type="button" className="tester-add-button" onClick={() => setModalOpen(true)}>Add Indicator</button>
      </>
    );
  }

  function renderRulesTab() {
    const hasEntryModel = rules.indicatorEntriesEnabled || rules.rangeBreakoutEnabled || rules.gridEnabled
      || rules.structuralBreakoutEnabled || rules.candlePatternEntryEnabled || rules.patternEntryEnabled
      || rules.scriptEnabled;
    const timeExitHourLabel = rules.timezoneMode === 'broker'
      ? 'Exit Hour (Broker Time)'
      : rules.timezoneMode === 'custom'
        ? 'Exit Hour (Strategy Time)'
        : 'Exit Hour (UTC)';

    function renderOperandSelect(value, onChange, ariaLabel, disabled = false) {
      return (
        <select value={value} onChange={onChange} aria-label={ariaLabel} disabled={disabled}>
          {Object.entries(operandOptionGroups).map(([group, options]) => (
            <optgroup key={group} label={group}>
              {options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </optgroup>
          ))}
        </select>
      );
    }

    function renderShiftInput(value, onChange, ariaLabel, disabled = false) {
      return (
        <input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={onChange}
          aria-label={ariaLabel}
          disabled={disabled}
        />
      );
    }

    function renderConditionGroup(title, side, modeField, conditions) {
      return (
        <div className="tester-card-section">
          <div className="tester-section-head">
            <button
              type="button"
              className="tester-section-title tester-collapse-toggle"
              onClick={() => setConditionCollapsed((prev) => ({ ...prev, [side]: !prev[side] }))}
              aria-expanded={!conditionCollapsed[side]}
            >
              {conditionCollapsed[side] ? <FiChevronRight /> : <FiChevronDown />} {title} <span className="tester-collapse-count">({conditions.length})</span>
            </button>
            <div className="inline-actions">
              <button
                type="button"
                className={`ghost-button compact-button tester-capture-btn ${captureTarget === `conditions:${side}` ? 'is-active' : ''}`}
                onClick={() => setCaptureTarget((value) => (value === `conditions:${side}` ? '' : `conditions:${side}`))}
                disabled={!rules.indicatorEntriesEnabled}
                title="Drag a candle range on the chart to auto-build conditions from price/indicator behaviour"
              >
                {captureTarget === `conditions:${side}` ? 'Drag on chart…' : 'Capture from chart'}
              </button>
            </div>
          </div>
          {!conditionCollapsed[side] && (
          <>
          <label className="tester-field tester-field-compact">
            <span>Match</span>
            <select value={rules[modeField]} onChange={(event) => updateRule(modeField, event.target.value)} disabled={!rules.indicatorEntriesEnabled}>
              <option value="all">All conditions</option>
              <option value="any">Any condition</option>
            </select>
          </label>
          <div className="tester-rule-list">
            {conditions.map((condition) => (
              <div key={condition.id} className="tester-rule-card tester-rule-condition-card">
                <div className="tester-rule-condition-head">
                  <strong>{title} condition</strong>
                  <button
                    type="button"
                    className="tester-icon-button"
                    onClick={() => removeRuleCondition(side, condition.id)}
                    disabled={conditions.length === 1 || !rules.indicatorEntriesEnabled}
                    aria-label={`Remove ${title.toLowerCase()} condition`}
                  >
                    <FiTrash2 />
                  </button>
                </div>
                <div className="tester-rule-grid tester-rule-grid-4">
                  <label className="tester-field tester-field-compact">
                    <span>Timeframe</span>
                    <select
                      value={condition.timeframe || 'chart'}
                      onChange={(event) => updateRuleCondition(side, condition.id, 'timeframe', event.target.value)}
                      disabled={!rules.indicatorEntriesEnabled}
                    >
                      {conditionTimeframeOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </label>
                  <label className="tester-field tester-field-compact">
                    <span>Left</span>
                    {renderOperandSelect(
                      condition.left,
                      (event) => updateRuleCondition(side, condition.id, 'left', event.target.value),
                      `${title} left operand`,
                      !rules.indicatorEntriesEnabled,
                    )}
                  </label>
                  <label className="tester-field tester-field-compact">
                    <span>Left Shift</span>
                    {renderShiftInput(
                      condition.leftShift ?? '0',
                      (event) => updateRuleCondition(side, condition.id, 'leftShift', event.target.value),
                      `${title} left shift`,
                      !rules.indicatorEntriesEnabled,
                    )}
                  </label>
                  <label className="tester-field tester-field-compact">
                    <span>Signal</span>
                    <select value={condition.operator} onChange={(event) => updateRuleCondition(side, condition.id, 'operator', event.target.value)} disabled={!rules.indicatorEntriesEnabled}>
                      {RULE_OPERATOR_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </label>
                </div>
                <div className="tester-rule-grid tester-rule-grid-4">
                  <label className="tester-field tester-field-compact">
                    <span>Target</span>
                    <select value={condition.rightMode} onChange={(event) => updateRuleCondition(side, condition.id, 'rightMode', event.target.value)} disabled={!rules.indicatorEntriesEnabled}>
                      {RULE_TARGET_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </label>
                  {condition.rightMode === 'value' ? (
                    <label className="tester-field tester-field-compact">
                      <span>Value</span>
                      <input type="number" step="0.01" value={condition.rightValue} onChange={(event) => updateRuleCondition(side, condition.id, 'rightValue', event.target.value)} disabled={!rules.indicatorEntriesEnabled} />
                    </label>
                  ) : (
                    <label className="tester-field tester-field-compact">
                      <span>Right</span>
                      {renderOperandSelect(
                        condition.rightOperand,
                        (event) => updateRuleCondition(side, condition.id, 'rightOperand', event.target.value),
                        `${title} right operand`,
                        !rules.indicatorEntriesEnabled,
                      )}
                    </label>
                  )}
                  {condition.rightMode === 'operand' && (
                    <label className="tester-field tester-field-compact">
                      <span>Right Shift</span>
                      {renderShiftInput(
                        condition.rightShift ?? '0',
                        (event) => updateRuleCondition(side, condition.id, 'rightShift', event.target.value),
                        `${title} right shift`,
                        !rules.indicatorEntriesEnabled,
                      )}
                    </label>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              className="tester-add-condition-row"
              onClick={() => addRuleCondition(side)}
              aria-label={`Add ${title.toLowerCase()} condition`}
              disabled={!rules.indicatorEntriesEnabled}
            >
              <FiPlus /> Add condition
            </button>
          </div>
          </>
          )}
        </div>
      );
    }

    const ruleComponentCatalog = [
      { group: 'Entry Models', items: [
        { flag: 'indicatorEntriesEnabled', label: 'Indicator Conditions' },
        { flag: 'rangeBreakoutEnabled', label: 'Range Breakout' },
        { flag: 'structuralBreakoutEnabled', label: 'Structural Breakout' },
        { flag: 'candlePatternEntryEnabled', label: 'Candle Patterns' },
        { flag: 'patternEntryEnabled', label: 'Pattern by Example' },
        { flag: 'scriptEnabled', label: 'VixScript' },
        { flag: 'gridEnabled', label: 'Grid / DCA' },
      ] },
      { group: 'Filters', items: [
        { flag: 'trendFilterEnabled', label: 'Trend Filter' },
        { flag: 'spreadFilterEnabled', label: 'Spread Filter' },
        { flag: 'timedEntryEnabled', label: 'Timed Entry' },
        { flag: 'newsFilterEnabled', label: 'News Filter' },
      ] },
      { group: 'Orders & Overlays', items: [
        { flag: 'pendingOrderEnabled', label: 'Pending Orders' },
        { flag: 'martingaleEnabled', label: 'Martingale' },
        { flag: 'antiMartingaleEnabled', label: 'Anti-Martingale' },
        { flag: 'basketTpEnabled', label: 'Basket TP/SL' },
        { flag: 'timeExitEnabled', label: 'Time Exit' },
        { flag: 'equityProtectionEnabled', label: 'Equity Protection' },
        { flag: 'breakevenOnlyEnabled', label: 'Breakeven Only' },
      ] },
    ];

    return (
      <>
        <div className="tester-card-section">
          <div className="tester-section-title">Strategy Components</div>
          <p className="tester-section-note">Add only the features your strategy needs — entry models combine, filters restrict, overlays manage risk.</p>
          {ruleComponentCatalog.map(({ group, items }) => (
            <div key={group} className="tester-component-group">
              <span className="tester-component-group-label">{group}</span>
              <div className="tester-chip-row">
                {items.map(({ flag, label }) => (
                  <button
                    key={flag}
                    type="button"
                    className={`tester-chip ${rules[flag] ? 'active' : ''}`}
                    onClick={() => updateRule(flag, !rules[flag])}
                  >
                    {rules[flag] ? '✓ ' : '+ '}{label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!hasEntryModel ? (
            <div className="tester-source-status error tester-rule-warning">
              <strong>No entry model enabled</strong>
              <p>Add an entry model above (Indicator Conditions, Range Breakout, Structural Breakout, Candle Patterns, or Grid). With none, the tester and exported EA have no entry signal.</p>
            </div>
          ) : null}
        </div>
        <div className="tester-card-section">
          <div className="tester-section-title">Time Zone</div>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field">
              <span>Strategy Clock</span>
              <select value={rules.timezoneMode} onChange={(event) => updateRule('timezoneMode', event.target.value)}>
                {STRATEGY_TIMEZONE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
            </label>
            {rules.timezoneMode === 'broker' ? (
              <label className="tester-field">
                <span>Broker UTC Offset <em className="tester-unit">hours</em></span>
                <input type="number" step="0.5" value={rules.brokerUtcOffsetHours} onChange={(event) => updateRule('brokerUtcOffsetHours', event.target.value)} />
              </label>
            ) : null}
            {rules.timezoneMode === 'custom' ? (
              <>
                <label className="tester-field">
                  <span>Strategy UTC Offset <em className="tester-unit">hours</em></span>
                  <input type="number" step="0.5" value={rules.customUtcOffsetHours} onChange={(event) => updateRule('customUtcOffsetHours', event.target.value)} />
                </label>
                <label className="tester-field">
                  <span>Broker UTC Offset <em className="tester-unit">hours</em></span>
                  <input type="number" step="0.5" value={rules.brokerUtcOffsetHours} onChange={(event) => updateRule('brokerUtcOffsetHours', event.target.value)} />
                </label>
              </>
            ) : null}
          </div>
        </div>
        {rules.rangeBreakoutEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Range Breakout</div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field">
                <span>Breakout Side</span>
                <select value={rules.rangeBreakoutSide} onChange={(event) => updateRule('rangeBreakoutSide', event.target.value)}>
                  <option value="both">Both</option>
                  <option value="long">Long Only</option>
                  <option value="short">Short Only</option>
                </select>
              </label>
              <label className="tester-field"><span>Range Start Hour</span><input type="number" min="0" max="23" value={rules.rangeStartHour} onChange={(event) => updateRule('rangeStartHour', event.target.value)} /></label>
              <label className="tester-field"><span>Range Start Minute</span><input type="number" min="0" max="59" value={rules.rangeStartMinute} onChange={(event) => updateRule('rangeStartMinute', event.target.value)} /></label>
              <label className="tester-field"><span>Range End Hour</span><input type="number" min="0" max="23" value={rules.rangeEndHour} onChange={(event) => updateRule('rangeEndHour', event.target.value)} /></label>
              <label className="tester-field"><span>Range End Minute</span><input type="number" min="0" max="59" value={rules.rangeEndMinute} onChange={(event) => updateRule('rangeEndMinute', event.target.value)} /></label>
              <label className="tester-field"><span>Breakout Buffer <em className="tester-unit">points</em></span><input type="number" step="0.1" value={rules.rangeBreakoutBuffer} onChange={(event) => updateRule('rangeBreakoutBuffer', event.target.value)} /></label>
              <label className="tester-field tester-field-checkbox"><span>Trade Range Once <em className="tester-unit">per range</em><InfoTip text="Trade each timed range only once — after price breaks it, wait for the next range. Off = the range can be traded repeatedly as price re-crosses the boundary." /></span><input type="checkbox" checked={rules.rangeBreakoutOnce !== false} onChange={(event) => updateRule('rangeBreakoutOnce', event.target.checked)} /></label>
            </div>
          </div>
        )}
        {rules.structuralBreakoutEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Structural Breakout</div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field">
                <span>Reference<InfoTip text="The structure price must break to enter: Previous Day/Week High-Low (yesterday's or last week's range), Donchian Channel (highest high / lowest low of N bars), or ATR Volatility (a band N×ATR above/below the prior close)." /></span>
                <select value={rules.structuralBreakoutRef} onChange={(event) => updateRule('structuralBreakoutRef', event.target.value)}>
                  <option value="prevDayHL">Previous Day High/Low</option>
                  <option value="prevWeekHL">Previous Week High/Low</option>
                  <option value="donchian">Donchian Channel (N bars)</option>
                  <option value="atrVolatility">ATR Volatility</option>
                </select>
              </label>
              <label className="tester-field">
                <span>Side</span>
                <select value={rules.structuralBreakoutSide} onChange={(event) => updateRule('structuralBreakoutSide', event.target.value)}>
                  <option value="both">Both</option>
                  <option value="long">Long Only</option>
                  <option value="short">Short Only</option>
                </select>
              </label>
              <label className="tester-field"><span>Buffer <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.structuralBreakoutBuffer} onChange={(event) => updateRule('structuralBreakoutBuffer', event.target.value)} /></label>
              <label className="tester-field tester-field-checkbox"><span>Trade Level Once <em className="tester-unit">per period</em><InfoTip text="Trade each structural level only once per period (per day, or per week for Previous Week H/L) instead of re-arming after a trade closes. Off = the level can trade again the same period." /></span><input type="checkbox" checked={rules.structuralBreakoutOnce === true} onChange={(event) => updateRule('structuralBreakoutOnce', event.target.checked)} /></label>
              {rules.structuralBreakoutRef === 'donchian' && (
                <label className="tester-field"><span>Donchian Period <em className="tester-unit">bars</em></span><input type="number" min="2" value={rules.donchianPeriod} onChange={(event) => updateRule('donchianPeriod', event.target.value)} /></label>
              )}
              {rules.structuralBreakoutRef === 'atrVolatility' && (
                <>
                  <label className="tester-field"><span>ATR Period <em className="tester-unit">bars</em></span><input type="number" min="2" value={rules.atrBreakoutPeriod} onChange={(event) => updateRule('atrBreakoutPeriod', event.target.value)} /></label>
                  <label className="tester-field"><span>ATR Multiple <em className="tester-unit">×</em></span><input type="number" min="0" step="0.1" value={rules.atrBreakoutMultiple} onChange={(event) => updateRule('atrBreakoutMultiple', event.target.value)} /></label>
                </>
              )}
            </div>
          </div>
        )}
        {rules.candlePatternEntryEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Candle Patterns</div>
            <label className="tester-field"><span>Side</span>
              <select value={rules.candlePatternSide} onChange={(event) => updateRule('candlePatternSide', event.target.value)}>
                <option value="both">Both</option>
                <option value="long">Long Only</option>
                <option value="short">Short Only</option>
              </select>
            </label>
            <div className="tester-checkbox-row">
              {[['pinBarBull', 'Pin Bar (Bull)'], ['pinBarBear', 'Pin Bar (Bear)'], ['engulfBull', 'Engulfing (Bull)'], ['engulfBear', 'Engulfing (Bear)'], ['hammer', 'Hammer'], ['shootingStar', 'Shooting Star'], ['morningStar', 'Morning Star'], ['eveningStar', 'Evening Star']].map(([id, label]) => (
                <label key={id} className="tester-field tester-field-checkbox tester-field-inline">
                  <span>{label}</span>
                  <input type="checkbox"
                    checked={(rules.candlePatternList || []).includes(id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...(rules.candlePatternList || []), id]
                        : (rules.candlePatternList || []).filter((v) => v !== id);
                      updateRule('candlePatternList', next);
                    }} />
                </label>
              ))}
            </div>
          </div>
        )}
        {rules.patternEntryEnabled && (() => {
          const longCount = (rules.patternLongTemplates || []).length;
          const shortCount = (rules.patternShortTemplates || []).length;
          const longBtn = captureTarget === 'pattern:long' ? 'Drag a long example on the chart…' : 'Add long example';
          const shortBtn = captureTarget === 'pattern:short' ? 'Drag a short example on the chart…' : 'Add short example';
          return (
            <div className="tester-card-section">
              <div className="tester-section-title">Pattern by Example</div>
              <p className="muted-text tester-pattern-hint">
                Drag a rectangle over the candles that define a pattern (entry fires on the last selected bar). Add several examples per side for a more robust match — a window triggers if it matches ANY example.
              </p>
              <label className="tester-field"><span>Match on</span>
                <select value={rules.patternMatchMode} onChange={(event) => updateRule('patternMatchMode', event.target.value)}>
                  <option value="close">Close shape</option>
                  <option value="ohlc">Candle shape (OHLC)</option>
                </select>
              </label>
              <label className="tester-field"><span>Similarity</span>
                <select value={rules.patternSimilarity} onChange={(event) => updateRule('patternSimilarity', event.target.value)}>
                  <option value="correlation">Correlation (fixed length)</option>
                  <option value="dtw">DTW (time-warped)</option>
                </select>
              </label>
              <div className="tester-pattern-grid">
                <button
                  type="button"
                  className={`ghost-button compact-button tester-pattern-select-btn long ${captureTarget === 'pattern:long' ? 'is-active' : ''}`}
                  onClick={() => setCaptureTarget((value) => (value === 'pattern:long' ? '' : 'pattern:long'))}
                >
                  {longBtn}
                </button>
                <button
                  type="button"
                  className={`ghost-button compact-button tester-pattern-select-btn short ${captureTarget === 'pattern:short' ? 'is-active' : ''}`}
                  onClick={() => setCaptureTarget((value) => (value === 'pattern:short' ? '' : 'pattern:short'))}
                >
                  {shortBtn}
                </button>
              </div>
              <p className="muted-text tester-pattern-counts">Long examples: {longCount} · Short examples: {shortCount}</p>
              {(longCount > 0 || shortCount > 0) && (
                <div className="tester-pattern-grid">
                  <button type="button" className="tester-pattern-clear" disabled={!longCount}
                    onClick={() => setRules((previous) => ({ ...previous, patternLongTemplates: [] }))}>Clear long</button>
                  <button type="button" className="tester-pattern-clear" disabled={!shortCount}
                    onClick={() => setRules((previous) => ({ ...previous, patternShortTemplates: [] }))}>Clear short</button>
                </div>
              )}
              <label className="tester-field">
                <span>Match threshold <em className="tester-unit">{Number(rules.patternThreshold).toFixed(2)}</em></span>
                <input
                  type="range" min="0.5" max="0.99" step="0.01"
                  value={rules.patternThreshold}
                  onChange={(event) => updateRule('patternThreshold', event.target.value)}
                />
              </label>
            </div>
          );
        })()}
        {(rules.trendFilterEnabled || rules.spreadFilterEnabled || rules.timedEntryEnabled) && (
          <div className="tester-card-section">
            <div className="tester-section-title">Filters</div>
            <div className="tester-rule-grid tester-rule-grid-2">
              {rules.trendFilterEnabled && (
                <>
                  <label className="tester-field"><span>Trend Timeframe</span>
                    <select value={rules.trendFilterTimeframe} onChange={(e) => updateRule('trendFilterTimeframe', e.target.value)}>
                      {['M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'].map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                    </select>
                  </label>
                  <label className="tester-field"><span>Trend MA</span>
                    <select value={rules.trendFilterMaType} onChange={(e) => updateRule('trendFilterMaType', e.target.value)}>
                      <option value="EMA">EMA</option>
                      <option value="SMA">SMA</option>
                    </select>
                  </label>
                  <label className="tester-field"><span>Trend MA Period</span><input type="number" min="1" value={rules.trendFilterMaPeriod} onChange={(e) => updateRule('trendFilterMaPeriod', e.target.value)} /></label>
                  <label className="tester-field"><span>Trend Mode</span>
                    <select value={rules.trendFilterMode} onChange={(e) => updateRule('trendFilterMode', e.target.value)}>
                      <option value="slope">MA Slope (rising/falling)</option>
                      <option value="price">Price vs MA</option>
                    </select>
                  </label>
                </>
              )}
              {rules.spreadFilterEnabled && (
                <label className="tester-field"><span>Max Spread <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.maxSpreadPoints} onChange={(e) => updateRule('maxSpreadPoints', e.target.value)} /></label>
              )}
              {rules.timedEntryEnabled && (
                <>
                  <label className="tester-field"><span>Entry Start Hour</span><input type="number" min="0" max="23" value={rules.timedEntryStartHour} onChange={(e) => updateRule('timedEntryStartHour', e.target.value)} /></label>
                  <label className="tester-field"><span>Entry Start Minute</span><input type="number" min="0" max="59" value={rules.timedEntryStartMinute} onChange={(e) => updateRule('timedEntryStartMinute', e.target.value)} /></label>
                  <label className="tester-field"><span>Entry End Hour</span><input type="number" min="0" max="23" value={rules.timedEntryEndHour} onChange={(e) => updateRule('timedEntryEndHour', e.target.value)} /></label>
                  <label className="tester-field"><span>Entry End Minute</span><input type="number" min="0" max="59" value={rules.timedEntryEndMinute} onChange={(e) => updateRule('timedEntryEndMinute', e.target.value)} /></label>
                </>
              )}
            </div>
          </div>
        )}
        <div className="tester-card-section">
          <div className="tester-section-title">Day Filter</div>
          <div className="tester-chip-row">
            {DAY_FILTER_OPTIONS.map((day) => (
              <button
                key={day.id}
                type="button"
                className={`tester-chip ${rules.dayFilters.includes(day.id) ? 'active' : ''}`}
                onClick={() => toggleRuleDay(day.id)}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
        {rules.indicatorEntriesEnabled && renderConditionGroup('Long', 'long', 'longMode', rules.longConditions)}
        {rules.indicatorEntriesEnabled && renderConditionGroup('Short', 'short', 'shortMode', rules.shortConditions)}
        {rules.scriptEnabled && (() => {
          const scriptText = String(rules.scriptSource || '');
          const compiled = scriptText.trim() ? compileVixScript(scriptText) : null;
          return (
            <div className="tester-card-section">
              <div className="tester-section-title">VixScript<InfoTip text="Runs once per closed signal candle. buy()/sell() open at the next bar open through the normal entry gate (filters and trade limits still apply); state.x persists across bars. Scripts backtest with full realism and export to MQ5 — indicators referenced with ind() must be added to the chart, and DEMA/TEMA/TRIX/Donchian/Keltner/Swing are not scriptable in exports yet." /></div>
              <textarea
                className="tester-script-editor"
                rows={12}
                spellCheck={false}
                value={scriptText}
                onChange={(event) => updateRule('scriptSource', event.target.value)}
                placeholder={"if (crossover('close', 'ma_default')) {\n  buy()\n}\nif (crossunder('close', 'ma_default')) {\n  sell()\n}"}
              />
              <p className={`tester-script-status ${!compiled ? '' : compiled.ok ? 'is-ok' : 'is-error'}`}>
                {!compiled ? 'Empty script — nothing will run.' : compiled.ok ? '✓ Script parses cleanly.' : `✗ ${compiled.error}`}
              </p>
            </div>
          );
        })()}
        <div className="tester-card-section">
          <div className="tester-section-title">Trade Management</div>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field"><span>Lot Size <em className="tester-unit">lots</em></span><input type="number" step="0.01" value={setup.lotSize} onChange={(event) => updateSetup('lotSize', event.target.value)} /></label>
            <label className="tester-field"><span>Stop Loss <em className="tester-unit">points</em></span><input type="number" value={rules.stopLoss} onChange={(event) => updateRule('stopLoss', event.target.value)} /></label>
            <label className="tester-field"><span>Take Profit <em className="tester-unit">points</em></span><input type="number" value={rules.takeProfit} onChange={(event) => updateRule('takeProfit', event.target.value)} /></label>
            <label className="tester-field"><span>Trail Stop <em className="tester-unit">points</em></span><input type="number" value={rules.trailingStop} onChange={(event) => updateRule('trailingStop', event.target.value)} /></label>
            <label className="tester-field"><span>Breakeven Trigger <em className="tester-unit">points</em></span><input type="number" value={rules.breakEvenTrigger} onChange={(event) => updateRule('breakEvenTrigger', event.target.value)} /></label>
            <label className="tester-field"><span>Breakeven Offset <em className="tester-unit">points</em></span><input type="number" value={rules.breakEvenOffset} onChange={(event) => updateRule('breakEvenOffset', event.target.value)} /></label>
            <label className="tester-field tester-field-checkbox"><span>Close On Opposite</span><input type="checkbox" checked={rules.closeOnOpposite} onChange={(event) => updateRule('closeOnOpposite', event.target.checked)} /></label>
            <label className="tester-field"><span>Max Open Trades</span><input type="number" min="1" step="1" value={rules.maxOpenTrades} onChange={(event) => updateRule('maxOpenTrades', event.target.value)} /></label>
            <label className="tester-field tester-field-checkbox"><span>Breakeven Only <em className="tester-unit">no TP</em></span><input type="checkbox" checked={rules.breakevenOnlyEnabled} onChange={(event) => updateRule('breakevenOnlyEnabled', event.target.checked)} /></label>
            <label className="tester-field tester-field-checkbox">
              <span>Structure Trail<InfoTip text="Trail the stop to the most recent swing low (long) or swing high (short) over the lookback window, instead of a fixed points distance. The stop only ever ratchets in your favour. Exports to MQ5 using iLowest/iHighest over the same lookback." /></span>
              <input type="checkbox" checked={rules.structureTrailingEnabled} onChange={(event) => updateRule('structureTrailingEnabled', event.target.checked)} />
            </label>
            {rules.structureTrailingEnabled && (
              <>
                <label className="tester-field"><span>Swing Lookback <em className="tester-unit">bars</em></span><input type="number" min="1" step="1" value={rules.structureTrailingLookback} onChange={(event) => updateRule('structureTrailingLookback', event.target.value)} /></label>
                <label className="tester-field"><span>Structure Buffer <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.structureTrailingBuffer} onChange={(event) => updateRule('structureTrailingBuffer', event.target.value)} /></label>
              </>
            )}
            <label className="tester-field tester-field-checkbox">
              <span>Partial TP<InfoTip text="Close part of the position at TP1 (a nearer target), banking profit while the rest runs to the full Take Profit / stop / trailing. Optionally move the stop to breakeven after the partial. Exports to MQ5 via PositionClosePartial." /></span>
              <input type="checkbox" checked={rules.partialTpEnabled} onChange={(event) => updateRule('partialTpEnabled', event.target.checked)} />
            </label>
            {rules.partialTpEnabled && (
              <>
                <label className="tester-field"><span>TP1 Distance <em className="tester-unit">points</em></span><input type="number" min="1" value={rules.partialTpPoints} onChange={(event) => updateRule('partialTpPoints', event.target.value)} /></label>
                <label className="tester-field"><span>Close At TP1 <em className="tester-unit">%</em></span><input type="number" min="1" max="99" value={rules.partialTpPercent} onChange={(event) => updateRule('partialTpPercent', event.target.value)} /></label>
                <label className="tester-field tester-field-checkbox"><span>Breakeven After TP1</span><input type="checkbox" checked={rules.partialTpMoveBreakeven} onChange={(event) => updateRule('partialTpMoveBreakeven', event.target.checked)} /></label>
              </>
            )}
            {rules.timeExitEnabled && (
              <>
                <label className="tester-field"><span>{timeExitHourLabel}</span><input type="number" min="0" max="23" value={rules.timeExitHour} onChange={(event) => updateRule('timeExitHour', event.target.value)} /></label>
                <label className="tester-field"><span>Exit Minute</span><input type="number" min="0" max="59" value={rules.timeExitMinute} onChange={(event) => updateRule('timeExitMinute', event.target.value)} /></label>
              </>
            )}
          </div>
        </div>
        {rules.martingaleEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Martingale</div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field"><span>Factor <em className="tester-unit">×</em></span><input type="number" step="0.1" value={rules.martingaleFactor} onChange={(event) => updateRule('martingaleFactor', event.target.value)} /></label>
              <label className="tester-field"><span>Max Steps</span><input type="number" value={rules.martingaleMaxSteps} onChange={(event) => updateRule('martingaleMaxSteps', event.target.value)} /></label>
            </div>
          </div>
        )}
        {rules.antiMartingaleEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Anti-Martingale <em className="tester-unit">scale up on wins</em></div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field"><span>Factor <em className="tester-unit">×</em></span><input type="number" step="0.1" value={rules.antiMartingaleFactor} onChange={(event) => updateRule('antiMartingaleFactor', event.target.value)} /></label>
              <label className="tester-field"><span>Max Steps</span><input type="number" value={rules.antiMartingaleMaxSteps} onChange={(event) => updateRule('antiMartingaleMaxSteps', event.target.value)} /></label>
            </div>
          </div>
        )}
        {rules.basketTpEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Basket TP / SL <em className="tester-unit">all trades combined</em></div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field"><span>Basket TP <em className="tester-unit">USD</em></span><input type="number" min="0" value={rules.basketTpUsd} onChange={(event) => updateRule('basketTpUsd', event.target.value)} /></label>
              <label className="tester-field"><span>Basket SL <em className="tester-unit">USD</em></span><input type="number" min="0" value={rules.basketSlUsd} onChange={(event) => updateRule('basketSlUsd', event.target.value)} /></label>
            </div>
          </div>
        )}
        {rules.equityProtectionEnabled && (
          <div className="tester-card-section">
            <div className="tester-section-title">Equity Protection <em className="tester-unit">pause on drop</em></div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field"><span>Mode</span>
                <select value={rules.equityProtectionMode} onChange={(event) => updateRule('equityProtectionMode', event.target.value)}>
                  <option value="pct">% drop from peak equity</option>
                  <option value="usd">Equity floor (USD)</option>
                </select>
              </label>
              <label className="tester-field"><span>{rules.equityProtectionMode === 'usd' ? 'Equity Floor (USD)' : 'Max Drop (%)'}</span><input type="number" min="0" step="0.1" value={rules.equityProtectionValue} onChange={(event) => updateRule('equityProtectionValue', event.target.value)} /></label>
            </div>
          </div>
        )}
        {rules.pendingOrderEnabled && (
        <div className="tester-card-section">
          <div className="tester-section-title">Pending Orders</div>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field"><span>Type</span>
              <select value={rules.pendingOrderType} onChange={(event) => updateRule('pendingOrderType', event.target.value)}>
                <option value="stop">Stop (BuyStop / SellStop)</option>
                <option value="limit">Limit (BuyLimit / SellLimit)</option>
              </select>
            </label>
            <label className="tester-field"><span>Offset <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.pendingOffsetPoints} onChange={(event) => updateRule('pendingOffsetPoints', event.target.value)} /></label>
            <label className="tester-field"><span>Expiry <em className="tester-unit">bars · 0=GTC</em></span><input type="number" min="0" step="1" value={rules.pendingExpiryBars} onChange={(event) => updateRule('pendingExpiryBars', event.target.value)} /></label>
            <label className="tester-field tester-field-checkbox"><span>Delete Opposing Order After Trigger<InfoTip text="When one breakout pending order fills, immediately cancel the opposite-side pending (OCO). Turn off to keep the opposing order resting in case price reverses." /></span><input type="checkbox" checked={rules.pendingDeleteOpposite !== false} onChange={(event) => updateRule('pendingDeleteOpposite', event.target.checked)} /></label>
          </div>
        </div>
        )}
        {rules.gridEnabled && (
        <div className="tester-card-section">
          <div className="tester-section-title">Grid / DCA</div>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field"><span>Orders</span><input type="number" min="2" step="1" value={rules.gridOrderCount} onChange={(event) => updateRule('gridOrderCount', event.target.value)} /></label>
            <label className="tester-field"><span>Spacing <em className="tester-unit">points</em></span><input type="number" min="1" value={rules.gridSpacingPoints} onChange={(event) => updateRule('gridSpacingPoints', event.target.value)} /></label>
            <label className="tester-field"><span>Lot Multiplier <em className="tester-unit">×</em></span><input type="number" min="1" step="0.1" value={rules.gridLotMultiplier} onChange={(event) => updateRule('gridLotMultiplier', event.target.value)} /></label>
            <label className="tester-field"><span>Basket TP <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.gridBasketTpPoints} onChange={(event) => updateRule('gridBasketTpPoints', event.target.value)} /></label>
            <label className="tester-field"><span>Basket SL <em className="tester-unit">points</em></span><input type="number" min="0" value={rules.gridBasketSlPoints} onChange={(event) => updateRule('gridBasketSlPoints', event.target.value)} /></label>
          </div>
        </div>
        )}
        <div className="tester-card-section">
          <div className="tester-section-title">Advanced Risk</div>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field" style={{ gridColumn: 'span 2' }}><span>Position Sizing</span>
              <select value={rules.riskMode} onChange={(e) => updateRule('riskMode', e.target.value)}>
                <option value="flat_lot">Flat Lot (manual)</option>
                <option value="fixed_usd">Fixed $ Risk per Trade</option>
                <option value="pct_balance">% of Balance per Trade</option>
                <option value="pct_equity">% of Equity per Trade</option>
              </select>
            </label>
            {rules.riskMode !== 'flat_lot' && (
              <label className="tester-field" style={{ gridColumn: 'span 2' }}>
                <span>{rules.riskMode === 'fixed_usd' ? 'Risk Amount ($)' : 'Risk Amount (%)'}</span>
                <input type="number" min="0.01" step="0.01" value={rules.riskAmount} onChange={(e) => updateRule('riskAmount', e.target.value)} />
              </label>
            )}
            <label className="tester-field"><span>Daily Loss Limit</span>
              <select value={rules.maxDailyLossMode} onChange={(e) => updateRule('maxDailyLossMode', e.target.value)}>
                <option value="off">Off</option>
                <option value="usd">Fixed $ Amount</option>
                <option value="pct">% of Balance</option>
              </select>
            </label>
            {rules.maxDailyLossMode !== 'off' && (
              <label className="tester-field">
                <span>{rules.maxDailyLossMode === 'pct' ? 'Max Daily Loss (%)' : 'Max Daily Loss ($)'}</span>
                <input type="number" min="0" step="0.1" value={rules.maxDailyLossValue} onChange={(e) => updateRule('maxDailyLossValue', e.target.value)} />
              </label>
            )}
            <label className="tester-field"><span>Max Drawdown (%)</span>
              <input type="number" min="0" step="0.1" value={rules.maxDrawdownPct} onChange={(e) => updateRule('maxDrawdownPct', e.target.value)} placeholder="0 = off" />
            </label>
            <label className="tester-field"><span>Max Consec. Losses</span>
              <input type="number" min="0" step="1" value={rules.maxConsecutiveLosses} onChange={(e) => updateRule('maxConsecutiveLosses', e.target.value)} placeholder="0 = off" />
            </label>
            <label className="tester-field"><span>Cooldown <em className="tester-unit">bars</em><InfoTip text="After a trade is opened, ignore new entry signals until this many signal-timeframe bars have passed. 0 = off. Applies to all entry models." /></span>
              <input type="number" min="0" step="1" value={rules.cooldownBars} onChange={(e) => updateRule('cooldownBars', e.target.value)} placeholder="0 = off" />
            </label>
          </div>
        </div>
        {rules.newsFilterEnabled && (
        <div className="tester-card-section">
          <div className="tester-section-title">News Filter</div>
          <p className="tester-section-note">Applies to live trading only — not emulated in backtest.</p>
          <div className="tester-rule-grid tester-rule-grid-2">
            <label className="tester-field"><span>Minutes Before</span>
              <input type="number" min="0" step="1" value={rules.newsFilterMinutesBefore} onChange={(e) => updateRule('newsFilterMinutesBefore', e.target.value)} />
            </label>
            <label className="tester-field"><span>Minutes After</span>
              <input type="number" min="0" step="1" value={rules.newsFilterMinutesAfter} onChange={(e) => updateRule('newsFilterMinutesAfter', e.target.value)} />
            </label>
            <div className="tester-field" style={{ gridColumn: 'span 2' }}>
              <span>Impact Level</span>
              <div className="tester-checkbox-row">
                {['high', 'medium', 'low'].map((level) => (
                  <label key={level} className="tester-field tester-field-checkbox tester-field-inline">
                    <span style={{ textTransform: 'capitalize' }}>{level}</span>
                    <input type="checkbox"
                      checked={(rules.newsFilterImpact || []).includes(level)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(rules.newsFilterImpact || []), level]
                          : (rules.newsFilterImpact || []).filter((v) => v !== level);
                        updateRule('newsFilterImpact', next);
                      }} />
                  </label>
                ))}
              </div>
            </div>
            <div className="tester-field" style={{ gridColumn: 'span 2' }}>
              <span>Currencies</span>
              <div className="tester-checkbox-row">
                {['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD'].map((cur) => (
                  <label key={cur} className="tester-field tester-field-checkbox tester-field-inline">
                    <span>{cur}</span>
                    <input type="checkbox"
                      checked={(rules.newsFilterCurrencies || []).includes(cur)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(rules.newsFilterCurrencies || []), cur]
                          : (rules.newsFilterCurrencies || []).filter((v) => v !== cur);
                        updateRule('newsFilterCurrencies', next);
                      }} />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}
      </>
    );
  }

  function renderPanelTab() {
    const PANEL_TEMPLATES = [
      { id: 'minimal', label: 'Minimal', widgets: ['symbol_tf', 'spread'] },
      { id: 'trade_monitor', label: 'Trade Monitor', widgets: ['symbol_tf', 'spread', 'floating_pnl', 'equity', 'open_trades'] },
      { id: 'full_hud', label: 'Full HUD', widgets: ['symbol_tf', 'spread', 'floating_pnl', 'balance', 'equity', 'open_trades', 'last_trade', 'session_name', 'daily_pnl', 'news_countdown'] },
    ];
    const ALL_WIDGETS = [
      { id: 'symbol_tf', label: 'Symbol & Timeframe' },
      { id: 'spread', label: 'Spread' },
      { id: 'floating_pnl', label: 'Floating P&L' },
      { id: 'balance', label: 'Balance' },
      { id: 'equity', label: 'Equity' },
      { id: 'open_trades', label: 'Open Trades' },
      { id: 'last_trade', label: 'Last Trade Result' },
      { id: 'session_name', label: 'Current Session' },
      { id: 'daily_pnl', label: 'Daily P&L' },
      { id: 'news_countdown', label: 'Next News Event' },
    ];
    const activeWidgets = Array.isArray(rules.panelWidgets) ? rules.panelWidgets : [];
    const availableWidgets = ALL_WIDGETS.filter((w) => !activeWidgets.includes(w.id));

    function selectTemplate(tpl) {
      updateRule('panelTemplate', tpl.id);
      updateRule('panelWidgets', [...tpl.widgets]);
    }
    function addWidget(id) {
      updateRule('panelWidgets', [...activeWidgets, id]);
      updateRule('panelTemplate', 'custom');
    }
    function removeWidget(id) {
      updateRule('panelWidgets', activeWidgets.filter((w) => w !== id));
      updateRule('panelTemplate', 'custom');
    }
    function moveWidget(id, dir) {
      const idx = activeWidgets.indexOf(id);
      if (idx < 0) return;
      const next = [...activeWidgets];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      updateRule('panelWidgets', next);
      updateRule('panelTemplate', 'custom');
    }

    const posLabel = { 'top-left': 'Top Left', 'top-right': 'Top Right', 'bottom-left': 'Bottom Left', 'bottom-right': 'Bottom Right' };
    const widgetLabel = Object.fromEntries(ALL_WIDGETS.map((w) => [w.id, w.label]));
    const bgColor = rules.panelTheme === 'light' ? '#f5f5f5' : '#1a1e2a';
    const textColor = rules.panelTheme === 'light' ? '#1a1e2a' : '#e2e8f0';
    const borderColor = rules.panelTheme === 'light' ? '#d0d5e0' : '#2e3446';
    const accentColor = '#38f2c1';

    return (
      <>
        <div className="tester-card-section">
          <label className="tester-field tester-field-checkbox">
            <span>Enable Info Panel in EA</span>
            <input type="checkbox" checked={Boolean(rules.panelEnabled)} onChange={(e) => updateRule('panelEnabled', e.target.checked)} />
          </label>
        </div>
        <div className={`tester-panel-designer${rules.panelEnabled ? '' : ' tester-panel-designer--disabled'}`}>
          <div className="tester-card-section">
            <div className="tester-section-title">Template</div>
            <div className="tester-panel-template-row">
              {PANEL_TEMPLATES.map((tpl) => (
                <button key={tpl.id} type="button"
                  className={`tester-panel-tpl-btn${rules.panelTemplate === tpl.id ? ' active' : ''}`}
                  onClick={() => selectTemplate(tpl)} disabled={!rules.panelEnabled}>
                  {tpl.label}
                </button>
              ))}
              <button type="button"
                className={`tester-panel-tpl-btn${rules.panelTemplate === 'custom' ? ' active' : ''}`}
                disabled>Custom</button>
            </div>
          </div>
          <div className="tester-card-section">
            <div className="tester-section-title">Position &amp; Style</div>
            <div className="tester-rule-grid tester-rule-grid-2">
              <label className="tester-field"><span>Position</span>
                <select value={rules.panelPosition} onChange={(e) => updateRule('panelPosition', e.target.value)} disabled={!rules.panelEnabled}>
                  {Object.entries(posLabel).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </label>
              <label className="tester-field"><span>Theme</span>
                <select value={rules.panelTheme || 'dark'} onChange={(e) => updateRule('panelTheme', e.target.value)} disabled={!rules.panelEnabled}>
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </label>
            </div>
          </div>
          <div className="tester-card-section">
            <div className="tester-section-title">Widgets</div>
            <div className="tester-panel-widgets-layout">
              <div className="tester-panel-widgets-col">
                <div className="tester-panel-widgets-label">Active</div>
                {activeWidgets.length === 0 && <div className="tester-panel-empty">No widgets selected</div>}
                {activeWidgets.map((wid, idx) => (
                  <div key={wid} className="tester-panel-widget-row active">
                    <span>{widgetLabel[wid] || wid}</span>
                    <div className="tester-panel-widget-actions">
                      <button type="button" disabled={!rules.panelEnabled || idx === 0} onClick={() => moveWidget(wid, -1)}>↑</button>
                      <button type="button" disabled={!rules.panelEnabled || idx === activeWidgets.length - 1} onClick={() => moveWidget(wid, 1)}>↓</button>
                      <button type="button" disabled={!rules.panelEnabled} onClick={() => removeWidget(wid)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="tester-panel-widgets-col">
                <div className="tester-panel-widgets-label">Available</div>
                {availableWidgets.length === 0 && <div className="tester-panel-empty">All widgets added</div>}
                {availableWidgets.map((w) => (
                  <div key={w.id} className="tester-panel-widget-row">
                    <span>{w.label}</span>
                    <button type="button" disabled={!rules.panelEnabled} onClick={() => addWidget(w.id)}>+</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="tester-card-section">
            <div className="tester-section-title">Preview</div>
            <div className="tester-panel-preview-wrap">
              <div className="tester-panel-preview" style={{ background: bgColor, border: `1px solid ${borderColor}`, color: textColor }}>
                <div className="tester-panel-preview-header" style={{ borderBottom: `1px solid ${borderColor}`, color: accentColor }}>
                  Info Panel · {posLabel[rules.panelPosition] || 'Top Right'}
                </div>
                {activeWidgets.length === 0 && <div style={{ padding: '6px 10px', opacity: 0.5, fontSize: '0.78rem' }}>No widgets</div>}
                {activeWidgets.map((wid) => (
                  <div key={wid} className="tester-panel-preview-row" style={{ borderBottom: `1px solid ${borderColor}22` }}>
                    <span style={{ opacity: 0.6 }}>{widgetLabel[wid] || wid}</span>
                    <span style={{ color: accentColor }}>—</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderSummaryTab() {
    if (!showResults) {
      return (
        <div className="tester-results-empty">
            <strong>No results yet.</strong>
            <p className="muted-text">Run the first backtest to generate summary metrics, trade analysis, and reports.</p>
        </div>
      );
    }

    const keyStats = [
      { label: 'Largest Win', value: formatCurrency(summary.analysis.largestWin) },
      { label: 'Largest Loss', value: formatCurrency(summary.analysis.largestLoss) },
      { label: 'Buy Win %', value: `${summary.analysis.buyWinRate.toFixed(1)}%` },
      { label: 'Sell Win %', value: `${summary.analysis.sellWinRate.toFixed(1)}%` },
      { label: 'Max Consecutive Wins', value: `${summary.analysis.maxConsecutiveWins}` },
      { label: 'Max Consecutive Losses', value: `${summary.analysis.maxConsecutiveLosses}` },
      { label: 'Expectancy', value: formatCurrency(summary.analysis.expectancy) },
      { label: 'Average Duration', value: formatMinutesAsDuration(summary.analysis.averageDurationMinutes) },
      { label: 'Average Bars Held', value: Number(summary.analysis.averageBarsHeld || 0).toFixed(1) },
      { label: 'Recovery Factor', value: Number(summary.analysis.recoveryFactor || 0).toFixed(2) },
      { label: 'Best Day', value: `${summary.analysis.bestDay?.label || '-'} · ${formatCurrency(summary.analysis.bestDay?.pnl || 0)}` },
      { label: 'Worst Day', value: `${summary.analysis.worstDay?.label || '-'} · ${formatCurrency(summary.analysis.worstDay?.pnl || 0)}` },
    ];

    return (
      <>
        <div className="tester-results-block tester-results-block-tight">
          <div className="tester-block-title">Summary</div>
          <div className="tester-metric-grid">
            {metricCards.map((card) => (
              <div key={card.key} className="tester-metric-card">
                <div className="tester-metric-label">{card.label}</div>
                <div className={`tester-metric-value ${metricTone(card.key, summary.metrics[card.key])}`}>{formatMetric(card.type, summary.metrics[card.key])}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="tester-results-block">
          <div className="tester-block-title">Equity</div>
          <EquityMiniChart summary={summary} onExpand={() => setExpandedChart('equity')} />
        </div>
        <div className="tester-results-block">
          <div className="tester-block-title">Key Stats</div>
          <div className="tester-analysis-grid">
            {keyStats.map((item) => (
              <div key={item.label} className="tester-analysis-card">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  function renderAnalysisTab() {
    if (!showResults) {
      return (
        <div className="tester-results-empty">
          <strong>No analysis yet.</strong>
          <p className="muted-text">Run the first backtest to unlock time, weekday, and trade-distribution breakdowns.</p>
        </div>
        );
      }

      return (
        <>
          <div className="tester-results-block">
            <div className="tester-block-title">Trade Breakdown</div>
            <div className="tester-analysis-grid">
            <div className="tester-analysis-card">
              <span>Gross Profit</span>
              <strong>{formatCurrency(summary.analysis.grossProfit)}</strong>
            </div>
            <div className="tester-analysis-card">
              <span>Gross Loss</span>
              <strong>{formatCurrency(-summary.analysis.grossLoss)}</strong>
            </div>
            <div className="tester-analysis-card">
              <span>Buy Net Profit</span>
              <strong>{formatCurrency(summary.analysis.buyNetProfit)}</strong>
            </div>
            <div className="tester-analysis-card">
              <span>Sell Net Profit</span>
              <strong>{formatCurrency(summary.analysis.sellNetProfit)}</strong>
            </div>
            <div className="tester-analysis-card">
              <span>Buy Trades</span>
              <strong>{summary.analysis.buyTrades}</strong>
            </div>
            <div className="tester-analysis-card">
              <span>Sell Trades</span>
              <strong>{summary.analysis.sellTrades}</strong>
              </div>
            </div>
          </div>
          <div className="tester-analysis-chart-grid">
            <div className="tester-results-block">
              <div className="tester-block-title">Profits by Day</div>
              <AxisBarChart
                items={summary.analysis.profitByDay}
                valueKey="pnl"
                valueType="currency"
                positiveNegative
                tooltipFormatter={(day) => (
                  `${day.label}: ${formatCurrency(day.pnl)} | ${day.trades} trades | ${day.wins} wins | ${day.losses} losses`
                )}
                caption="Hover a bar for weekday trade count, wins, losses, and net profit."
                onExpand={() => setExpandedChart('weekday-profit')}
              />
            </div>
            <div className="tester-results-block tester-results-block-fill">
              <div className="tester-block-title">Entries by Time</div>
              <AxisBarChart
                items={summary.analysis.entriesByHour}
                valueKey="entries"
                valueType="number"
                tooltipFormatter={(hour) => (
                  `${hour.label}: ${hour.entries} entries | ${hour.buyEntries} buy | ${hour.sellEntries} sell | ${hour.wins} wins | ${hour.losses} losses | ${formatCurrency(hour.pnl)}`
                )}
                caption="Hourly entry distribution across the selected range."
                onExpand={() => setExpandedChart('hourly-entries')}
              />
            </div>
          </div>
        </>
      );
    }

  function renderTradesTab() {
    if (!showResults) {
      return (
        <div className="tester-results-empty">
          <strong>No trades yet.</strong>
          <p className="muted-text">Run the first backtest to populate the trade log.</p>
        </div>
      );
    }

    return (
      <div className="tester-results-block tester-results-block-fill">
        <div className="tester-block-title">Trades</div>
        <div className="tester-trade-log">
          {summary.trades.slice().reverse().map((trade) => (
            <div key={trade.id} className="tester-trade-row">
              <span className={`tester-trade-side ${trade.side === 'BUY' ? 'buy' : 'sell'}`}>{trade.side}</span>
              <span>{formatPrice(trade.entry, engine.profile)}</span>
              <span>{formatPrice(trade.exit, engine.profile)}</span>
              <span className={trade.pnl >= 0 ? 'positive' : 'negative'}>{formatCurrency(trade.pnl)}</span>
              <span>{trade.reason}</span>
            </div>
          ))}
          {!summary.trades.length && <p className="muted-text">No closed trades.</p>}
        </div>
      </div>
    );
  }

  function renderJournalTab() {
    const entries = Array.isArray(engine?.journal) ? engine.journal : [];
    const errorCount = entries.filter((entry) => entry.level === 'error').length;
    const warnCount = entries.filter((entry) => entry.level === 'warning').length;
    const formatJournalTime = (time) => {
      if (time === null || time === undefined) return '';
      const ms = Number(time) < 1e12 ? Number(time) * 1000 : Number(time);
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16).replace('T', ' ');
    };
    const levelColor = (level) => (level === 'error' ? '#ff6b6b' : level === 'warning' ? '#f5a623' : '#9aa0a6');
    return (
      <div className="tester-results-block tester-results-block-fill">
        <div className="tester-block-title">
          Journal{entries.length ? ` · ${errorCount} error${errorCount === 1 ? '' : 's'}, ${warnCount} warning${warnCount === 1 ? '' : 's'}` : ''}
        </div>
        <div className="tester-trade-log">
          {entries.slice().reverse().map((entry, index) => (
            <div
              key={`${entry.code}-${index}`}
              style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 12, alignItems: 'baseline' }}
            >
              <span style={{ color: '#9aa0a6', whiteSpace: 'nowrap' }}>{formatJournalTime(entry.time)}</span>
              <span style={{ color: levelColor(entry.level), textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {entry.level}
              </span>
              <span>{entry.message}</span>
            </div>
          ))}
          {!entries.length && (
            <p className="muted-text">No journal entries — orders executed without volume, stop, or margin issues.</p>
          )}
          {engine?.journalTruncated && <p className="muted-text">Journal truncated at 1000 entries.</p>}
        </div>
      </div>
    );
  }

  function renderDebugTab() {
    if (inspectedBar === null) {
      return (
        <div className="tester-results-empty">
          <strong>Ask the chart &ldquo;why?&rdquo;</strong>
          <p className="muted-text">
            Run a backtest, switch on the <em>?</em> button in the chart controls, then click any candle.
            This panel shows every condition&apos;s actual values, each filter&apos;s verdict, and the exact
            reason a trade did or didn&apos;t open there.
          </p>
        </div>
      );
    }
    if (!debugInfo) {
      return (
        <div className="tester-results-empty">
          <strong>No signal evaluation at this bar.</strong>
          <p className="muted-text">
            The engine didn&apos;t evaluate a signal at or before this candle — it may be inside the
            indicator warm-up at the start of the test, or the current chart hasn&apos;t been backtested yet.
          </p>
        </div>
      );
    }
    const trace = debugInfo.trace;
    const debugRules = debugInfo.rules;
    const fmtVal = (value) => (Number.isFinite(value) ? String(Number(Number(value).toPrecision(6))) : '—');
    const fmtTime = (time) => {
      const date = new Date(Number(time));
      return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16).replace('T', ' ');
    };
    const operatorLabel = (id) => RULE_OPERATOR_OPTIONS.find((option) => option.id === id)?.label || id;
    const vetoLabels = {
      conflicting_signals: 'long and short signals fired together, so they cancel out.',
      cooldown: 'the cooldown is active — too few bars since the last entry.',
      timed_entry: 'the time is outside the timed-entry window.',
      exit_this_bar: 'a position exited on this bar, which blocks new entries until the next bar.',
      day_filter: 'the day-of-week filter blocks this day.',
      session_filter: 'no selected trading session is active at this time.',
      pending_order_mode: 'pending-order mode placed/kept a resting order instead of a market entry.',
      grid_mode: 'grid mode manages entries for this signal.',
      max_open_trades: 'the maximum number of open trades was already reached.',
      order_rejected: 'the order was attempted but rejected — see the Journal (volume, stops, or margin).',
    };
    const enteredTrade = trace.entered
      ? (engine.trades || []).find((tradeEntry) => tradeEntry.entryIndex === trace.index)
      : null;
    const anySignal = trace.conditionLong || trace.conditionShort || trace.rangeLong || trace.rangeShort
      || trace.structuralLong || trace.structuralShort || trace.candleLong || trace.candleShort
      || trace.patternLong || trace.patternShort;
    const entryModels = [
      { id: 'cond', label: 'Indicator conditions', enabled: debugRules.indicatorEntriesEnabled, long: trace.conditionLong, short: trace.conditionShort },
      { id: 'range', label: 'Range breakout', enabled: debugRules.rangeBreakoutEnabled, long: trace.rangeLong, short: trace.rangeShort },
      { id: 'structural', label: 'Structural breakout', enabled: debugRules.structuralBreakoutEnabled, long: trace.structuralLong, short: trace.structuralShort },
      { id: 'candle', label: 'Candle patterns', enabled: debugRules.candlePatternEntryEnabled, long: trace.candleLong, short: trace.candleShort },
      { id: 'pattern', label: 'Pattern by example', enabled: debugRules.patternEntryEnabled, long: trace.patternLong, short: trace.patternShort },
      { id: 'script', label: 'VixScript', enabled: debugRules.scriptEnabled, long: trace.scriptLong, short: trace.scriptShort },
    ].filter((model) => model.enabled);
    const filterRows = [
      { id: 'spread', label: 'Spread filter', active: debugRules.spreadFilterEnabled, ok: trace.spreadOk },
      { id: 'trendL', label: 'Trend filter — long side', active: debugRules.trendFilterEnabled, ok: trace.trendLongOk },
      { id: 'trendS', label: 'Trend filter — short side', active: debugRules.trendFilterEnabled, ok: trace.trendShortOk },
      { id: 'timed', label: 'Timed-entry window', active: debugRules.timedEntryEnabled, ok: trace.timedEntryOk },
      { id: 'cooldown', label: 'Cooldown', active: Number(debugRules.cooldownBars) > 0, ok: trace.cooldownOk },
      { id: 'day', label: 'Day of week', active: true, ok: trace.dayOk },
      { id: 'session', label: 'Sessions', active: true, ok: trace.sessionOk },
    ].filter((filterRow) => filterRow.active);
    const passChip = (ok) => <span className={`tester-debug-chip ${ok ? 'is-pass' : 'is-fail'}`}>{ok ? '✓' : '✗'}</span>;
    const renderTruthTable = (sideLabel, group) => (
      group.rows.length ? (
        <div className="tester-debug-section">
          <div className="tester-debug-section-head">
            <strong>{sideLabel} conditions</strong>
            <span className="muted-text">match {group.mode === 'any' ? 'any' : 'all'}</span>
            {passChip(group.verdict)}
          </div>
          {group.rows.map((row, rowIndex) => (
            <div key={`${sideLabel}_${rowIndex}`} className={`tester-debug-row ${row.pass ? '' : 'is-fail'}`}>
              <span className="tester-debug-expr">
                {getOperandDisplayLabel(engine.indicators, row.condition.left)}[{row.condition.leftShift}]
                {' '}{operatorLabel(row.condition.operator)}{' '}
                {row.condition.rightMode === 'value'
                  ? row.condition.rightValue
                  : `${getOperandDisplayLabel(engine.indicators, row.condition.rightOperand)}[${row.condition.rightShift}]`}
                {row.timeframe && row.timeframe !== (engine.setup?.timeframe || '') ? ` @${row.timeframe}` : ''}
              </span>
              <span className="tester-debug-values">
                {row.available ? `${fmtVal(row.leftValue)} vs ${fmtVal(row.rightValue)}` : 'values unavailable'}
              </span>
              {passChip(row.pass)}
            </div>
          ))}
        </div>
      ) : null
    );
    return (
      <div className="tester-results-block tester-debug-panel">
        <div className="tester-block-title">Why this bar? · {fmtTime(debugInfo.signalTime)}</div>
        <p className="tester-section-note">
          Signal candle at {fmtTime(debugInfo.signalTime)} (shift 0). A decision here opens a trade at
          the <em>next</em> candle&apos;s open.
        </p>
        <div className="tester-debug-outcome">
          {trace.entered ? (
            <>
              <strong>Opened a {trace.entered} trade.</strong>
              {enteredTrade ? (
                <span className="muted-text"> Entry {formatPrice(enteredTrade.entry, engine.profile)} · exit {formatPrice(enteredTrade.exit, engine.profile)} · P/L {formatCurrency(enteredTrade.pnl)}</span>
              ) : null}
            </>
          ) : trace.veto ? (
            <><strong>Signal fired, but no trade:</strong> <span>{vetoLabels[trace.veto] || trace.veto}</span></>
          ) : anySignal ? (
            <strong>Signal fired on one side but was filtered out.</strong>
          ) : (
            <strong>No entry signal on this bar.</strong>
          )}
        </div>
        {entryModels.length ? (
          <div className="tester-debug-section">
            <div className="tester-debug-section-head"><strong>Entry models</strong><span className="muted-text">long / short</span></div>
            {entryModels.map((model) => (
              <div key={model.id} className="tester-debug-row">
                <span className="tester-debug-expr">{model.label}</span>
                <span className="tester-debug-values">{passChip(model.long)} {passChip(model.short)}</span>
              </div>
            ))}
          </div>
        ) : null}
        {renderTruthTable('Long', debugInfo.long)}
        {renderTruthTable('Short', debugInfo.short)}
        {filterRows.length ? (
          <div className="tester-debug-section">
            <div className="tester-debug-section-head"><strong>Filters</strong></div>
            {filterRows.map((filterRow) => (
              <div key={filterRow.id} className={`tester-debug-row ${filterRow.ok ? '' : 'is-fail'}`}>
                <span className="tester-debug-expr">{filterRow.label}</span>
                <span className="tester-debug-values" />
                {passChip(filterRow.ok)}
              </div>
            ))}
          </div>
        ) : null}
        <div className="tester-debug-section">
          <div className="tester-debug-section-head"><strong>Trade state</strong></div>
          <div className="tester-debug-row">
            <span className="tester-debug-expr">Open positions at decision time</span>
            <span className="tester-debug-values">{trace.openPositions} of {debugRules.maxOpenTrades}</span>
            {passChip(Number(trace.openPositions) < Number(debugRules.maxOpenTrades))}
          </div>
        </div>
      </div>
    );
  }

  function renderOptimizeTab() {
    const usedIds = new Set(optimizeParams.map((param) => param.id));
    const metricOptions = [
      { id: 'profitFactor', label: 'Profit Factor' },
      { id: 'netProfit', label: 'Net Profit' },
      { id: 'expectancy', label: 'Expectancy' },
      { id: 'sharpe', label: 'Sharpe' },
      { id: 'winRate', label: 'Win Rate' },
      { id: 'recoveryFactor', label: 'Recovery Factor' },
      { id: 'maxDrawdown', label: 'Min Drawdown' },
    ];
    const job = optimizeJob;
    const result = job?.result;
    const running = optimizePending || ['queued', 'running'].includes(job?.status);
    const walkForward = optimizeMode === 'walkforward';
    const metricLabel = (metricOptions.find((m) => m.id === (result?.metric || optimizeMetric)) || {}).label || '';
    const fmt = (v) => (Number.isFinite(Number(v)) ? Number(v) : '—');
    return (
      <div className="tester-results-block tester-optimize-panel">
        <div className="tester-block-title">
          {walkForward ? 'Walk-Forward' : 'Optimize'}
          <InfoTip text="Grid: every parameter combination is backtested and ranked by your metric — but a single best combination is usually curve-fitting. Walk-Forward is the honest test: it optimizes on past data, then measures those settings on the NEXT unseen segment, and repeats. Walk-forward efficiency near or above 1 with mostly-profitable out-of-sample windows is what you want; a great grid score that collapses out-of-sample is overfitting." />
        </div>
        <div className="tester-optimize-mode">
          <button type="button" className={!walkForward ? 'active' : ''} onClick={() => setOptimizeMode('grid')} disabled={running}>Grid</button>
          <button type="button" className={walkForward ? 'active' : ''} onClick={() => setOptimizeMode('walkforward')} disabled={running}>Walk-Forward</button>
        </div>
        <div className="tester-optimize-params">
          {optimizeParams.length === 0 && (
            <p className="muted-text">Add parameters to sweep. Each gets a min / max / step; all combinations are tested.</p>
          )}
          {optimizeParams.map((param, index) => {
            const meta = optimizableParams.find((option) => option.id === param.id) || { type: 'number' };
            return (
              <div key={param.id} className="tester-optimize-param-row">
                <select
                  className="tester-optimize-param-select"
                  value={param.id}
                  onChange={(event) => updateOptimizeParam(index, 'id', event.target.value)}
                  disabled={running}
                >
                  {optimizableParams
                    .filter((option) => option.id === param.id || !usedIds.has(option.id))
                    .map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
                {meta.type === 'boolean' && (
                  <span className="tester-optimize-hint">tests off &amp; on</span>
                )}
                {meta.type === 'enum' && (
                  <div className="tester-optimize-enum">
                    {(meta.enumValues || []).map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`tester-chip ${(param.values || []).includes(value) ? 'active' : ''}`}
                        onClick={() => toggleOptimizeEnumValue(index, value)}
                        disabled={running}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
                {(!meta.type || meta.type === 'number') && (
                  <>
                    <label className="tester-optimize-minifield"><span>Min</span><input type="number" value={param.min} onChange={(event) => updateOptimizeParam(index, 'min', event.target.value)} disabled={running} /></label>
                    <label className="tester-optimize-minifield"><span>Max</span><input type="number" value={param.max} onChange={(event) => updateOptimizeParam(index, 'max', event.target.value)} disabled={running} /></label>
                    <label className="tester-optimize-minifield"><span>Step</span><input type="number" value={param.step} onChange={(event) => updateOptimizeParam(index, 'step', event.target.value)} disabled={running} /></label>
                  </>
                )}
                <button type="button" className="tester-icon-button" onClick={() => removeOptimizeParam(index)} disabled={running} aria-label="Remove parameter"><FiTrash2 /></button>
              </div>
            );
          })}
          {optimizeParams.length < 4 && optimizeParams.length < optimizableParams.length && (
            <button type="button" className="tester-add-condition-row" onClick={addOptimizeParam} disabled={running}>
              <FiPlus /> Add parameter
            </button>
          )}
        </div>
        <div className="tester-rule-grid tester-rule-grid-2">
          <label className="tester-field tester-field-compact">
            <span>Rank by</span>
            <select value={optimizeMetric} onChange={(event) => setOptimizeMetric(event.target.value)} disabled={running}>
              {metricOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </label>
          <label className="tester-field tester-field-compact">
            <span>Min Trades</span>
            <input type="number" min="0" value={optimizeMinTrades} onChange={(event) => setOptimizeMinTrades(event.target.value)} disabled={running} />
          </label>
          {walkForward && (
            <label className="tester-field tester-field-compact">
              <span>Windows</span>
              <input type="number" min="2" max="12" value={walkForwardWindows} onChange={(event) => setWalkForwardWindows(event.target.value)} disabled={running} />
            </label>
          )}
        </div>
        <div className="tester-optimize-run">
          <span className="muted-text">{optimizeCombos.toLocaleString()} combination{optimizeCombos === 1 ? '' : 's'}</span>
          {running ? (
            <button type="button" className="ghost-button" onClick={stopOptimize}>Stop</button>
          ) : (
            <button type="button" className="primary-cta" onClick={runOptimize} disabled={!optimizeParams.length || !canRun}>Run optimization</button>
          )}
        </div>
        {running && (
          <div className="tester-optimize-progress">
            <div className="tester-progress-track"><div className="tester-progress-fill" style={{ width: `${Math.max(2, Math.min(100, job?.progress || 0))}%` }} /></div>
            <span className="muted-text">{job?.currentStep?.stage === 'loading-history' ? 'Loading history…' : `Testing ${job?.completedSteps || 0} / ${job?.totalSteps || optimizeCombos}`}</span>
          </div>
        )}
        {job?.status === 'failed' && <p className="tester-script-status is-error">✗ {job.error || 'Optimization failed.'}</p>}
        {walkForward && result && job?.status === 'completed' && (() => {
          const wfe = result.walkForwardEfficiency;
          const wfeTone = wfe === null ? '' : wfe >= 0.5 ? 'is-pass' : 'is-fail';
          const netTone = (result.oosNetProfit || 0) >= 0 ? 'is-pass' : 'is-fail';
          return (
            <div className="tester-optimize-results">
              <div className="tester-wf-summary">
                <div className="tester-wf-stat"><span>Walk-forward efficiency</span><strong className={wfeTone}>{wfe === null ? '—' : wfe}</strong></div>
                <div className="tester-wf-stat"><span>Out-of-sample net</span><strong className={netTone}>{formatCurrency(result.oosNetProfit)}</strong></div>
                <div className="tester-wf-stat"><span>Profitable windows</span><strong>{result.profitableWindows}/{result.windowsTested}</strong></div>
                <div className="tester-wf-stat"><span>OOS trades</span><strong>{result.oosTrades}</strong></div>
              </div>
              <p className="tester-section-note">
                Each window optimizes on the data before it, then trades the next unseen segment. In-sample vs out-of-sample {metricLabel} tells you whether the edge is real or curve-fit.
              </p>
              <div className="tester-optimize-table-wrap" ref={attachDragScroll}>
                <table className="tester-optimize-table">
                  <thead>
                    <tr><th>#</th><th>Chosen params</th><th>Out-of-sample</th><th>IS {metricLabel}</th><th>OOS {metricLabel}</th><th>OOS net</th><th>Trades</th></tr>
                  </thead>
                  <tbody>
                    {result.perWindow.map((row) => (
                      <tr key={row.window} className={row.oos && row.oos.netProfit > 0 ? 'is-best' : ''}>
                        <td>{row.window}</td>
                        <td style={{ textAlign: 'left' }}>{row.bestParams ? Object.entries(row.bestParams).map(([k, v]) => `${k}=${v}`).join(', ') : '—'}</td>
                        <td>{row.oosFrom} → {row.oosTo}</td>
                        <td>{fmt(row.isMetric)}</td>
                        <td>{row.oosMetric === null ? '—' : fmt(row.oosMetric)}</td>
                        <td>{row.oos ? fmt(row.oos.netProfit) : '—'}</td>
                        <td>{row.oos ? fmt(row.oos.totalTrades) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {!walkForward && result && job?.status === 'completed' && (() => {
          const cols = result.paramAxes || [];
          return (
            <div className="tester-optimize-results">
              <p className="tester-section-note">
                {result.evaluated} tested, ranked by {metricLabel}{result.filteredOut ? ` · ${result.filteredOut} below min trades hidden` : ''}.
              </p>
              <div className="tester-optimize-table-wrap" ref={attachDragScroll}>
                <table className="tester-optimize-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {cols.map((axis) => <th key={axis.id}>{axis.label}</th>)}
                      <th>Net</th><th>PF</th><th>Win%</th><th>DD%</th><th>Trades</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {result.ranked.slice(0, 100).map((row, index) => (
                      <tr key={index} className={index === 0 ? 'is-best' : ''}>
                        <td>{index + 1}</td>
                        {cols.map((axis) => <td key={axis.id}>{fmt(row.params[axis.label])}</td>)}
                        <td>{fmt(row.metrics.netProfit)}</td>
                        <td>{fmt(row.metrics.profitFactor)}</td>
                        <td>{fmt(row.metrics.winRate)}</td>
                        <td>{fmt(row.metrics.maxDrawdown)}</td>
                        <td>{fmt(row.metrics.totalTrades)}</td>
                        <td><button type="button" className="ghost-button compact-button" onClick={() => applyOptimizeResult(row)}>Apply</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  function renderMonthlyTab() {
    if (!showResults) {
      return (
        <div className="tester-results-empty">
          <strong>No monthly data yet.</strong>
          <p className="muted-text">Run the first backtest to see the monthly performance breakdown.</p>
        </div>
        );
      }

      return (
        <div className="tester-results-block tester-results-block-fill">
          <div className="tester-block-title">Monthly</div>
          <AxisBarChart
            items={months.map((month, index) => ({ label: month, pnl: summary.monthly[index] }))}
            valueKey="pnl"
            valueType="currency"
            positiveNegative
            tooltipFormatter={(month) => `${month.label}: ${formatCurrency(month.pnl)}`}
            caption="Monthly net performance across the active backtest range."
            onExpand={() => setExpandedChart('monthly-profit')}
          />
        </div>
      );
    }

  function renderExpandedChartModal() {
      if (!expandedChart || !showResults) {
        return null;
      }

      let title = '';
      let chart = null;

      if (expandedChart === 'equity') {
        title = 'Equity Curve';
        chart = <EquityMiniChart summary={summary} detailed />;
      } else if (expandedChart === 'weekday-profit') {
        title = 'Profits by Day';
        chart = (
          <AxisBarChart
            items={summary.analysis.profitByDay}
            valueKey="pnl"
            valueType="currency"
            positiveNegative
            expanded
            tooltipFormatter={(day) => (
              `${day.label}: ${formatCurrency(day.pnl)} | ${day.trades} trades | ${day.wins} wins | ${day.losses} losses`
            )}
            caption="Weekday profitability across the completed backtest."
          />
        );
      } else if (expandedChart === 'hourly-entries') {
        title = 'Entries by Time';
        chart = (
          <AxisBarChart
            items={summary.analysis.entriesByHour}
            valueKey="entries"
            valueType="number"
            expanded
            tooltipFormatter={(hour) => (
              `${hour.label}: ${hour.entries} entries | ${hour.buyEntries} buy | ${hour.sellEntries} sell | ${hour.wins} wins | ${hour.losses} losses | ${formatCurrency(hour.pnl)}`
            )}
            caption="Hourly entry distribution across the selected backtest range."
          />
        );
      } else if (expandedChart === 'monthly-profit') {
        title = 'Monthly';
        chart = (
          <AxisBarChart
            items={months.map((month, index) => ({ label: month, pnl: summary.monthly[index] }))}
            valueKey="pnl"
            valueType="currency"
            positiveNegative
            expanded
            tooltipFormatter={(month) => `${month.label}: ${formatCurrency(month.pnl)}`}
            caption="Month-by-month net performance for the current result set."
          />
        );
      }

      if (!chart) {
        return null;
      }

      return (
        <div className="tester-modal-overlay" onClick={() => setExpandedChart('')} role="presentation">
          <div className="tester-modal tester-chart-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <div className="tester-modal-header">
              <h3>{title}</h3>
              <button type="button" className="tester-icon-button" onClick={() => setExpandedChart('')}>x</button>
            </div>
            <div className="tester-modal-list tester-chart-modal-body">
              {chart}
            </div>
          </div>
        </div>
      );
  }

  function renderDialog() {
    if (!dialog) {
      return null;
    }

    return (
      <div className="tester-modal-overlay" onClick={closeDialog} role="presentation">
        <div className={`tester-modal tester-confirm-modal ${dialog.tone || ''}`} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
          <div className="tester-modal-header">
            <h3>{dialog.title}</h3>
            <button type="button" className="tester-icon-button" onClick={closeDialog}>x</button>
          </div>
          <div className="tester-confirm-body">
            <p>{dialog.message}</p>
          </div>
          <div className="tester-confirm-actions">
            <button type="button" className="tester-ghost-button" onClick={closeDialog}>{dialog.cancelLabel}</button>
            {dialog.thirdLabel && (
              <button
                type="button"
                className="tester-ghost-button"
                onClick={() => {
                  if (typeof dialog.onThird === 'function') {
                    dialog.onThird();
                  } else {
                    closeDialog();
                  }
                }}
              >
                {dialog.thirdLabel}
              </button>
            )}
            <button
              type="button"
              className={`tester-add-button ${dialog.tone === 'danger' ? 'danger' : ''}`}
              onClick={() => {
                if (typeof dialog.onConfirm === 'function') {
                  dialog.onConfirm();
                } else {
                  closeDialog();
                }
              }}
            >
              {dialog.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderWalkthrough() {
    if (!walkthroughOpen) {
      return null;
    }
    const step = WALKTHROUGH_STEPS[Math.max(0, Math.min(WALKTHROUGH_STEPS.length - 1, walkthroughStep))];
    const progress = `${walkthroughStep + 1} / ${WALKTHROUGH_STEPS.length}`;
    return (
      <div className="tester-walkthrough-overlay" role="presentation">
        <div className="tester-walkthrough-card" role="dialog" aria-modal="false" aria-label="VixAlgoBuilder walkthrough">
          <div className="tester-walkthrough-kicker">First-use guide · {progress}</div>
          <h3>{step.title}</h3>
          <p>{step.body}</p>
          <div className="tester-walkthrough-dots" aria-hidden="true">
            {WALKTHROUGH_STEPS.map((item, index) => (
              <button
                key={item.title}
                type="button"
                className={index === walkthroughStep ? 'active' : ''}
                onClick={() => focusWalkthroughStep(index)}
                aria-label={`Show walkthrough step ${index + 1}`}
              />
            ))}
          </div>
          <div className="tester-walkthrough-actions">
            <button type="button" className="tester-ghost-button" onClick={closeWalkthrough}>Skip guide</button>
            {walkthroughStep > 0 && (
              <button type="button" className="tester-ghost-button" onClick={() => focusWalkthroughStep(walkthroughStep - 1)}>Back</button>
            )}
            <button type="button" className="tester-add-button" onClick={advanceWalkthrough}>
              {walkthroughStep >= WALKTHROUGH_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  }

    return (
      <HelmetProvider>
        <div className={`tester-page ${lightMode ? 'light' : ''}`}>
        <Helmet>
          <title>VixAlgoBuilder - Online Strategy Builder</title>
          <meta
            name="description"
            content="Build indicator-driven strategies, watch a live chart replay, and export supported MQ5 logic in VixAlgoBuilder."
          />
        </Helmet>

        <div className="tester-workspace">
          {/** Mask stale chart content while a new provider preview is loading. */}
          <header className="tester-topbar">
            <div className="tester-brand-block">
              <h1 className="tester-title">VixAlgoBuilder</h1>
            </div>
            <div className="tester-toolbar-actions">
              <label className="tester-speed-field">
                <span>Speed</span>
                <select value={playbackSpeed} onChange={(event) => setPlaybackSpeed(event.target.value)}>
                  {PLAYBACK_SPEED_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </label>
              <button
                type="button"
                className="tester-ghost-button tester-guide-button"
                onClick={() => {
                  setLeftTab('ai');
                  setLeftCollapsed(false);
                  setMobileView('setup');
                }}
              >
                <FiCpu /> <span>VixAI</span>
              </button>
              <button type="button" className="tester-ghost-button tester-guide-button" onClick={() => navigate('/builder-docs')}>
                <FiBookOpen /> <span>Docs</span>
              </button>
              <button type="button" className="tester-ghost-button tester-guide-button" onClick={openWalkthrough}>
                Guide
              </button>
              <button type="button" className="tester-icon-button tester-toolbar-icon" onClick={() => setLightMode((previous) => !previous)} aria-label={lightMode ? 'Switch to dark mode' : 'Switch to light mode'} title={lightMode ? 'Dark mode' : 'Light mode'}>
                {lightMode ? <FiMoon /> : <FiSun />}
              </button>
              {running && (
                <button type="button" className="tester-icon-button tester-toolbar-icon" onClick={paused ? resumeRun : pauseRun} aria-label={paused ? 'Resume backtest' : 'Pause backtest'} title={paused ? 'Resume' : 'Pause'}>
                  {paused ? <FiPlay /> : <FiPause />}
                </button>
              )}
              <button
                type="button"
                className={`tester-icon-button tester-toolbar-icon tester-toolbar-icon-primary ${runButtonBusy ? 'is-loading' : ''}`}
                onClick={runButtonBusy && (backfillStartPending || (autoRunAfterBackfill && hasActiveBackfillJob))
                  ? requestBackfillCancel
                  : runButtonActive
                    ? stopRun
                    : startRun}
                disabled={!runButtonActive && !canRun}
                aria-label={runButtonLabel}
                title={runButtonTitle}
              >
                {runButtonBusy ? <span className="tester-button-spinner" aria-hidden="true" /> : runButtonActive ? <FiSquare /> : <FiPlay />}
              </button>
            </div>
          </header>

          <div className="tester-mobile-tabs">
            {['setup', 'chart', 'results'].map((panel) => (
              <button key={panel} type="button" className={mobileView === panel ? 'active' : ''} onClick={() => setMobileView(panel)}>
                {panel === 'setup' ? 'Setup' : panel.charAt(0).toUpperCase() + panel.slice(1)}
              </button>
            ))}
          </div>

          <section className="tester-layout" ref={workspaceRef} style={{ '--left-panel-width': `${leftPanelWidth}px`, '--right-panel-width': `${rightPanelWidth}px` }}>
            <div className={`tester-center ${mobileView !== 'chart' ? 'mobile-hidden' : ''}`}>
              <div className={`tester-chart-card ${runFeedback.visible ? 'has-run-status' : ''}`}>
                <div className="tester-chart-toolbar">
                  <div className="tester-toolbar-left">
                    <div className="tester-chart-symbol">{setup.symbol}</div>
                    <div className="tester-chart-meta">
                      {sourceDescription}
                      <LiveFeedIndicator
                        enabled={!!marketState.liveTicksEnabled}
                        tick={liveTick}
                        timeframeLabel={setup.timeframe}
                      />
                    </div>
                  </div>
                  <div className="tester-timeframe-strip">
                    {TIMEFRAME_OPTIONS.map((option) => (
                      <button key={option} type="button" className={setup.timeframe === option ? 'active' : ''} onClick={() => updateSetup('timeframe', option)}>{option}</button>
                    ))}
                  </div>
                </div>
                {runFeedback.visible && (
                  <div className={`tester-run-status-bar ${runFeedback.tone || ''}`}>
                    <div className="tester-run-status-main">
                      {runFeedback.spinning ? <span className="tester-run-status-spinner" aria-hidden="true" /> : null}
                      <strong>{runFeedback.label}</strong>
                      <span>{runFeedback.detail}</span>
                    </div>
                    {runFeedback.meta ? <div className="tester-run-status-meta">{runFeedback.meta}</div> : null}
                  </div>
                )}
                <div className="tester-progress-track"><div className="tester-progress-fill" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}></div></div>
                <div className="tester-svg-shell">
                  {showOptimizeViz
                    ? <OptimizationVizPanel viz={optimizeViz} metricLabel={optimizeMetricLabel} />
                    : <StrategyTesterChart engine={engine} currentBar={currentBar} chartType={setup.chartType} viewResetRevision={chartViewRevision} onRequestOlder={loadOlderHistory} canRequestOlder={hasOlderHistory} isRequestingOlder={olderHistoryPending} selectMode={!!captureTarget} onRangeSelected={handleRangeSelected} liveRules={rules} conditionPreview={capturePreview} onBarInspect={handleBarInspect} inspectedIndex={debugInfo ? debugInfo.barIndex : null} liveFromTime={requestedLiveFrom} onChartFocus={() => { setLeftCollapsed(true); setRightCollapsed(true); }} />}
                </div>
                {pendingConditionCapture && (() => {
                  const total = pendingConditionCapture.conditions.length;
                  const kept = captureKeptConditions.length;
                  const matchCount = capturePreview?.indices?.length || 0;
                  return (
                    <div className="tester-capture-dock" onClick={(event) => event.stopPropagation()}>
                      <h4>Capture {pendingConditionCapture.side} conditions</h4>
                      <p className="muted-text">
                        Detected {total} condition{total === 1 ? '' : 's'}, all anchored on your selected entry candle. Lower = entry candle only, higher = tie more prior bars to the entry. Matching signal candles light up on the chart as you drag — your selected entry is always one of them.
                      </p>
                      <label className="tester-field">
                        <span>Strictness <em className="tester-unit">{kept} of {total} conditions</em></span>
                        <input
                          type="range" min="1" max={captureMaxStrictness} step="1"
                          value={captureStrict}
                          onChange={(event) => setCaptureStrictness(Number(event.target.value))}
                        />
                      </label>
                      <p className="tester-capture-matches">{matchCount} signal candle{matchCount === 1 ? '' : 's'} match on this chart</p>
                      <div className="tester-capture-modal-actions">
                        <button type="button" className="primary-cta" onClick={() => applyCapturedConditions(true)}>Replace</button>
                        <button type="button" className="ghost-button" onClick={() => applyCapturedConditions(false)}>Append</button>
                        <button type="button" className="ghost-button" onClick={() => setPendingConditionCapture(null)}>Cancel</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <aside className={`tester-panel tester-panel-left ${leftCollapsed ? 'is-collapsed' : ''} ${mobileView !== 'setup' ? 'mobile-hidden' : ''}`} style={{ width: leftPanelWidth }}>
              {leftCollapsed ? (
                <button type="button" className="tester-collapsed-tab" onClick={() => setLeftCollapsed(false)}>Setup</button>
              ) : (
                <>
                  <div className="tester-panel-header">
                    <span>Setup</span>
                    <button type="button" className="tester-panel-toggle" onClick={() => setLeftCollapsed(true)}>Hide</button>
                  </div>
                    <div className="tester-panel-tabs">
                      <button type="button" className={leftTab === 'strategies' ? 'active' : ''} onClick={() => setLeftTab('strategies')}>Strategies</button>
                      <button type="button" className={leftTab === 'ai' ? 'active' : ''} onClick={() => setLeftTab('ai')}>VixAI</button>
                      <button type="button" className={leftTab === 'market' ? 'active' : ''} onClick={() => setLeftTab('market')}>Market</button>
                      <button type="button" className={leftTab === 'indicators' ? 'active' : ''} onClick={() => setLeftTab('indicators')}>Indicators</button>
                      <button type="button" className={leftTab === 'rules' ? 'active' : ''} onClick={() => setLeftTab('rules')}>Rules</button>
                      <button type="button" className={leftTab === 'panel' ? 'active' : ''} onClick={() => setLeftTab('panel')}>Panel</button>
                    </div>
                    <div className="tester-panel-scroll">
                      {leftTab === 'strategies' && renderStrategiesTab()}
                      {leftTab === 'ai' && renderAiTab()}
                      {leftTab === 'market' && renderMarketTab()}
                      {leftTab === 'indicators' && renderIndicatorsTab()}
                      {leftTab === 'rules' && renderRulesTab()}
                      {leftTab === 'panel' && renderPanelTab()}
                    </div>
                </>
              )}
            </aside>

            {!leftCollapsed && <button type="button" className="tester-resizer tester-resizer-left" onMouseDown={() => setResizing('left')} aria-label="Resize setup panel" />}

            <aside className={`tester-panel tester-panel-right ${rightCollapsed ? 'is-collapsed' : ''} ${mobileView !== 'results' ? 'mobile-hidden' : ''}`} style={{ width: rightPanelWidth }}>
              {rightCollapsed ? (
                <button type="button" className="tester-collapsed-tab" onClick={() => setRightCollapsed(false)}>Results</button>
              ) : (
                <>
                  <div className="tester-panel-header">
                    <span>Results</span>
                      <div className="tester-panel-header-actions">
                        <button
                          type="button"
                          className="tester-icon-button"
                          onClick={downloadReport}
                          aria-label="Download report"
                          title={hasRunBacktest ? 'Download report' : 'Run a backtest first'}
                          disabled={!hasRunBacktest}
                        >
                          <FiDownload />
                        </button>
                      <button type="button" className="tester-panel-toggle" onClick={() => setRightCollapsed(true)}>Hide</button>
                    </div>
                  </div>
                  <div className="tester-panel-tabs">
                    <button type="button" className={rightTab === 'summary' ? 'active' : ''} onClick={() => setRightTab('summary')}>Summary</button>
                    <button type="button" className={rightTab === 'analysis' ? 'active' : ''} onClick={() => setRightTab('analysis')}>Analysis</button>
                    <button type="button" className={rightTab === 'trades' ? 'active' : ''} onClick={() => setRightTab('trades')}>Trades</button>
                    <button type="button" className={rightTab === 'monthly' ? 'active' : ''} onClick={() => setRightTab('monthly')}>Monthly</button>
                    <button type="button" className={rightTab === 'optimize' ? 'active' : ''} onClick={() => setRightTab('optimize')}>Optimize</button>
                    <button type="button" className={rightTab === 'debug' ? 'active' : ''} onClick={() => setRightTab('debug')}>Debug</button>
                    <button type="button" className={rightTab === 'journal' ? 'active' : ''} onClick={() => setRightTab('journal')}>
                      Journal{journalErrorCount ? ` (${journalErrorCount})` : ''}
                    </button>
                  </div>
                  <div className="tester-panel-scroll">
                    {rightTab === 'summary' && renderSummaryTab()}
                    {rightTab === 'analysis' && renderAnalysisTab()}
                    {rightTab === 'trades' && renderTradesTab()}
                    {rightTab === 'monthly' && renderMonthlyTab()}
                    {rightTab === 'optimize' && renderOptimizeTab()}
                    {rightTab === 'debug' && renderDebugTab()}
                    {rightTab === 'journal' && renderJournalTab()}
                  </div>
                </>
              )}
            </aside>

            {!rightCollapsed && <button type="button" className="tester-resizer tester-resizer-right" onMouseDown={() => setResizing('right')} aria-label="Resize results panel" />}
          </section>
        </div>

          {renderWalkthrough()}
          {renderDialog()}
          {modalOpen && (
            <div className="tester-modal-overlay" onClick={() => setModalOpen(false)} role="presentation">
              <div className="tester-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
              <div className="tester-modal-header">
                <h3>Add Indicator</h3>
                <button type="button" className="tester-icon-button" onClick={() => setModalOpen(false)}>x</button>
              </div>
                <div className="tester-modal-list">
                  {Object.values(INDICATOR_LIBRARY)
                    .sort((left, right) => {
                      if (left.area !== right.area) {
                        return left.area === 'price' ? -1 : 1;
                      }
                      return left.label.localeCompare(right.label);
                    })
                    .map((indicator) => (
                    <button key={indicator.type} type="button" className="tester-modal-option" onClick={() => addIndicator(indicator.type)}>
                      <span className="tester-indicator-dot" style={{ backgroundColor: indicator.color }}></span>
                      <span><strong>{indicator.label}</strong><small>{indicator.area === 'price' ? 'Overlay' : 'Oscillator'}</small></span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {renderExpandedChartModal()}
        </div>
      </HelmetProvider>
    );
  }

export default StrategyTester;










































